import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, GraduationCap, Zap, Code2, Heart, Trophy, Medal, Award, Globe, Layers, BookOpen, User, UserCircle2, Building2, Users, TrendingUp, BarChart2, Code, Terminal, Cpu, Wrench, Target, Rocket, Star, Shield, Flag, Smile, MapPin, FileText, Lightbulb, Settings, Bookmark, Calendar, Pen, Folder, Link2, Coffee, Music, Camera, FlaskConical, Atom, Brain, Crown, Flame, Handshake, LifeBuoy, Compass, Languages, HeartHandshake, UserCheck, Phone, Mail } from 'lucide-react';

const PREVIEW_ICON_MAP = {
  'briefcase':       Briefcase,
  'graduation-cap':  GraduationCap,
  'zap':             Zap,
  'code2':           Code2,
  'rocket':          Rocket,
  'bar-chart':       BarChart2,
  'trending-up':     TrendingUp,
  'trophy':          Trophy,
  'medal':           Medal,
  'award':           Award,
  'target':          Target,
  'bookmark':        Bookmark,
  'wrench':          Wrench,
  'flask':           FlaskConical,
  'file-text':       FileText,
  'book-open':       BookOpen,
  'layers':          Layers,
  'globe':           Globe,
  'languages':       Languages,
  'heart':           Heart,
  'handshake':       Handshake,
  'user':            User,
  'users':           Users,
  'star':            Star,
  'lightbulb':       Lightbulb,
  'camera':          Camera,
  'music':           Music,
  'compass':         Compass,
  'brain':           Brain,
  'cpu':             Cpu,
  'terminal':        Terminal,
  'code':            Code,
  'settings':        Settings,
  'flame':           Flame,
  'crown':           Crown,
  'shield':          Shield,
  'flag':            Flag,
  'map-pin':         MapPin,
  'calendar':        Calendar,
  'pen':             Pen,
  'folder':          Folder,
  'link2':           Link2,
  'coffee':          Coffee,
  'atom':            Atom,
  'smile':           Smile,
  'building2':       Building2,
  'heart-handshake': HeartHandshake,
  'life-buoy':       LifeBuoy,
};

// Provides the total fill height (rounded up to whole pages) to templates that
// need their flex layout to stretch across all pages (e.g. NavyTimeline).
const PageFillContext = React.createContext(0);

let activeDateFormat = 'shortMonth';

const fmtDate = (d, format = activeDateFormat) => {
  if (!d) return 'Present';
  const [y, m] = d.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const longMonths = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthIndex = Math.max(0, Math.min(11, parseInt(m || '1', 10) - 1));
  if (!m || format === 'year') return y;
  if (format === 'longMonth') return `${longMonths[monthIndex]} ${y}`;
  if (format === 'numeric') return `${String(monthIndex + 1).padStart(2, '0')}/${y}`;
  return `${months[monthIndex]} ${y}`;
};

const DEFAULT_SECTION_ORDER = ['experience','education','skills','projects','strengths','goals','hobbies','achievements','certifications','awards','languages','volunteering','publications','references','customBlocks'];
const PAGE_SAFE_SECTION_TITLES = new Set([
  'summary', 'professional summary', 'profile', 'experience', 'work experience',
  'employment history', 'education', 'skills', 'technical skills', 'projects',
  'career goals', 'hobbies', 'hobbies & interests', 'achievements',
  'certifications', 'awards', 'languages', 'volunteering', 'publications',
  'references', 'key strengths'
]);
const getSectionOrder = (d) => d.sectionOrder || DEFAULT_SECTION_ORDER;
const st = (d, id, def) => d.sectionTitles?.[id] || def;
const secT = (d, id, def) => {
  const iconId = (d.sectionIcons || {})[id];
  const title = (d.sectionTitles || {})[id] || def;
  const IconComp = iconId ? PREVIEW_ICON_MAP[iconId] : null;
  if (!IconComp) return title;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <IconComp size={9} strokeWidth={2.2} style={{ flexShrink: 0 }} />
      <span>{title}</span>
    </span>
  );
};

const Divider = ({ color, style: divStyle = 'line' }) => {
  if (divStyle === 'none') return null;
  if (divStyle === 'dots') return <div style={{ textAlign: 'center', color, margin: '6px 0 10px', fontSize: '8pt', letterSpacing: 4, opacity: 0.6 }}>· · · · ·</div>;
  return <div style={{ height: 2, background: color, margin: '8px 0 12px', borderRadius: 1 }} />;
};

// Returns borderBottom style object based on pi.sectionDivider setting
const sectionBorderStyle = (pi, color, width = 2) => {
  const d = pi?.sectionDivider || 'line';
  return d === 'line' ? { borderBottom: `${width}px solid ${color}` } : {};
};

// Renders dots divider below a section heading when sectionDivider === 'dots'
const SectionDividerLine = ({ pi, color }) => {
  if ((pi?.sectionDivider || 'line') !== 'dots') return null;
  return <div style={{ textAlign: 'center', color, margin: '2px 0 8px', fontSize: '8pt', letterSpacing: 4, opacity: 0.6 }}>· · · · ·</div>;
};

const BulletList = ({ bullets, symbol = '•', color }) => bullets?.length > 0 ? (
  <div style={{ margin: '5px 0 0 2px', ...(color ? { color } : {}) }}>
    {bullets.map((b, i) => b && (
      <div key={i} data-resume-entry style={{ display: 'flex', gap: 6, marginBottom: 2, fontSize: '9pt', alignItems: 'flex-start', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
        <span style={{ flexShrink: 0, marginTop: 1 }}>{symbol}</span>
        <span>{b}</span>
      </div>
    ))}
  </div>
) : null;

const IdentityRow = ({ pi }) => {
  const parts = [];
  if (pi.showDOB && pi.dateOfBirth) parts.push(`Born: ${new Date(pi.dateOfBirth).toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'})}`);
  if (pi.showNationality && pi.nationality) parts.push(`Nationality: ${pi.nationality}`);
  if (pi.showSex && pi.sex) parts.push(`Sex: ${pi.sex}`);
  if (!parts.length) return null;
  return <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 4 }}>{parts.join('  ·  ')}</div>;
};

const HobbiesDisplay = ({ hobbies, d, color, transform = 'uppercase', marginTop = 14, sec }) => {
  if (!hobbies) return null;
  const items = hobbies.split(',').map(h => h.trim()).filter(Boolean);
  if (!items.length) return null;
  const title = secT(d || {}, 'hobbies', 'Hobbies & Interests');
  const heading = sec ? sec(title) : <div style={{ color, fontWeight: 'bold', textTransform: transform, letterSpacing: 1, fontSize: '9.5pt', marginBottom: 6 }}>{title}</div>;
  return (
    <div data-resume-section style={{ marginTop: sec ? 0 : marginTop, marginBottom: 14, ...noBreak }}>
      {heading}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {items.map((h, i) => <span key={i} data-resume-tag style={{ background: color + '15', color, border: `1px solid ${color}33`, borderRadius: 20, padding: '2px 10px', fontSize: '8.5pt', fontWeight: 500, display: 'inline-block', breakInside: 'avoid', pageBreakInside: 'avoid' }}>{h}</span>)}
      </div>
    </div>
  );
};

const StrengthsDisplay = ({ strengths, d, color, transform = 'uppercase', marginTop = 14, sec }) => {
  if (!strengths) return null;
  const items = strengths.split(',').map(h => h.trim()).filter(Boolean);
  if (!items.length) return null;
  const title = secT(d, 'strengths', 'Key Strengths');
  const heading = sec ? sec(title) : <div style={{ color, fontWeight: 'bold', textTransform: transform, letterSpacing: 1, fontSize: '9.5pt', marginBottom: 6 }}>{title}</div>;
  return (
    <div data-resume-section style={{ marginTop: sec ? 0 : marginTop, marginBottom: 14, ...noBreak }}>
      {heading}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {items.map((h, i) => <span key={i} data-resume-tag style={{ background: color + '18', color, border: `1px solid ${color}33`, borderRadius: 4, padding: '2px 10px', fontSize: '8.5pt', fontWeight: 600, wordBreak: 'break-word', display: 'inline-block', breakInside: 'avoid', pageBreakInside: 'avoid' }}>{h}</span>)}
      </div>
    </div>
  );
};

const GoalsDisplay = ({ goals, d, color, transform = 'uppercase', marginTop = 14, sec }) => {
  if (!goals) return null;
  const title = secT(d, 'goals', 'Career Goals');
  const heading = sec ? sec(title) : <div style={{ color, fontWeight: 'bold', textTransform: transform, letterSpacing: 1, fontSize: '9.5pt', marginBottom: 5 }}>{title}</div>;
  return (
    <div data-resume-section style={{ marginTop: sec ? 0 : marginTop, marginBottom: 14, ...noBreak }}>
      {heading}
      <p style={{ fontSize: '9pt', color: '#374151', lineHeight: 1.55, margin: 0, wordBreak: 'break-word' }}>{goals}</p>
    </div>
  );
};

const SkillTags = ({ items, color }) => {
  const tags = (items || '').split(',').map(t => t.trim()).filter(Boolean);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 3 }}>
      {tags.map((t, i) => (
        <span key={i} data-resume-tag style={{ background: color + '18', color, border: `1px solid ${color}33`, borderRadius: 12, padding: '1px 7px', fontSize: '7pt', fontWeight: 500, display: 'inline-block', breakInside: 'avoid', pageBreakInside: 'avoid' }}>{t}</span>
      ))}
    </div>
  );
};

// Shared section renderers used across templates
const noBreak = { breakInside: 'avoid', pageBreakInside: 'avoid' };

const AwardsItems = ({ awards, color, divStyle, symbol }) => awards?.length > 0 ? awards.map(a => (
  <div key={a.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontWeight: 700 }}>{a.title}</span>
      {a.date && <span style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(a.date)}</span>}
    </div>
    {a.issuer && <div style={{ fontSize: '8.5pt', color, fontStyle: 'italic' }}>{a.issuer}</div>}
    {a.description && <p style={{ margin: '2px 0 0', fontSize: '9pt', color: '#4B5563' }}>{a.description}</p>}
  </div>
)) : null;

const VolunteeringItems = ({ volunteering, color, bulletSymbol }) => volunteering?.length > 0 ? volunteering.map(v => (
  <div key={v.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontWeight: 700 }}>{v.role}</div>
        <div style={{ fontSize: '9pt', color }}>{v.organization}{v.location && ` · ${v.location}`}</div>
      </div>
      <div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(v.startDate)} – {v.current ? 'Present' : fmtDate(v.endDate)}</div>
    </div>
    {v.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#4B5563' }}>{v.description}</p>}
    <BulletList bullets={v.bullets} symbol={bulletSymbol} />
  </div>
)) : null;

const ReferencesItems = ({ references, referencesAvailableOnRequest, color }) => {
  if (referencesAvailableOnRequest) return <p style={{ margin: 0, fontSize: '9.5pt', fontStyle: 'italic', color: '#6B7280' }}>References available upon request.</p>;
  return references?.length > 0 ? (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {references.map(r => (
        <div key={r.id} data-resume-entry style={{ fontSize: '9pt', ...noBreak }}>
          <div style={{ fontWeight: 700 }}>{r.name}</div>
          {r.relationship && <div style={{ fontSize: '8pt', color, fontStyle: 'italic' }}>{r.relationship}</div>}
          {r.title && <div style={{ fontSize: '8pt', color: '#4B5563' }}>{r.title}{r.company && ` · ${r.company}`}</div>}
          {r.email && <div style={{ fontSize: '8pt', color: '#6B7280' }}>{r.email}</div>}
          {r.phone && <div style={{ fontSize: '8pt', color: '#6B7280' }}>{r.phone}</div>}
        </div>
      ))}
    </div>
  ) : null;
};

const PublicationsItems = ({ publications }) => publications?.length > 0 ? publications.map(p => (
  <div key={p.id} data-resume-entry style={{ marginBottom: 8, fontSize: '9.5pt', ...noBreak }}>
    {p.authors && <span style={{ color: '#4B5563' }}>{p.authors} </span>}
    {p.year && <span style={{ color: '#6B7280' }}>({p.year}). </span>}
    "{p.title}."
    {p.venue && <> <em style={{ color: '#4B5563' }}>{p.venue}</em>.</>}
  </div>
)) : null;

const LanguagesItems = ({ languages }) => languages?.length > 0 ? (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}>
    {languages.map(l => (
      <div key={l.id} data-resume-entry style={{ fontSize: '9.5pt', ...noBreak }}>
        <span style={{ fontWeight: 600 }}>{l.language}</span>{l.proficiency && <span style={{ color: '#6B7280' }}> — {l.proficiency}</span>}
      </div>
    ))}
  </div>
) : null;

const CertificationsItems = ({ certifications, color }) => certifications?.length > 0 ? certifications.map(cert => (
  <div key={cert.id} data-resume-entry style={{ fontSize: '9.5pt', marginBottom: 3, ...noBreak }}>
    <span style={{ fontWeight: 600 }}>{cert.name}</span>{cert.issuer && <span style={{ color: color || '#4B5563' }}> — {cert.issuer}</span>}{cert.date && <span style={{ color: '#6B7280' }}>, {fmtDate(cert.date)}</span>}
  </div>
)) : null;

const CustomBlocks = ({ blocks, color, divStyle, bulletSymbol }) => blocks?.map(blk => (
  <div key={blk.id} style={{ marginBottom: 14, ...noBreak }}>
    <div style={{ fontWeight: 'bold', fontSize: '10pt', color }}>{blk.title}</div>
    <Divider color={color + '44'} style={divStyle} />
    {blk.description && <p style={{ margin: '0 0 4px', fontSize: '9.5pt' }}>{blk.description}</p>}
    {blk.showDate && blk.date && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(blk.date)}</div>}
    {blk.showLink && blk.link && <a href={blk.link} style={{ fontSize: '8.5pt', color: color }}>{blk.link}</a>}
    {blk.showBullets && <BulletList bullets={blk.bullets} symbol={bulletSymbol} />}
  </div>
));

// ── Helper: resolve colors with user overrides ────────────────────────────────
const useColors = (pi, defaults) => {
  const resolved = pi.accentColor || defaults.accent;
  const section  = pi.sectionColor || defaults.section || resolved;
  return {
    accent:  resolved,
    name:    pi.nameColor    || defaults.name    || '#ffffff',
    section: section,
    body:    pi.bodyColor    || defaults.body    || '#1F2937',
    sidebar: pi.sidebarColor || defaults.sidebar || resolved + '18',
    link:    pi.linkColor    || defaults.link    || section,
  };
};

// Lighten (positive pct) or darken (negative pct) a 6-digit hex color
const shadeHex = (hex, pct) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const ch = (shift) => {
    const v = Math.round(((num >> shift) & 0xff) * (1 + pct));
    return Math.max(0, Math.min(255, v));
  };
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
};

const FONT_FAMILIES = {
  ibm: '"IBM Plex Mono", "Courier New", monospace',
  inter: '"Inter", "Segoe UI", Arial, sans-serif',
  modern: '"Segoe UI", Arial, sans-serif',
  classic: '"Times New Roman", Georgia, serif',
  elegant: '"Garamond", Georgia, serif',
  clean: '"Helvetica Neue", Arial, sans-serif',
  mono: '"Courier New", monospace',
  roboto: '"Roboto", Arial, sans-serif',
  lato: '"Lato", Arial, sans-serif',
  raleway: '"Raleway", Arial, sans-serif',
  merriweather: '"Merriweather", Georgia, serif',
  playfair: '"Playfair Display", Georgia, serif',
  sourcesans: '"Source Sans 3", Arial, sans-serif',
  nunito: '"Nunito", Arial, sans-serif',
  opensans: '"Open Sans", Arial, sans-serif',
};

const FONT_SCALE = { compact: 0.88, normal: 1, large: 1.08, xl: 1.16 };
const MARGIN_SCALE = { compact: 0.7, normal: 1, relaxed: 1.35 };
const SAMPLE_PROFILE_NAME = 'Sarah Chen';
const sampleProfileName = (pi) => {
  const name = (pi.fullName || '').trim();
  return name || SAMPLE_PROFILE_NAME;
};

const scaledPt = (size, pi) => {
  const scale = FONT_SCALE[pi.fontSizeMode || 'normal'] || 1;
  return `${Math.round(size * scale * 10) / 10}pt`;
};

const useDesign = (pi, defaults = {}) => {
  const mscale = MARGIN_SCALE[pi.margins || 'normal'] || 1;
  const base = defaults.padding || 20;
  return {
    fontFamily: pi.fontFamily && pi.fontFamily !== 'template' ? FONT_FAMILIES[pi.fontFamily] : defaults.fontFamily,
    fontSize: scaledPt(defaults.fontSize || 10, pi),
    lineHeight: pi.lineSpacing ? `${pi.lineSpacing}px` : (defaults.lineHeight || 1.5),
    padding: Math.round(base * mscale),
  };
};

// Cover-letter variant: same design settings, CL-tuned defaults
const useCLDesign = (pi, defaults = {}) => {
  const mscale = MARGIN_SCALE[pi.margins || 'normal'] || 1;
  return {
    font: pi.fontFamily && pi.fontFamily !== 'template' && FONT_FAMILIES[pi.fontFamily]
      ? FONT_FAMILIES[pi.fontFamily] : defaults.font,
    fontSize: scaledPt(defaults.fontSize || 9.5, pi),
    lineHeight: pi.lineSpacing ? `${pi.lineSpacing}px` : (defaults.lineHeight || 1.75),
    pad: (base) => Math.round(base * mscale),
  };
};

const sectionTransform = (pi) => pi.sectionCase === 'custom' ? 'none' : 'uppercase';

// ── Helper: photo element with size/shape ─────────────────────────────────────
const Photo = ({ pi, border = '3px solid rgba(255,255,255,0.4)', fallbackSize = 64 }) => {
  if (!pi.photo || pi.showPhoto === false) return null;
  const size   = pi.photoSize  || fallbackSize;
  const shape  = pi.photoShape || 'circle';
  const radius = shape === 'circle' ? '50%' : shape === 'rounded' ? '10px' : '2px';
  return (
    <img src={pi.photo} alt="" style={{
      width: size, height: size, borderRadius: radius,
      objectFit: 'cover', border, flexShrink: 0,
    }} />
  );
};

// ── MODERN (two-column blue) ──────────────────────────────────────────────────
const NavyPhotoSlot = ({ pi }) => {
  if (!pi.photo || pi.showPhoto === false) return null;
  const size = pi.photoSize || 120;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: -(size / 2 + 16), marginBottom: 14 }}>
      <div style={{ borderRadius: '50%', background: 'white', padding: 4, flexShrink: 0, lineHeight: 0, boxShadow: '0 5px 16px rgba(15, 23, 42, 0.22)' }}>
        <img src={pi.photo} alt="" style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', objectPosition: '50% 18%', border: 'none', flexShrink: 0,
          background: 'linear-gradient(135deg, #DCE3E8 0%, #F8FAFC 100%)',
        }} />
      </div>
    </div>
  );
};

const AcademicModernPhotoSlot = ({ pi, sidebarColor }) => {
  if (!pi.photo || pi.showPhoto === false) return null;
  const size = pi.photoSize || 120;
  return (
    <div style={{ marginTop: 32, marginBottom: 28, lineHeight: 0, borderRadius: '50%', boxShadow: `0 0 0 8px ${sidebarColor}, 0 0 0 13px white`, display: 'inline-block', flexShrink: 0 }}>
      <img src={pi.photo} alt="" style={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', objectPosition: '50% 18%', border: 'none', flexShrink: 0,
        background: 'rgba(255,255,255,0.12)',
      }} />
    </div>
  );
};

const ModernTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c  = useColors(pi, { accent: '#991B1B', name: '#ffffff', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: 'Georgia, serif', fontSize: 10, lineHeight: 1.5, padding: 20 });
  const pad = ds.padding;
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sec = (t, mt = 14) => <><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginTop: mt }}>{t}</div><Divider color={c.section + '55'} style={pi.sectionDivider} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', flexDirection: 'column', minHeight: minH, position: 'relative', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, right: 185, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
          <div style={{ position: 'absolute', right: 0, width: 185, top: (i + 1) * defaultMinH, height: 56, backgroundColor: 'white', backgroundImage: `linear-gradient(${c.sidebar}, ${c.sidebar})`, zIndex: 3 }} />
        </React.Fragment>
      ))}
      <div style={{ background: c.accent, color: 'white', padding: `${pad}px ${pad + 6}px` }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '20pt', fontWeight: 'bold', color: c.name }}>{pi.fullName}</div>
            {pi.jobTitle && <div style={{ fontSize: '11pt', opacity: 0.9, marginTop: 2, color: c.name }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 6, fontSize: '8.5pt', opacity: 0.85, color: c.name }}>
              {[pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).map((x, i) => <span key={i}>{x}</span>)}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flex: 1 }}>
        <div style={{ flex: 2, padding: '18px 22px', color: c.body, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
          {pi.summary && <><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt' }}>{pi.summaryTitle || 'Summary'}</div><Divider color={c.section + '55'} style={pi.sectionDivider} /><p style={{ margin: '0 0 16px', fontSize: '9.5pt' }}>{pi.summary}</p></>}
          {getSectionOrder(d).filter(id => !['education','skills','certifications','languages'].includes(id)).map(id => {
            switch (id) {
              case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt' }}>{secT(d,'experience', 'Experience')}</div><Divider color={c.section + '55'} style={pi.sectionDivider} />{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 'bold' }}>{e.title}</div><div style={{ color: c.link, fontSize: '9.5pt' }}>{e.company}{e.location && ` · ${e.location}`}</div></div><div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div>{e.description && <p style={{ margin: '4px 0 2px', fontSize: '9pt', color: '#4B5563', fontStyle: 'italic' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
              case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginTop: 14 }}>{secT(d,'projects', 'Projects')}</div><Divider color={c.section + '55'} style={pi.sectionDivider} />{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontWeight: 'bold' }}>{p.title}</span>{p.date && <span style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(p.date)}</span>}</div>{p.skills && <div style={{ fontSize: '8.5pt', color: '#6B7280', marginBottom: 2 }}>Tech: {p.skills}</div>}{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} />{p.link && <a href={p.link} style={{ fontSize: '8pt', color: c.link }}>{p.link}</a>}</div>)}</div> : null;
              case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginTop: 14 }}>{secT(d,'achievements', 'Achievements')}</div><Divider color={c.section + '55'} style={pi.sectionDivider} />{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontWeight: 600 }}>{a.title}</span>{a.date && <span style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(a.date)}</span>}</div>{a.description && <p style={{ margin: '2px 0', fontSize: '9pt', color: '#4B5563' }}>{a.description}</p>}</div>)}</div> : null;
              case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginTop: 14 }}>{secT(d,'volunteering', 'Volunteering')}</div><Divider color={c.section + '55'} style={pi.sectionDivider} /><VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
              case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginTop: 14 }}>{secT(d,'awards', 'Awards')}</div><Divider color={c.section + '55'} style={pi.sectionDivider} /><AwardsItems awards={d.awards} color={c.link} /></div> : null;
              case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginTop: 14 }}>{secT(d,'publications', 'Publications')}</div><Divider color={c.section + '55'} style={pi.sectionDivider} /><PublicationsItems publications={d.publications} /></div> : null;
              case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginTop: 14 }}>{secT(d,'references', 'References')}</div><Divider color={c.section + '55'} style={pi.sectionDivider} /><ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
              case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
              default: return null;
            }
          })}
        </div>
        <div style={{ width: '185px', background: c.sidebar, padding: '18px 14px', borderLeft: `1px solid ${c.section}33`, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0, flexShrink: 0 }}>
          {getSectionOrder(d).filter(id => ['education','skills','certifications','languages'].includes(id)).map(id => {
            switch (id) {
              case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', fontSize: '8.5pt', textTransform: sectionTransform(pi), letterSpacing: 1, marginBottom: 8 }}>{secT(d,'education', 'Education')}</div>{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><div style={{ fontWeight: 600, fontSize: '8.5pt' }}>{edu.degree}</div><div style={{ color: c.link, fontSize: '8pt' }}>{edu.school}</div><div style={{ fontSize: '7.5pt', color: '#6B7280' }}>{fmtDate(edu.endDate)}{edu.gpa && ` · GPA ${edu.gpa}`}</div></div>)}<div style={{ height: 1, background: c.section + '33', margin: '10px 0' }} /></div> : null;
              case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', fontSize: '8.5pt', textTransform: sectionTransform(pi), letterSpacing: 1, marginBottom: 8 }}>{secT(d,'skills', 'Skills')}</div>{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 7, ...noBreak }}><div style={{ fontWeight: 600, fontSize: '8pt' }}>{s.category}</div><div style={{ fontSize: '7.5pt', color: '#4B5563' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}<div style={{ height: 1, background: c.section + '33', margin: '10px 0' }} /></div> : null;
              case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', fontSize: '8.5pt', textTransform: sectionTransform(pi), letterSpacing: 1, marginBottom: 8 }}>{secT(d,'certifications', 'Certifications')}</div>{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><div style={{ fontWeight: 600, fontSize: '8pt' }}>{cert.name}</div><div style={{ fontSize: '7.5pt', color: '#6B7280' }}>{cert.issuer}</div></div>)}<div style={{ height: 1, background: c.section + '33', margin: '10px 0' }} /></div> : null;
              case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ color: c.section, fontWeight: 'bold', fontSize: '8.5pt', textTransform: sectionTransform(pi), letterSpacing: 1, marginBottom: 8 }}>{secT(d,'languages', 'Languages')}</div>{d.languages.map(l => <div key={l.id} data-resume-entry style={{ fontSize: '8pt', marginBottom: 3, ...noBreak }}><span style={{ fontWeight: 600 }}>{l.language}</span>{l.proficiency && <span style={{ color: '#6B7280' }}> · {l.proficiency}</span>}</div>)}</div> : null;
              default: return null;
            }
          })}
        </div>
      </div>
    </div>
  );
};

// ── CLASSIC ───────────────────────────────────────────────────────────────────
const ClassicTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1F2937', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: '"Times New Roman", serif', fontSize: 10, lineHeight: 1.6 });
  const secHead = (t) => <><div style={{ fontWeight: 'bold', color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5, ...sectionBorderStyle(pi, c.section, 1.5), paddingBottom: 6, marginBottom: 8, marginTop: 14 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  const sec = secHead;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '28px 32px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', borderBottom: `3px double ${c.section}`, paddingBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '20pt', fontWeight: 'bold', letterSpacing: 2, color: c.section, textTransform: sectionTransform(pi) }}>{pi.fullName}</div>
            {pi.jobTitle && <div style={{ fontSize: '10.5pt', fontStyle: 'italic', color: c.link }}>{pi.jobTitle}</div>}
            <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 4 }}>{[pi.email, pi.phone, pi.location].filter(Boolean).join('  ·  ')}</div>
            <IdentityRow pi={pi} />
          </div>
        </div>
      </div>
      {pi.summary && <>{secHead(pi.summaryTitle || 'Objective')}<p style={{ margin: 0, fontSize: '9.5pt', textAlign: 'justify' }}>{pi.summary}</p></>}
      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'experience', 'Professional Experience'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div style={{ fontWeight: 'bold' }}>{e.title}, {e.company}</div><div style={{ fontSize: '9pt', color: '#4B5563' }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div>{e.location && <div style={{ fontSize: '8.5pt', color: '#6B7280', fontStyle: 'italic' }}>{e.location}</div>}{e.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#374151' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 'bold' }}>{edu.degree}</div><div>{edu.school}{edu.location && `, ${edu.location}`}</div></div><div style={{ fontSize: '9pt', color: '#4B5563' }}>{fmtDate(edu.startDate)} – {fmtDate(edu.endDate)}</div></div>{edu.gpa && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>GPA: {edu.gpa}</div>}</div>)}</div> : null;
          case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'skills', 'Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ fontSize: '9.5pt', marginBottom: 3, ...noBreak }}><strong>{s.category}:</strong> {pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div>)}</div> : null;
          case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'projects', 'Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ fontWeight: 'bold' }}>{p.title}{p.date && <span style={{ fontWeight: 'normal', color: '#6B7280' }}> — {fmtDate(p.date)}</span>}</div>{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'achievements', 'Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ fontSize: '9.5pt', marginBottom: 4, ...noBreak }}><strong>{a.title}</strong>{a.date && ` (${fmtDate(a.date)})`}: {a.description}</div>)}</div> : null;
          case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'certifications', 'Certifications'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: '9.5pt', marginBottom: 3, ...noBreak }}>{cert.name} — {cert.issuer}{cert.date && `, ${fmtDate(cert.date)}`}</div>)}</div> : null;
          case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'languages', 'Languages'))}<LanguagesItems languages={d.languages} /></div> : null;
          case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'awards', 'Awards'))}<AwardsItems awards={d.awards} color={c.section} /></div> : null;
          case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.section} bulletSymbol={pi.bulletSymbol} /></div> : null;
          case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'publications', 'Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
          case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{secHead(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.section} /></div> : null;
          case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
          default: return null;
        }
      })}
    </div>
  );
};

// ── EXECUTIVE ─────────────────────────────────────────────────────────────────
const ExecutiveTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const _c = useColors(pi, { accent: '#1F2937', name: '#ffffff', section: '#FB923C', body: '#1F2937' });
  const c = { ..._c, link: pi.linkColor || _c.section, sidebar: pi.sidebarColor || _c.section + '30' };
  const ds = useDesign(pi, { fontFamily: '"Palatino Linotype",Palatino,serif', fontSize: 10, lineHeight: 1.6 });
  const sec = (t) => <><div style={{ fontSize: '11pt', fontWeight: 'bold', color: c.section, textTransform: sectionTransform(pi), letterSpacing: 2, ...sectionBorderStyle(pi, c.section, 2), paddingBottom: 4, marginBottom: 8, marginTop: 16 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ background: c.accent, color: 'white', padding: '26px 30px', display: 'flex', gap: 18, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '22pt', fontWeight: 'bold' }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ color: c.name, opacity: 0.78, fontSize: '11pt', marginTop: 2 }}>{pi.jobTitle}</div>}
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: '8.5pt', color: '#D1D5DB', flexWrap: 'wrap' }}>
            {[pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).map((c, i) => <span key={i}>{c}</span>)}
          </div>
          <IdentityRow pi={{ ...pi, ...{ color: '#9CA3AF' } }} />
        </div>
      </div>
      <div style={{ padding: '18px 28px' }}>
        {pi.summary && <><div style={{ background: c.sidebar, borderLeft: `4px solid ${c.section}`, padding: '10px 14px', marginBottom: 16, borderRadius: '0 4px 4px 0' }}><p style={{ margin: 0, fontStyle: 'italic', fontSize: '9.5pt' }}>{pi.summary}</p></div></>}
        {getSectionOrder(d).filter(id => !['education','skills'].includes(id)).map(id => {
          switch (id) {
            case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'certifications', 'Certifications'))}<CertificationsItems certifications={d.certifications} color={c.link} /></div> : null;
            case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'languages', 'Languages'))}<LanguagesItems languages={d.languages} /></div> : null;
            case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience', 'Career History'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 14, paddingLeft: 12, borderLeft: `2px solid ${c.sidebar}`, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 'bold' }}>{e.title}</div><div style={{ color: c.link, fontSize: '9.5pt' }}>{e.company}{e.location && ` · ${e.location}`}</div></div><span style={{ fontSize: '8pt', background: c.sidebar, padding: '2px 8px', borderRadius: 12, color: '#6B7280' }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</span></div>{e.description && <p style={{ margin: '4px 0 2px', fontSize: '9pt', color: '#4B5563' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
            case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'projects', 'Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><div style={{ fontWeight: 'bold' }}>{p.title}{p.date && <span style={{ fontWeight: 'normal', color: '#6B7280', fontSize: '9pt' }}> · {fmtDate(p.date)}</span>}</div>{p.skills && <div style={{ fontSize: '8.5pt', color: c.link }}>{p.skills}</div>}{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
            case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements', 'Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><span style={{ fontWeight: 600 }}>{a.title}</span>{a.date && <span style={{ fontSize: '8.5pt', color: '#6B7280' }}> · {fmtDate(a.date)}</span>}: <span style={{ fontSize: '9.5pt' }}>{a.description}</span></div>)}</div> : null;
            case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards', 'Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
            case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
            case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications', 'Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
            case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
            case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
            case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
            default: return null;
          }
        })}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 4 }}>
          <div>{d.education?.length > 0 && <>{sec(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ fontWeight: 'bold', fontSize: '9.5pt' }}>{edu.degree}</div><div style={{ color: c.link, fontSize: '9pt' }}>{edu.school}</div><div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(edu.endDate)}{edu.gpa && ` · GPA ${edu.gpa}`}</div></div>)}</>}</div>
          <div>{d.skills?.length > 0 && <>{sec(secT(d,'skills', 'Expertise'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 5, ...noBreak }}><div style={{ fontSize: '8pt', fontWeight: 600 }}>{s.category}</div><div style={{ fontSize: '8pt', color: '#6B7280' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}</>}</div>
        </div>
      </div>
    </div>
  );
};

// ── MINIMAL ───────────────────────────────────────────────────────────────────
const MinimalTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#059669', body: '#111827' });
  const ds = useDesign(pi, { fontFamily: '"Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.7 });
  const sec = t => <div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{t}</div>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '32px 40px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: 24, display: 'flex', gap: 16, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '22pt', fontWeight: 700, letterSpacing: 0, color: c.section }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ color: '#6B7280', marginTop: 2 }}>{pi.jobTitle}</div>}
          <div style={{ fontSize: '8.5pt', color: '#9CA3AF', marginTop: 4 }}>{[pi.email, pi.phone, pi.location].filter(Boolean).join('   ·   ')}</div>
          <IdentityRow pi={pi} />
        </div>
      </div>
      {pi.summary && <p style={{ margin: '0 0 20px', color: '#374151', borderLeft: `3px solid ${c.section}`, paddingLeft: 12 }}>{pi.summary}</p>}
      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, marginBottom: 12 }}>{secT(d,'experience', 'Experience')}</div>{d.experience.map(e => <div key={e.id} data-resume-entry style={{ display: 'flex', gap: 18, marginBottom: 14, ...noBreak }}><div style={{ width: 70, fontSize: '8pt', color: '#9CA3AF', textAlign: 'right', paddingTop: 1, flexShrink: 0 }}>{fmtDate(e.startDate)}<br/>—<br/>{e.current ? 'Now' : fmtDate(e.endDate)}</div><div style={{ flex: 1 }}><div style={{ fontWeight: 600 }}>{e.title}</div><div style={{ color: c.link, fontSize: '9pt' }}>{e.company}</div>{e.description && <p style={{ margin: '2px 0', fontSize: '8.5pt', color: '#4B5563' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div></div>)}</div> : null;
          case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}><div><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, marginBottom: 10 }}>{secT(d,'education', 'Education')}</div>{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ fontWeight: 600 }}>{edu.degree}</div><div style={{ color: '#6B7280', fontSize: '9pt' }}>{edu.school}</div><div style={{ color: '#9CA3AF', fontSize: '8pt' }}>{fmtDate(edu.endDate)}</div></div>)}</div>{d.skills?.length > 0 && <div><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, marginBottom: 10 }}>{secT(d,'skills', 'Skills')}</div>{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 5, ...noBreak }}><strong style={{ fontSize: '9pt' }}>{s.category}:</strong> <span style={{ color: '#4B5563', fontSize: '9pt' }}>{s.items}</span></div>)}</div>}</div></div> : (d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}><div /><div><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, marginBottom: 10 }}>{secT(d,'skills', 'Skills')}</div>{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 5, ...noBreak }}><strong style={{ fontSize: '9pt' }}>{s.category}:</strong> <span style={{ color: '#4B5563', fontSize: '9pt' }}>{s.items}</span></div>)}</div></div></div> : null);
          case 'skills': return null; // rendered with education above
          case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'certifications', 'Certifications')}</div><CertificationsItems certifications={d.certifications} color={c.link} /></div> : null;
          case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'languages', 'Languages')}</div><LanguagesItems languages={d.languages} /></div> : null;
          case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'projects', 'Projects')}</div>{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><span style={{ fontWeight: 600 }}>{p.title}</span>{p.date && <span style={{ color: '#9CA3AF', fontSize: '8.5pt' }}> · {fmtDate(p.date)}</span>}{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt', color: '#4B5563' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'achievements', 'Achievements')}</div>{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 6, fontSize: '9.5pt', ...noBreak }}><span style={{ fontWeight: 600 }}>{a.title}</span> — {a.description}</div>)}</div> : null;
          case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'awards', 'Awards')}</div><AwardsItems awards={d.awards} color={c.link} /></div> : null;
          case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'volunteering', 'Volunteering')}</div><VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
          case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'publications', 'Publications')}</div><PublicationsItems publications={d.publications} /></div> : null;
          case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3, margin: '16px 0 10px' }}>{secT(d,'references', 'References')}</div><ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
          case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
          default: return null;
        }
      })}
    </div>
  );
};

// ── CREATIVE (red sidebar) ────────────────────────────────────────────────────
const CreativeTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#DC2626', name: '#ffffff', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI",sans-serif', fontSize: 10, lineHeight: 1.5 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sec = (t, mt = 14) => <><div style={{ fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', ...sectionBorderStyle(pi, c.section + '22', 2), paddingBottom: 4, marginBottom: 8, marginTop: mt }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, position: 'relative', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 210, top: (i + 1) * defaultMinH, height: 56, background: c.accent, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 210, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      <div style={{ width: 210, background: c.accent, color: 'white', padding: '28px 18px', flexShrink: 0, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
        {pi.photo && <div style={{ textAlign: 'center', marginBottom: 16 }}><Photo pi={pi} border="3px solid rgba(255,255,255,0.4)" fallbackSize={80} /></div>}
        <div style={{ fontSize: '16pt', fontWeight: 800, lineHeight: 1.2, marginBottom: 4 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: '9pt', opacity: 0.85, marginBottom: 12 }}>{pi.jobTitle}</div>}
        <div style={{ height: 2, background: 'rgba(255,255,255,0.3)', margin: '12px 0' }} />
        {[pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).map((c, i) => <div key={i} style={{ fontSize: '8pt', opacity: 0.85, marginBottom: 4 }}>{c}</div>)}
        {(pi.showDOB || pi.showNationality || pi.showSex) && <><div style={{ height: 2, background: 'rgba(255,255,255,0.3)', margin: '12px 0' }} />{pi.showDOB && pi.dateOfBirth && <div style={{ fontSize: '8pt', opacity: 0.8, marginBottom: 3 }}>DOB: {pi.dateOfBirth}</div>}{pi.showNationality && pi.nationality && <div style={{ fontSize: '8pt', opacity: 0.8, marginBottom: 3 }}>Nationality: {pi.nationality}</div>}{pi.showSex && pi.sex && <div style={{ fontSize: '8pt', opacity: 0.8 }}>Sex: {pi.sex}</div>}</>}
        {getSectionOrder(d).filter(id => ['skills','languages'].includes(id)).map(id => {
          switch (id) {
            case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ height: 2, background: 'rgba(255,255,255,0.3)', margin: '12px 0' }} /><div style={{ fontSize: '9pt', fontWeight: 700, textTransform: sectionTransform(pi), letterSpacing: 1, marginBottom: 8 }}>{secT(d,'skills', 'Skills')}</div>{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><div style={{ fontSize: '8pt', fontWeight: 700 }}>{s.category}</div><div style={{ fontSize: '7.5pt', opacity: 0.8 }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}</div> : null;
            case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ height: 2, background: 'rgba(255,255,255,0.3)', margin: '12px 0' }} /><div style={{ fontSize: '9pt', fontWeight: 700, textTransform: sectionTransform(pi), letterSpacing: 1, marginBottom: 8 }}>{secT(d,'languages', 'Languages')}</div>{d.languages.map(l => <div key={l.id} data-resume-entry style={{ fontSize: '8pt', marginBottom: 3, ...noBreak }}>{l.language}{l.proficiency && ` · ${l.proficiency}`}</div>)}</div> : null;
            default: return null;
          }
        })}
      </div>
      <div style={{ flex: 1, padding: '24px 22px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.summary && <><div style={{ fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1, fontSize: '9.5pt', marginBottom: 6 }}>{pi.summaryTitle || 'About Me'}</div><p style={{ margin: '0 0 16px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        {getSectionOrder(d).filter(id => !['skills','languages'].includes(id)).map(id => {
          switch (id) {
            case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience', 'Experience'), 0)}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 700 }}>{e.title}</div><div style={{ color: c.link, fontSize: '9pt' }}>{e.company}</div></div><div style={{ fontSize: '8pt', color: '#6B7280' }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div>{e.description && <p style={{ margin: '3px 0 2px', fontSize: '8.5pt', color: '#4B5563' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
            case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ fontWeight: 700 }}>{edu.degree}</div><div style={{ color: c.link, fontSize: '9pt' }}>{edu.school}</div><div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(edu.endDate)}{edu.gpa && ` · GPA ${edu.gpa}`}</div></div>)}</div> : null;
            case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'projects', 'Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><span style={{ fontWeight: 700 }}>{p.title}</span>{p.skills && <span style={{ color: '#6B7280', fontSize: '8.5pt' }}> · {p.skills}</span>}{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
            case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements', 'Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><span style={{ fontWeight: 600 }}>{a.title}</span>: <span style={{ fontSize: '9pt' }}>{a.description}</span></div>)}</div> : null;
            case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'certifications', 'Certifications'))}<CertificationsItems certifications={d.certifications} color={c.link} /></div> : null;
            case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards', 'Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
            case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
            case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications', 'Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
            case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
            case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
            case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
            default: return null;
          }
        })}
      </div>
    </div>
  );
};

// ── TECH ──────────────────────────────────────────────────────────────────────
const TechTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0891B2', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: '"Courier New",monospace', fontSize: 9.5, lineHeight: 1.6 });
  const sec = t => <div style={{ fontWeight: 700, color: c.section, fontSize: '9pt', textTransform: sectionTransform(pi), letterSpacing: 2, borderLeft: `3px solid ${c.section}`, paddingLeft: 8, marginBottom: 10, marginTop: 16 }}>{t}</div>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '24px 28px', background: 'white', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ borderBottom: `3px solid ${c.section}`, paddingBottom: 14, marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '18pt', fontWeight: 700, color: c.section }}>{pi.fullName}</div>
              {pi.jobTitle && <div style={{ fontSize: '10pt', color: '#374151' }}>&gt; {pi.jobTitle}</div>}
              <IdentityRow pi={pi} />
            </div>
          </div>
          <div style={{ fontSize: '8pt', color: '#6B7280', textAlign: 'right' }}>
            {[pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).map((c, i) => <div key={i}>{c}</div>)}
          </div>
        </div>
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Overview')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience', 'Work Experience'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, paddingLeft: 10, borderLeft: `2px solid ${c.sidebar}`, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontWeight: 700 }}>{e.title} <span style={{ color: c.link }}>@</span> {e.company}</span><span style={{ fontSize: '8.5pt', background: c.sidebar, color: c.link, padding: '1px 8px', borderRadius: 4 }}>{fmtDate(e.startDate)} → {e.current ? 'now' : fmtDate(e.endDate)}</span></div>{e.description && <p style={{ margin: '3px 0 2px', fontSize: '8.5pt', color: '#4B5563' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'skills', 'Tech Stack'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 4, fontSize: '9pt', ...noBreak }}><span style={{ color: c.link, fontWeight: 700 }}>[{s.category}]</span> {pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div>)}</div> : null;
          case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'projects', 'Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><div style={{ fontWeight: 700 }}>{p.title}{p.date && <span style={{ fontWeight: 'normal', color: '#6B7280', fontSize: '8.5pt' }}> // {fmtDate(p.date)}</span>}</div>{p.skills && <div style={{ color: c.link, fontSize: '8.5pt' }}>{p.skills}</div>}{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} />{p.link && <a href={p.link} style={{ fontSize: '8pt', color: c.link }}>{p.link}</a>}</div>)}</div> : null;
          case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 4 }}><div>{sec(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><div style={{ fontWeight: 700 }}>{edu.degree}</div><div style={{ fontSize: '9pt', color: c.link }}>{edu.school}</div></div>)}</div>{d.certifications?.length > 0 && <div>{sec(secT(d,'certifications', 'Certifications'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: '9pt', marginBottom: 4, ...noBreak }}><span style={{ fontWeight: 700 }}>{cert.name}</span><br/><span style={{ color: '#6B7280' }}>{cert.issuer}</span></div>)}</div>}</div></div> : (d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 4 }}><div /><div>{sec(secT(d,'certifications', 'Certifications'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: '9pt', marginBottom: 4, ...noBreak }}><span style={{ fontWeight: 700 }}>{cert.name}</span><br/><span style={{ color: '#6B7280' }}>{cert.issuer}</span></div>)}</div></div></div> : null);
          case 'certifications': return null; // rendered with education above
          case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'languages', 'Languages'))}<LanguagesItems languages={d.languages} /></div> : null;
          case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements', 'Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ fontSize: '9.5pt', marginBottom: 4, ...noBreak }}>▸ <strong>{a.title}</strong>: {a.description}</div>)}</div> : null;
          case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards', 'Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
          case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
          case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications', 'Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
          case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
          case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
          default: return null;
        }
      })}
    </div>
  );
};

// ── ELEGANT (gold) ────────────────────────────────────────────────────────────
const ElegantTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#B45309', name: '#1F2937', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: '"Garamond","Georgia",serif', fontSize: 10, lineHeight: 1.6 });
  const sec = t => <><div style={{ textAlign: 'center', margin: '16px 0 10px' }}><span style={{ background: c.section, color: 'white', padding: '3px 18px', fontSize: '8.5pt', fontWeight: 700, textTransform: sectionTransform(pi), letterSpacing: 2, borderRadius: 2 }}>{t}</span></div></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '32px 36px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', borderTop: `3px double ${c.section}`, borderBottom: `3px double ${c.section}`, padding: '18px 0', marginBottom: 20 }}>
        <div style={{ fontSize: '24pt', fontWeight: 700, color: c.name === '#ffffff' ? c.body : c.name, letterSpacing: 1 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ color: c.link, fontSize: '11pt', fontStyle: 'italic', marginTop: 4 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 8 }}>{[pi.email, pi.phone, pi.location].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ textAlign: 'justify', fontSize: '9.5pt', margin: '0 0 8px' }}>{pi.summary}</p></>}
      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience', 'Professional Experience'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 700 }}>{e.title}</div><div style={{ color: c.link, fontStyle: 'italic', fontSize: '9.5pt' }}>{e.company}{e.location && ` · ${e.location}`}</div></div><div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div>{e.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#374151' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'education': return (d.education?.length > 0 || d.skills?.length > 0) ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>{d.education?.length > 0 && <div>{sec(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ fontWeight: 700 }}>{edu.degree}</div><div style={{ color: c.link, fontStyle: 'italic', fontSize: '9.5pt' }}>{edu.school}</div></div>)}</div>}{d.skills?.length > 0 && <div>{sec(secT(d,'skills', 'Competencies'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 4, fontSize: '9.5pt', ...noBreak }}><strong>{s.category}:</strong> {pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div>)}</div>}</div></div> : null;
          case 'skills': return null; // rendered with education above
          case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'projects', 'Notable Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><span style={{ fontWeight: 700 }}>{p.title}</span>: <span style={{ fontSize: '9.5pt' }}>{p.summary}</span><BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements', 'Honours & Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 6, fontSize: '9.5pt', ...noBreak }}><strong>{a.title}</strong> — {a.description}</div>)}</div> : null;
          case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'certifications', 'Certifications'))}<div style={{ textAlign: 'center' }}><CertificationsItems certifications={d.certifications} color={c.link} /></div></div> : null;
          case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'languages', 'Languages'))}<div style={{ display: 'flex', justifyContent: 'center' }}><LanguagesItems languages={d.languages} /></div></div> : null;
          case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards', 'Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
          case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
          case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications', 'Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
          case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
          case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
          default: return null;
        }
      })}
    </div>
  );
};

// ── CORPORATE (navy) ──────────────────────────────────────────────────────────
const CorporateTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1E3A5F', name: '#ffffff', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: '"Arial",sans-serif', fontSize: 10, lineHeight: 1.5 });
  const sec = t => <div style={{ background: c.section, color: 'white', padding: '5px 14px', fontWeight: 700, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 1.5, marginBottom: 10, marginTop: 16, borderRadius: 2 }}>{t}</div>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ background: c.accent, color: 'white', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Photo pi={pi} border="2px solid #FED7AA" fallbackSize={64} />
          <div>
            <div style={{ fontSize: '20pt', fontWeight: 700 }}>{pi.fullName}</div>
            {pi.jobTitle && <div style={{ color: c.name, opacity: 0.82, fontSize: '10.5pt', marginTop: 2 }}>{pi.jobTitle}</div>}
          </div>
        </div>
        <div style={{ fontSize: '8.5pt', color: '#CBD5E1', textAlign: 'right' }}>
          {[pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).map((c, i) => <div key={i}>{c}</div>)}
          {pi.showDOB && pi.dateOfBirth && <div style={{ marginTop: 4 }}>DOB: {pi.dateOfBirth}</div>}
          {pi.showNationality && pi.nationality && <div>{pi.nationality}</div>}
        </div>
      </div>
      <div style={{ padding: '16px 28px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Executive Summary')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt' }}>{pi.summary}</p></>}
        {getSectionOrder(d).map(id => {
          switch (id) {
            case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience', 'Experience'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><span style={{ fontWeight: 700 }}>{e.title}</span> <span style={{ color: c.link }}>·</span> <span style={{ color: c.link }}>{e.company}</span></div><span style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</span></div>{e.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#4B5563' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
            case 'education': return (d.education?.length > 0 || d.projects?.length > 0 || d.skills?.length > 0 || d.certifications?.length > 0) ? <div key={id} data-resume-section style={{ ...noBreak }}><div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 20 }}><div>{d.education?.length > 0 && <>{sec(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ fontWeight: 700 }}>{edu.degree}</div><div style={{ color: c.link }}>{edu.school}</div><div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(edu.endDate)}</div></div>)}</>}{d.projects?.length > 0 && <>{sec(secT(d,'projects', 'Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><span style={{ fontWeight: 700 }}>{p.title}</span>{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</>}</div><div>{d.skills?.length > 0 && <>{sec(secT(d,'skills', 'Core Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 5, fontSize: '9pt', ...noBreak }}><div style={{ fontWeight: 700, color: c.link }}>{s.category}</div><div style={{ color: '#4B5563' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}</>}{d.certifications?.length > 0 && <>{sec(secT(d,'certifications', 'Credentials'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: '9pt', marginBottom: 4, ...noBreak }}>{cert.name}<br/><span style={{ color: '#6B7280' }}>{cert.issuer}</span></div>)}</>}</div></div></div> : null;
            case 'projects': return null; // rendered with education above
            case 'skills': return null; // rendered with education above
            case 'certifications': return null; // rendered with education above
            case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'languages', 'Languages'))}<LanguagesItems languages={d.languages} /></div> : null;
            case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements', 'Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 6, fontSize: '9.5pt', ...noBreak }}>⭐ <strong>{a.title}</strong> — {a.description}</div>)}</div> : null;
            case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards', 'Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
            case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
            case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications', 'Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
            case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
            case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
            case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
            default: return null;
          }
        })}
      </div>
    </div>
  );
};

// ── ACADEMIC ──────────────────────────────────────────────────────────────────
const AcademicTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#374151', body: '#111827' });
  const ds = useDesign(pi, { fontFamily: '"Times New Roman",serif', fontSize: 10.5, lineHeight: 1.65 });
  const sec = t => <><div style={{ fontWeight: 700, color: c.section, fontSize: '10pt', textTransform: sectionTransform(pi), letterSpacing: 1.5, ...sectionBorderStyle(pi, c.section, 2), paddingBottom: 5, marginBottom: 8, marginTop: 16 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '32px 38px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        {pi.photo && <div style={{ marginBottom: 10 }}><Photo pi={pi} border="none" fallbackSize={80} /></div>}
        <div style={{ fontSize: '22pt', fontWeight: 700, letterSpacing: 0.5 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: '11pt', color: c.link, marginTop: 4 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: '9pt', color: '#6B7280', marginTop: 6 }}>{[pi.email, pi.phone, pi.location, pi.website].filter(Boolean).join('  |  ')}</div>
        {(pi.showDOB || pi.showNationality || pi.showSex) && <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 4 }}>{[pi.showDOB && pi.dateOfBirth && `DOB: ${pi.dateOfBirth}`, pi.showNationality && pi.nationality && `Nationality: ${pi.nationality}`, pi.showSex && pi.sex && `Sex: ${pi.sex}`].filter(Boolean).join('  |  ')}</div>}
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Research Interests')}<p style={{ margin: '0 0 4px', textAlign: 'justify' }}>{pi.summary}</p></>}
      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience', 'Academic Positions'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 700 }}>{e.title}</div><div style={{ fontStyle: 'italic' }}>{e.company}{e.location && `, ${e.location}`}</div></div><div style={{ fontSize: '9.5pt', color: '#4B5563' }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div>{e.description && <p style={{ margin: '3px 0 2px', fontSize: '9.5pt' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 700 }}>{edu.degree}</div><div style={{ fontStyle: 'italic' }}>{edu.school}{edu.location && `, ${edu.location}`}</div></div><div style={{ fontSize: '9.5pt', color: '#4B5563' }}>{fmtDate(edu.startDate)} – {fmtDate(edu.endDate)}</div></div>{edu.gpa && <div style={{ fontSize: '9pt' }}>GPA: {edu.gpa}</div>}</div>)}</div> : null;
          case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications', 'Selected Publications'))}{d.publications.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 8, fontSize: '10pt', ...noBreak }}>{p.authors} ({p.year}). "{p.title}." <em>{p.venue}</em>.</div>)}</div> : null;
          case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'skills', 'Research Skills & Tools'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ fontSize: '10pt', marginBottom: 3, ...noBreak }}><strong>{s.category}:</strong> {pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div>)}</div> : null;
          case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'projects', 'Research Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><div style={{ fontWeight: 700 }}>{p.title}</div>{p.summary && <p style={{ margin: '2px 0 4px' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements', 'Awards & Honours'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontWeight: 700 }}>{a.title}</span>{a.date && <span style={{ color: '#6B7280', fontSize: '9pt' }}>{fmtDate(a.date)}</span>}</div><p style={{ margin: '2px 0', fontSize: '9.5pt' }}>{a.description}</p></div>)}</div> : null;
          case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'certifications', 'Certifications'))}<CertificationsItems certifications={d.certifications} color={c.link} /></div> : null;
          case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'languages', 'Languages'))}<LanguagesItems languages={d.languages} /></div> : null;
          case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards', 'Additional Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
          case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
          case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
              case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
          case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
          default: return null;
        }
      })}
    </div>
  );
};

// ── NAVY TIMELINE ────────────────────────────────────────────────────────────
const NavyTimelineTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#2C3E50', body: '#333333' });
  const navy = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Segoe UI", Arial, sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const body = c.body;
  const sym = pi.bulletSymbol || '•';

  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const pageFillH = React.useContext(PageFillContext);
  const minH = pageFillH || defaultMinH;
  const allSkills = (d.skills || []).flatMap(s => s.items.split(',').map(t => t.trim()).filter(Boolean));
  const refEntry = d.references?.[0];
  const FlowText = ({ children, style }) => {
    const parts = String(children || '').split(/(\s+)/);
    return (
      <p data-navy-flow-text style={style}>
        {parts.map((part, i) => /\s+/.test(part)
          ? part
          : <span key={i} data-navy-flow-word>{part}</span>)}
      </p>
    );
  };

  // Sidebar section heading — respects d.sectionIcons when a sectionId is supplied
  const sideHead = (lbl, sectionId) => {
    const customId = sectionId && (d.sectionIcons || {})[sectionId];
    const IconComp = customId ? PREVIEW_ICON_MAP[customId] : null;
    return (
      <div data-navy-page-safe="sidebar-heading" style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: 'uppercase', letterSpacing: 1.5, borderBottom: `1.5px solid ${c.section}66`, paddingBottom: 5, marginTop: 'var(--navy-page-offset, 0px)', marginBottom: 8 }}>
        {IconComp ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <IconComp size={8} strokeWidth={2.2} style={{ flexShrink: 0 }} />
            <span>{lbl}</span>
          </span>
        ) : lbl}
      </div>
    );
  };

  // Right column section heading — respects d.sectionIcons, falls back to defaultIcon
  const rightHead = (defaultIcon, title, sectionId) => {
    const customId = sectionId && (d.sectionIcons || {})[sectionId];
    const IconComp = (customId && PREVIEW_ICON_MAP[customId]) || defaultIcon;
    return (
      <div data-navy-page-safe="heading" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'var(--navy-page-offset, 0px)', marginBottom: 8, breakAfter: 'avoid', pageBreakAfter: 'avoid', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: c.section, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IconComp size={11} color="white" strokeWidth={2.5} />
        </div>
        <div style={{ flex: 1, fontSize: '10.5pt', fontWeight: 700, color: c.section, textTransform: 'uppercase', letterSpacing: 2.5, borderBottom: `1.5px solid ${c.section}`, paddingBottom: 2 }}>{title}</div>
      </div>
    );
  };

  // Single-block timeline entry: vertical gray line + circle, for non-list sections
  const timelineBlock = (content) => (
    <div style={{ position: 'relative', paddingLeft: 28 }}>
      <div style={{ position: 'absolute', left: 9, top: 6, bottom: 0, width: 1.5, background: '#CBD5E1' }} />
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: -23, top: 4, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${navy}`, background: 'white' }} />
        {content}
      </div>
    </div>
  );

  // Timeline list: vertical gray line + empty circle per entry
  const timeline = (items, renderItem) => (
    <div style={{ position: 'relative', paddingLeft: 28 }}>
      <div style={{ position: 'absolute', left: 9, top: 6, bottom: 0, width: 1.5, background: '#CBD5E1' }} />
      {items.map((item, i) => (
        <div key={item.id} data-navy-page-safe="item" style={{ position: 'relative', marginTop: 'var(--navy-page-offset, 0px)', marginBottom: i < items.length - 1 ? 14 : 0, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
          <div style={{ position: 'absolute', left: -23, top: 4, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${navy}`, background: 'white' }} />
          {renderItem(item)}
        </div>
      ))}
    </div>
  );

  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);

  return (
    <div style={{ fontFamily: ds.fontFamily, fontSize: ds.fontSize, lineHeight: ds.lineHeight, color: body, display: 'flex', minHeight: minH, background: navy, position: 'relative', overflowWrap: 'break-word', wordBreak: 'break-word' }}>

      {/* Gap covers at top of each overflow page — match panel backgrounds so no content bleeds into the margin zone */}
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, top: (i + 1) * defaultMinH, width: 215, height: 54, background: '#EEF0F2', zIndex: 5 }} />
          <div style={{ position: 'absolute', left: 215, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 5 }} />
          <div style={{ position: 'absolute', left: 0, right: 0, top: (i + 1) * defaultMinH + 10, height: 4, background: navy, zIndex: 2 }} />
        </React.Fragment>
      ))}

      {/* ── LEFT SIDEBAR ── */}
      <div style={{ width: 215, flexShrink: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
        {/* Navy header — color bar only */}
        <div style={{ background: navy, minHeight: 130 }} />

        {/* Sidebar content — photo floats on header/content boundary */}
        <div style={{ flex: 1, background: '#EEF0F2', padding: '16px 14px' }}>
          <NavyPhotoSlot pi={pi} />

          {/* CONTACT */}
          <div style={{ marginBottom: 14 }}>
            {sideHead('Contact', 'contact')}
            {[
              { Icon: Phone,  val: pi.phone },
              { Icon: Mail,   val: pi.email },
              { Icon: MapPin, val: pi.location },
              { Icon: Globe,  val: pi.website },
              { Icon: Link2,  val: pi.linkedin },
            ].filter(x => x.val).map(({ Icon, val }, i) => (
              <div key={i} data-navy-page-safe="sidebar-item" style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 'var(--navy-page-offset, 0px)', marginBottom: 5, fontSize: '8.5pt' }}>
                <Icon size={10} color={navy} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ wordBreak: 'break-all' }}>{val}</span>
              </div>
            ))}
          </div>

          {/* SKILLS */}
          {allSkills.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {sideHead(st(d, 'skills', 'Skills'), 'skills')}
              {allSkills.map((sk, i) => (
                <div key={i} data-navy-page-safe="sidebar-item" style={{ display: 'flex', gap: 6, marginTop: 'var(--navy-page-offset, 0px)', marginBottom: 3, fontSize: '8.5pt' }}>
                  <span style={{ flexShrink: 0 }}>{sym}</span><span>{sk}</span>
                </div>
              ))}
            </div>
          )}

          {/* LANGUAGES */}
          {d.languages?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {sideHead(st(d, 'languages', 'Languages'), 'languages')}
              {d.languages.map(l => (
                <div key={l.id} data-navy-page-safe="sidebar-item" style={{ display: 'flex', gap: 6, marginTop: 'var(--navy-page-offset, 0px)', marginBottom: 3, fontSize: '8.5pt' }}>
                  <span style={{ flexShrink: 0 }}>{sym}</span>
                  <span>{l.language}{l.proficiency ? ` (${l.proficiency})` : ''}</span>
                </div>
              ))}
            </div>
          )}

          {/* CERTIFICATIONS */}
          {d.certifications?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {sideHead(st(d, 'certifications', 'Certifications'), 'certifications')}
              {d.certifications.map(cert => (
                <div key={cert.id} data-navy-page-safe="sidebar-item" style={{ marginTop: 'var(--navy-page-offset, 0px)', marginBottom: 6, fontSize: '8.5pt' }}>
                  <div style={{ fontWeight: 600 }}>{cert.name}</div>
                  {cert.issuer && <div style={{ color: '#5B6770' }}>{cert.issuer}</div>}
                  {cert.date && <div style={{ color: '#8A9499', fontSize: '8pt' }}>{fmtDate(cert.date)}</div>}
                </div>
              ))}
            </div>
          )}

          {/* REFERENCE */}
          {(refEntry || d.referencesAvailableOnRequest) && (
            <div style={{ marginBottom: 14 }}>
              {sideHead(st(d, 'references', 'Reference'), 'references')}
              {d.referencesAvailableOnRequest
                ? <p data-navy-page-safe="sidebar-item" style={{ margin: 0, marginTop: 'var(--navy-page-offset, 0px)', fontSize: '8.5pt', fontStyle: 'italic', color: '#5B6770' }}>Available upon request.</p>
                : refEntry && (
                  <div data-navy-page-safe="sidebar-item" style={{ marginTop: 'var(--navy-page-offset, 0px)', fontSize: '8.5pt' }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{refEntry.name}</div>
                    {(refEntry.title || refEntry.company) && (
                      <div style={{ marginBottom: 1 }}>{[refEntry.title, refEntry.company].filter(Boolean).join(' / ')}</div>
                    )}
                    {refEntry.relationship && <div style={{ fontStyle: 'italic', color: '#5B6770', marginBottom: 1 }}>{refEntry.relationship}</div>}
                    {refEntry.phone && <div><span style={{ fontWeight: 600 }}>Phone:</span> {refEntry.phone}</div>}
                    {refEntry.email && <div><span style={{ fontWeight: 600 }}>Email :</span> {refEntry.email}</div>}
                  </div>
                )
              }
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT MAIN ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Navy header — name + title */}
        <div style={{ background: navy, minHeight: 130, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '18px 22px' }}>
          <div style={{ fontSize: '22pt', fontWeight: 900, color: 'white', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.1 }}>
            {sampleProfileName(pi)}
          </div>
          {pi.jobTitle && (
            <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 3.5, marginTop: 7 }}>
              {pi.jobTitle}
            </div>
          )}
          <IdentityRow pi={pi} />
        </div>

        {/* Right content */}
        <div style={{ flex: 1, padding: '16px 22px', background: 'white', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>

          {/* PROFILE / SUMMARY */}
          {pi.summary && (
            <div data-resume-section style={{ marginBottom: 16, ...noBreak }}>
              {rightHead(User, pi.summaryTitle || 'Profile', 'summary')}
              {timelineBlock(<FlowText style={{ margin: 0, fontSize: '9.5pt', textAlign: 'justify', color: body, lineHeight: ds.lineHeight }}>{pi.summary}</FlowText>)}
            </div>
          )}

          {/* Ordered sections */}
          {getSectionOrder(d).map(id => {
            switch (id) {
              case 'experience': return d.experience?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(Briefcase, st(d, 'experience', 'Work Experience'), 'experience')}
                  {timeline(d.experience, e => (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>{e.company}</div>
                        <div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {fmtDate(e.startDate)} - {e.current ? 'PRESENT' : fmtDate(e.endDate)}
                        </div>
                      </div>
                      {e.title && <div style={{ fontSize: '9pt', color: '#6B7280', fontStyle: 'italic' }}>{e.title}</div>}
                      {e.description && <FlowText style={{ margin: '3px 0 2px', fontSize: '9pt', textAlign: 'justify', color: '#4B5563' }}>{e.description}</FlowText>}
                      <BulletList bullets={e.bullets} symbol={sym} />
                    </>
                  ))}
                </div>
              ) : null;

              case 'education': return d.education?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(GraduationCap, st(d, 'education', 'Education'), 'education')}
                  {timeline(d.education, edu => (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>{edu.degree}</div>
                        <div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {edu.startDate ? `${fmtDate(edu.startDate)} - ` : ''}{fmtDate(edu.endDate)}
                        </div>
                      </div>
                      <div style={{ fontSize: '9pt', color: '#4B5563' }}>{edu.school}{edu.location ? ` | ${edu.location}` : ''}</div>
                      {edu.gpa && <div style={{ fontSize: '9pt', marginTop: 1 }}><strong>GPA:</strong> {edu.gpa} / 4.0</div>}
                    </>
                  ))}
                </div>
              ) : null;

              case 'projects': return d.projects?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(Folder, st(d, 'projects', 'Projects'), 'projects')}
                  {timeline(d.projects, p => (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>{p.title}</div>
                        {p.date && <div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtDate(p.date)}</div>}
                      </div>
                      {p.skills && <div style={{ fontSize: '8.5pt', color: '#6B7280', fontStyle: 'italic' }}>{p.skills}</div>}
                      {p.summary && <FlowText style={{ margin: '2px 0', fontSize: '9pt', textAlign: 'justify' }}>{p.summary}</FlowText>}
                      <BulletList bullets={p.bullets} symbol={sym} />
                      {p.link && <a href={p.link} style={{ fontSize: '8pt', color: c.link }}>{p.link}</a>}
                    </>
                  ))}
                </div>
              ) : null;

              case 'achievements': return d.achievements?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(Trophy, st(d, 'achievements', 'Achievements'), 'achievements')}
                  {timeline(d.achievements, a => (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>{a.title}</div>
                        {a.date && <div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtDate(a.date)}</div>}
                      </div>
                      {a.description && <FlowText style={{ margin: '2px 0', fontSize: '9pt' }}>{a.description}</FlowText>}
                    </>
                  ))}
                </div>
              ) : null;

              case 'awards': return d.awards?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(Award, st(d, 'awards', 'Awards'), 'awards')}
                  {timeline(d.awards, a => (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontWeight: 700 }}>{a.title}</span>
                        {a.date && <span style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtDate(a.date)}</span>}
                      </div>
                      {a.issuer && <div style={{ fontSize: '8.5pt', color: c.link, fontStyle: 'italic' }}>{a.issuer}</div>}
                      {a.description && <FlowText style={{ margin: '2px 0 0', fontSize: '9pt', color: '#4B5563' }}>{a.description}</FlowText>}
                    </>
                  ))}
                </div>
              ) : null;

              case 'volunteering': return d.volunteering?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(HeartHandshake, st(d, 'volunteering', 'Volunteering'), 'volunteering')}
                  {timeline(d.volunteering, v => (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                        <div>
                          <div style={{ fontWeight: 700 }}>{v.role}</div>
                          <div style={{ fontSize: '9pt', color: c.link }}>{v.organization}{v.location && ` · ${v.location}`}</div>
                        </div>
                        {(v.startDate || v.endDate) && (
                          <div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {fmtDate(v.startDate)} – {v.current ? 'Present' : fmtDate(v.endDate)}
                          </div>
                        )}
                      </div>
                      {v.description && <FlowText style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#4B5563' }}>{v.description}</FlowText>}
                      <BulletList bullets={v.bullets} symbol={sym} />
                    </>
                  ))}
                </div>
              ) : null;

              case 'publications': return d.publications?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(BookOpen, st(d, 'publications', 'Publications'), 'publications')}
                  {timeline(d.publications, p => (
                    <div style={{ fontSize: '9.5pt' }}>
                      {p.authors && <span style={{ color: '#4B5563' }}>{p.authors} </span>}
                      {p.year && <span style={{ color: '#6B7280' }}>({p.year}). </span>}
                      "{p.title}."
                      {p.venue && <> <em style={{ color: '#4B5563' }}>{p.venue}</em>.</>}
                    </div>
                  ))}
                </div>
              ) : null;

              case 'goals': return d.goals ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(Target, st(d, 'goals', 'Career Goals'), 'goals')}
                  {timelineBlock(<FlowText style={{ margin: 0, fontSize: '9.5pt', color: body }}>{d.goals}</FlowText>)}
                </div>
              ) : null;

              case 'strengths': return d.strengths ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(Zap, st(d, 'strengths', 'Key Strengths'), 'strengths')}
                  {timelineBlock(
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {d.strengths.split(',').map((s, i) => s.trim() ? (
                        <span key={i} style={{ background: c.section + '15', color: c.section, border: `1px solid ${c.section}33`, borderRadius: 4, padding: '2px 8px', fontSize: '8.5pt', fontWeight: 500 }}>{s.trim()}</span>
                      ) : null)}
                    </div>
                  )}
                </div>
              ) : null;

              case 'hobbies': return d.hobbies ? (
                <div key={id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                  {rightHead(Smile, st(d, 'hobbies', 'Hobbies & Interests'), 'hobbies')}
                  {timelineBlock(
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {d.hobbies.split(',').map((h, i) => h.trim() ? (
                        <span key={i} style={{ background: c.section + '15', color: c.section, border: `1px solid ${c.section}33`, borderRadius: 20, padding: '2px 8px', fontSize: '8.5pt' }}>{h.trim()}</span>
                      ) : null)}
                    </div>
                  )}
                </div>
              ) : null;

              case 'customBlocks': return (
                <React.Fragment key={id}>
                  {(d.customBlocks || []).map(blk => (
                    <div key={blk.id} data-resume-section style={{ marginBottom: 16, ...noBreak }}>
                      {rightHead(Layers, blk.title)}
                      {timelineBlock(
                        <div>
                          {blk.description && <FlowText style={{ margin: '0 0 4px', fontSize: '9.5pt' }}>{blk.description}</FlowText>}
                          {blk.showDate && blk.date && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(blk.date)}</div>}
                          {blk.showLink && blk.link && <a href={blk.link} style={{ fontSize: '8.5pt', color: c.link }}>{blk.link}</a>}
                          {blk.showBullets && <BulletList bullets={blk.bullets} symbol={sym} />}
                        </div>
                      )}
                    </div>
                  ))}
                </React.Fragment>
              );

              // Sidebar items — skip in right column
              case 'skills':
              case 'languages':
              case 'certifications':
              case 'references':
                return null;
              default: return null;
            }
          })}
        </div>
      </div>
    </div>
  );
};

// ── BLUE CORPORATE ───────────────────────────────────────────────────────────
const BlueCorporateTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1B3A6B', body: '#1F2937' });
  const navy = c.accent;
  const ds = useDesign(pi, { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 10, lineHeight: 1.6, padding: 32 });
  const pad = ds.padding;
  const body = c.body;
  const sym = pi.bulletSymbol || '•';
  const divStyle = pi.sectionDivider || 'line';
  const divColor = '#CCCCCC';

  const sections = [];

  if (pi.summary) {
    sections.push({
      key: 'summary',
      label: pi.summaryTitle || 'Summary',
      el: <p style={{ margin: 0, textAlign: 'justify', fontSize: ds.fontSize, lineHeight: ds.lineHeight }}>{pi.summary}</p>,
    });
  }

  getSectionOrder(d).forEach(id => {
    switch (id) {
      case 'experience':
        if (d.experience?.length > 0) sections.push({ key: 'experience', label: secT(d, 'experience', 'Work Experience'), el: (
          <React.Fragment>{d.experience.map((e, i) => (
            <div key={e.id} data-resume-entry style={{ marginBottom: i < d.experience.length - 1 ? 12 : 0, ...noBreak }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontWeight: 700 }}>{e.title}{e.company ? `, ${e.company}` : ''}</div>
                <div style={{ fontWeight: 700, fontSize: '9pt', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {fmtDate(e.startDate)} - {e.current ? 'Present' : fmtDate(e.endDate)}
                </div>
              </div>
              {e.location && <div style={{ fontSize: '8.5pt', color: '#6B7280', fontStyle: 'italic' }}>{e.location}</div>}
              {e.description && <p style={{ margin: '2px 0', fontSize: '9pt', color: '#4B5563' }}>{e.description}</p>}
              <BulletList bullets={e.bullets} symbol={sym} />
            </div>
          ))}</React.Fragment>
        )}); break;
      case 'education':
        if (d.education?.length > 0) sections.push({ key: 'education', label: secT(d, 'education', 'Education'), el: (
          <React.Fragment>{d.education.map((edu, i) => (
            <div key={edu.id} data-resume-entry style={{ marginBottom: i < d.education.length - 1 ? 10 : 0, ...noBreak }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontWeight: 700 }}>{edu.degree}</div>
                <div style={{ fontSize: '9pt', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {edu.startDate ? `${fmtDate(edu.startDate)} - ` : ''}{fmtDate(edu.endDate)}
                </div>
              </div>
              <div style={{ fontSize: '9pt' }}>{edu.school}{edu.location ? `, ${edu.location}` : ''}</div>
              {edu.gpa && <div style={{ display: 'flex', gap: 5, fontSize: '9pt', marginTop: 1 }}><span>{sym}</span><span>Final CGPA: {edu.gpa}</span></div>}
            </div>
          ))}</React.Fragment>
        )}); break;
      case 'skills':
        if (d.skills?.length > 0) {
          const allItems = d.skills.flatMap(s => s.items.split(',').map(t => t.trim()).filter(Boolean));
          const half = Math.ceil(allItems.length / 2);
          const col1 = allItems.slice(0, half);
          const col2 = allItems.slice(half);
          sections.push({ key: 'skills', label: secT(d, 'skills', 'Key Skills'), el: (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16 }}>
              <div>{col1.map((item, i) => <div key={i} style={{ display: 'flex', gap: 5, fontSize: ds.fontSize, marginBottom: 2 }}><span style={{ flexShrink: 0 }}>{sym}</span><span>{item}</span></div>)}</div>
              <div>{col2.map((item, i) => <div key={i} style={{ display: 'flex', gap: 5, fontSize: ds.fontSize, marginBottom: 2 }}><span style={{ flexShrink: 0 }}>{sym}</span><span>{item}</span></div>)}</div>
            </div>
          )});
        } break;
      case 'projects':
        if (d.projects?.length > 0) sections.push({ key: 'projects', label: secT(d, 'projects', 'Projects'), el: (
          <React.Fragment>{d.projects.map((p, i) => (
            <div key={p.id} data-resume-entry style={{ marginBottom: i < d.projects.length - 1 ? 10 : 0, ...noBreak }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontWeight: 700 }}>{p.title}</div>
                {p.date && <div style={{ fontSize: '9pt', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtDate(p.date)}</div>}
              </div>
              {p.skills && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{p.skills}</div>}
              {p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}
              <BulletList bullets={p.bullets} symbol={sym} />
              {p.link && <a href={p.link} style={{ fontSize: '8pt', color: c.link }}>{p.link}</a>}
            </div>
          ))}</React.Fragment>
        )}); break;
      case 'certifications':
        if (d.certifications?.length > 0) sections.push({ key: 'certifications', label: secT(d, 'certifications', 'Certifications'), el: (
          <React.Fragment>{d.certifications.map((cert, i) => (
            <div key={cert.id} data-resume-entry style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: i < d.certifications.length - 1 ? 5 : 0, ...noBreak }}>
              <div><span style={{ fontWeight: 700 }}>{cert.name}</span>{cert.issuer && <span style={{ fontSize: '9pt', color: '#4B5563' }}> — {cert.issuer}</span>}</div>
              {cert.date && <div style={{ fontSize: '9pt', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtDate(cert.date)}</div>}
            </div>
          ))}</React.Fragment>
        )}); break;
      case 'languages':
        if (d.languages?.length > 0) {
          const half = Math.ceil(d.languages.length / 2);
          const col1 = d.languages.slice(0, half);
          const col2 = d.languages.slice(half);
          sections.push({ key: 'languages', label: secT(d, 'languages', 'Languages'), el: (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16 }}>
              <div>{col1.map(l => <div key={l.id} data-resume-entry style={{ display: 'flex', gap: 5, fontSize: ds.fontSize, marginBottom: 2, ...noBreak }}><span style={{ flexShrink: 0 }}>{sym}</span><span>{l.language}{l.proficiency ? ` — ${l.proficiency}` : ''}</span></div>)}</div>
              <div>{col2.map(l => <div key={l.id} data-resume-entry style={{ display: 'flex', gap: 5, fontSize: ds.fontSize, marginBottom: 2, ...noBreak }}><span style={{ flexShrink: 0 }}>{sym}</span><span>{l.language}{l.proficiency ? ` — ${l.proficiency}` : ''}</span></div>)}</div>
            </div>
          )});
        } break;
      case 'achievements':
        if (d.achievements?.length > 0) sections.push({ key: 'achievements', label: secT(d, 'achievements', 'Achievements'), el: (
          <React.Fragment>{d.achievements.map((a, i) => (
            <div key={a.id} data-resume-entry style={{ marginBottom: i < d.achievements.length - 1 ? 8 : 0, ...noBreak }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontWeight: 700 }}>{a.title}</div>
                {a.date && <div style={{ fontSize: '9pt', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtDate(a.date)}</div>}
              </div>
              {a.description && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{a.description}</p>}
            </div>
          ))}</React.Fragment>
        )}); break;
      case 'awards':
        if (d.awards?.length > 0) sections.push({ key: 'awards', label: secT(d, 'awards', 'Awards'), el: (
          <AwardsItems awards={d.awards} color={c.link} />
        )}); break;
      case 'volunteering':
        if (d.volunteering?.length > 0) sections.push({ key: 'volunteering', label: secT(d, 'volunteering', 'Volunteering'), el: (
          <VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={sym} />
        )}); break;
      case 'publications':
        if (d.publications?.length > 0) sections.push({ key: 'publications', label: secT(d, 'publications', 'Publications'), el: (
          <PublicationsItems publications={d.publications} />
        )}); break;
      case 'hobbies':
        if (d.hobbies) {
          const items = d.hobbies.split(',').map(h => h.trim()).filter(Boolean);
          if (items.length > 0) sections.push({ key: 'hobbies', label: secT(d, 'hobbies', 'Hobbies & Interests'), el: (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 18px' }}>
              {items.map((h, i) => <span key={i} style={{ display: 'flex', gap: 5 }}><span>{sym}</span><span>{h}</span></span>)}
            </div>
          )});
        } break;
      case 'strengths':
        if (d.strengths) {
          const items = d.strengths.split(',').map(h => h.trim()).filter(Boolean);
          if (items.length > 0) {
            const half = Math.ceil(items.length / 2);
            sections.push({ key: 'strengths', label: secT(d, 'strengths', 'Key Strengths'), el: (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16 }}>
                <div>{items.slice(0, half).map((item, i) => <div key={i} style={{ display: 'flex', gap: 5, fontSize: ds.fontSize, marginBottom: 2 }}><span style={{ flexShrink: 0 }}>{sym}</span><span>{item}</span></div>)}</div>
                <div>{items.slice(half).map((item, i) => <div key={i} style={{ display: 'flex', gap: 5, fontSize: ds.fontSize, marginBottom: 2 }}><span style={{ flexShrink: 0 }}>{sym}</span><span>{item}</span></div>)}</div>
              </div>
            )});
          }
        } break;
      case 'goals':
        if (d.goals) sections.push({ key: 'goals', label: secT(d, 'goals', 'Career Goals'), el: (
          <p style={{ margin: 0, fontSize: '9pt' }}>{d.goals}</p>
        )}); break;
      case 'references':
        if (d.references?.length > 0 || d.referencesAvailableOnRequest) sections.push({ key: 'references', label: secT(d, 'references', 'References'), el: (
          <ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} />
        )}); break;
      case 'customBlocks':
        (d.customBlocks || []).forEach(blk => sections.push({ key: `custom-${blk.id}`, label: blk.title, el: (
          <div>
            {blk.description && <p style={{ margin: '0 0 4px', fontSize: '9pt' }}>{blk.description}</p>}
            {blk.showDate && blk.date && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(blk.date)}</div>}
            {blk.showLink && blk.link && <a href={blk.link} style={{ fontSize: '8.5pt', color: c.link }}>{blk.link}</a>}
            {blk.showBullets && <BulletList bullets={blk.bullets} symbol={sym} />}
          </div>
        )})); break;
      default: break;
    }
  });

  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${pad}px ${pad + 4}px`, background: 'white', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '26pt', fontWeight: 900, color: navy, textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.1 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: '10pt', color: '#6B7280', marginTop: 2 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 6 }}>
          {[pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).join(' | ')}
        </div>
        {pi.website && <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 1 }}>{pi.website}</div>}
        <IdentityRow pi={pi} />
      </div>
      {/* Header rule */}
      <div style={{ height: 3, background: navy, marginBottom: 0 }} />
      {/* Sections */}
      {sections.map((sec, i) => (
        <React.Fragment key={sec.key}>
          {i > 0 && divStyle !== 'none' && (
            divStyle === 'dots'
              ? <div style={{ textAlign: 'center', color: divColor, margin: '0', fontSize: '8pt', letterSpacing: 4, opacity: 0.7 }}>· · · · ·</div>
              : <div style={{ height: 1, background: divColor }} />
          )}
          <div data-resume-section style={{ display: 'flex', padding: '13px 0', ...noBreak }}>
            <div style={{ width: 110, flexShrink: 0, paddingRight: 14, paddingTop: 1, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              <div style={{ fontSize: '7.5pt', fontWeight: 700, color: c.section, textTransform: 'uppercase', letterSpacing: 0.8 }}>{sec.label}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>{sec.el}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Cover Letter Templates ─────────────────────────────────────────────────────
const CL_SIGN_STYLE = { fontFamily: "'Dancing Script', 'Brush Script MT', cursive", fontSize: '22pt', lineHeight: 1.2, marginTop: 6, marginBottom: 2 };

const clDate = () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
const clFormatDate = (d) => {
  if (!d) return '';
  const parsed = new Date(d + 'T00:00:00');
  return isNaN(parsed) ? '' : parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};
const clGreeting = (d) => {
  if (d.openingGreeting) return d.openingGreeting;
  const hm = d.jobInfo?.hiringManager;
  if (hm) return `Dear ${hm},`;
  return 'Dear Hiring Manager,';
};

const CLSignatureBlock = ({ d, accentColor, bodyColor }) => {
  const pi = d.personalInfo || {};
  const salutation = d.closingSalutation || 'Best Regards,';
  const sig = d.signature || { type: 'cursive', image: null };
  const accent = accentColor || pi.accentColor || '#1E3A5F';
  const body = bodyColor || '#1F2937';
  return (
    <div style={{ marginTop: 20 }}>
      <div>{salutation}</div>
      {sig.type === 'cursive' && (
        <div style={{ ...CL_SIGN_STYLE, color: accent }}>{pi.fullName || 'Your Name'}</div>
      )}
      {sig.type === 'uploaded' && sig.image && (
        <img src={sig.image} alt="Signature" style={{ maxHeight: '44pt', maxWidth: '160pt', objectFit: 'contain', display: 'block', margin: '6px 0 2px' }} />
      )}
      <div style={{ fontSize: '9pt', fontWeight: 700, color: body }}>{pi.fullName || 'Your Name'}</div>
    </div>
  );
};

// Template 1 — Classic (navy formal, icon contact row)
const CLClassic = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#1E3A5F';
  const body = pi.bodyColor || '#1F2937';
  const ds = useCLDesign(pi, { font: 'Georgia, serif' });
  const contacts = [pi.phone, pi.email, pi.location].filter(Boolean);
  return (
    <div style={{ fontFamily: ds.font, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.pad(36)}px ${ds.pad(44)}px`, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: '20pt', fontWeight: 800, color: accent, letterSpacing: '-0.3px' }}>{pi.fullName || 'Your Name'}</div>
        <div style={{ fontSize: '9pt', color: '#6B7280', marginTop: 2 }}>{pi.jobTitle || ''}</div>
      </div>
      <div style={{ borderBottom: `2px solid ${accent}`, marginBottom: 8 }} />
      <div style={{ display: 'flex', gap: 20, marginBottom: 6, fontSize: '8.5pt', color: '#4B5563' }}>
        {contacts.map((c, i) => <span key={i}>◈ {c}</span>)}
      </div>
      <div style={{ borderBottom: '1px solid #D1D5DB', marginBottom: 18 }} />
      {clFormatDate(d.letterDate) && <div style={{ textAlign: 'right', color: '#6B7280', fontSize: '9pt', marginBottom: 16 }}>{clFormatDate(d.letterDate)}</div>}
      <div style={{ marginBottom: 18, lineHeight: 1.6 }}>
        {ji.hiringManager && <div style={{ fontWeight: 700 }}>{ji.hiringManager}</div>}
        {ji.company && <div>{ji.company}</div>}
        {ji.role && <div style={{ marginTop: 10, fontWeight: 700 }}>Re: {ji.role}</div>}
      </div>
      <div style={{ marginBottom: 12 }}>{clGreeting(d)}</div>
      {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
      {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
      {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
      <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
    </div>
  );
};

// Template 2 — Modern (large colored name, clean sans layout)
const CLModern = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#0D9488';
  const body = pi.bodyColor || '#1F2937';
  const ds = useCLDesign(pi, { font: 'Inter, sans-serif' });
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.pad(40)}px ${ds.pad(48)}px`, height: '100%', boxSizing: 'border-box', color: body }}>
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: '26pt', fontWeight: 900, color: accent, letterSpacing: '-1px', lineHeight: 1 }}>{pi.fullName || 'Your Name'}</div>
        <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 6 }}>
          {[pi.website, pi.email, pi.phone].filter(Boolean).join('  ·  ')}
        </div>
      </div>
      <div style={{ height: 1, background: '#E5E7EB', margin: '18px 0' }} />
      <div style={{ marginBottom: 20, lineHeight: 1.6 }}>
        <div style={{ color: '#6B7280', fontSize: '8.5pt', marginBottom: 6 }}>{clFormatDate(d.letterDate)}</div>
        {ji.hiringManager && <div style={{ fontWeight: 700 }}>{ji.hiringManager}</div>}
        {ji.role && <div style={{ color: '#6B7280' }}>{ji.role}</div>}
        {ji.company && <div>{ji.company}</div>}
      </div>
      <div style={{ marginBottom: 14 }}>{clGreeting(d)}</div>
      {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
      {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
      {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
      <CLSignatureBlock d={d} accentColor={body} bodyColor={accent} />
    </div>
  );
};

// Template 3 — Professional (blue name left, contact top-right)
const CLProfessional = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#1D4ED8';
  const body = pi.bodyColor || '#1F2937';
  const ds = useCLDesign(pi, { font: 'Inter, sans-serif' });
  const contacts = [pi.location, pi.phone, pi.email, pi.website].filter(Boolean);
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.pad(36)}px ${ds.pad(44)}px`, height: '100%', boxSizing: 'border-box', color: body }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ fontSize: '22pt', fontWeight: 900, color: accent, letterSpacing: '-0.5px', lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
        <div style={{ textAlign: 'right', fontSize: '8.5pt', color: '#4B5563', lineHeight: 1.8 }}>
          {contacts.map((c, i) => <div key={i}>{c}</div>)}
        </div>
      </div>
      <div style={{ height: 2, background: accent, marginBottom: 20 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div style={{ lineHeight: 1.6 }}>
          {ji.hiringManager && <div style={{ fontWeight: 700 }}>{ji.hiringManager}</div>}
          {ji.role && <div style={{ color: '#6B7280' }}>Hiring Manager, {ji.role}</div>}
          {ji.company && <div>{ji.company}</div>}
        </div>
        <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{clFormatDate(d.letterDate)}</div>
      </div>
      <div style={{ marginBottom: 14 }}>{clGreeting(d)}</div>
      {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
      {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
      {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
      <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
    </div>
  );
};

// Template 4 — Bold (large 2-line name, contact with icons right side)
const CLBold = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#4C1D95';
  const body = pi.bodyColor || '#1F2937';
  const ds = useCLDesign(pi, { font: 'Inter, sans-serif' });
  const nameParts = (pi.fullName || 'Your Name').split(' ');
  const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0];
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const contacts = [{ icon: '◈', val: pi.website }, { icon: '✉', val: pi.email }, { icon: '✆', val: pi.phone }, { icon: '⌖', val: pi.location }].filter(c => c.val);
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.pad(36)}px ${ds.pad(44)}px`, height: '100%', boxSizing: 'border-box', color: body }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: '28pt', fontWeight: 900, color: accent, lineHeight: 0.95, letterSpacing: '-1px' }}>{firstName}</div>
          {lastName && <div style={{ fontSize: '28pt', fontWeight: 900, color: accent, lineHeight: 0.95, letterSpacing: '-1px' }}>{lastName}</div>}
        </div>
        <div style={{ textAlign: 'right', fontSize: '8.5pt', color: '#6B7280', lineHeight: 2, marginTop: 4 }}>
          {contacts.map((c, i) => <div key={i}>{c.icon} {c.val}</div>)}
        </div>
      </div>
      <div style={{ height: 2, background: accent, marginBottom: 20 }} />
      <div style={{ textAlign: 'right', fontSize: '8.5pt', color: '#6B7280', marginBottom: 14 }}>{clFormatDate(d.letterDate)}</div>
      <div style={{ marginBottom: 14 }}>{clGreeting(d)}</div>
      {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
      {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
      {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
      <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
    </div>
  );
};

// Template 5 — Minimal (name + contact top-right, thin divider, job reference)
const CLMinimal = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#111827';
  const body = pi.bodyColor || '#1F2937';
  const ds = useCLDesign(pi, { font: 'Georgia, serif' });
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.pad(36)}px ${ds.pad(44)}px`, height: '100%', boxSizing: 'border-box', color: body }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <div style={{ textAlign: 'right', fontSize: '9pt', lineHeight: 1.8 }}>
          <div style={{ fontWeight: 700, color: accent }}>{pi.fullName || 'Your Name'}</div>
          {[pi.phone, pi.email, pi.website, pi.location].filter(Boolean).map((c, i) => <div key={i} style={{ color: '#6B7280' }}>{c}</div>)}
        </div>
      </div>
      <div style={{ height: 1, background: '#D1D5DB', margin: '12px 0 18px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div style={{ lineHeight: 1.65 }}>
          {ji.hiringManager && <div style={{ fontWeight: 700 }}>{ji.hiringManager}</div>}
          {ji.role && <div style={{ color: '#6B7280' }}>Human Resources, {ji.company}</div>}
          {pi.email && <div style={{ color: '#6B7280' }}>{pi.email}</div>}
          {pi.phone && <div style={{ color: '#6B7280' }}>{pi.phone}</div>}
          {pi.website && <div style={{ color: '#6B7280' }}>{pi.website}</div>}
          {pi.location && <div style={{ color: '#6B7280' }}>{pi.location}</div>}
        </div>
        <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{clFormatDate(d.letterDate)}</div>
      </div>
      {ji.role && <div style={{ fontWeight: 700, marginBottom: 12 }}>Job Reference: {ji.role}</div>}
      <div style={{ marginBottom: 14 }}>{clGreeting(d)}</div>
      {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
      {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
      {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
      <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
    </div>
  );
};

// Template 6 — Executive (gold lines, large caps name, two-col recipient+date)
const CLExecutive = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#B8960C';
  const body = pi.bodyColor || '#1A1A1A';
  const ds = useCLDesign(pi, { font: 'Georgia, "Times New Roman", serif' });
  const contacts = [pi.location, pi.email, pi.website].filter(Boolean);
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.pad(36)}px ${ds.pad(44)}px`, height: '100%', boxSizing: 'border-box', color: body }}>
      {/* Name block */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '22pt', fontWeight: 900, color: '#111', letterSpacing: '1px', textTransform: 'uppercase', lineHeight: 1.05 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '9pt', fontWeight: 700, color: '#555', letterSpacing: '2px', textTransform: 'uppercase', marginTop: 4 }}>{pi.jobTitle}</div>}
      </div>
      {/* Gold lines + contact */}
      <div style={{ borderTop: `1.5px solid ${accent}`, paddingTop: 6, marginBottom: 6 }}>
        {contacts.length > 0 && (
          <div style={{ fontSize: '8.5pt', color: '#555', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 16 }}>
            {contacts.map((c, i) => <span key={i}>{i > 0 && <span style={{ color: accent, marginRight: 16 }}>|</span>}{c}</span>)}
          </div>
        )}
      </div>
      <div style={{ borderBottom: `1.5px solid ${accent}`, marginBottom: 22 }} />
      {/* Recipient + Date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div style={{ lineHeight: 1.6 }}>
          {ji.hiringManager && <div style={{ fontWeight: 700 }}>{ji.hiringManager}</div>}
          {ji.role && <div style={{ color: '#555' }}>Hiring Manager, {ji.company}</div>}
          {pi.location && <div style={{ color: '#555' }}>{pi.location}</div>}
        </div>
        <div style={{ fontSize: '9pt', color: '#555' }}>{clFormatDate(d.letterDate)}</div>
      </div>
      <div style={{ marginBottom: 14, fontWeight: 500 }}>{clGreeting(d)}</div>
      {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
      {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
      {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
      <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
      <div style={{ borderBottom: `1.5px solid ${accent}`, marginTop: 20 }} />
    </div>
  );
};

// Template 7 — Elegant (blush header band, name+contact split, pink bottom line)
const CLElegant = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#D4A0A0';
  const headerBg = accent + '22';
  const body = pi.bodyColor || '#1A1A1A';
  const ds = useCLDesign(pi, { font: '"Helvetica Neue", Arial, sans-serif' });
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, height: '100%', boxSizing: 'border-box', color: body, display: 'flex', flexDirection: 'column' }}>
      {/* Blush header band */}
      <div style={{ background: headerBg, borderBottom: `2px solid ${accent}`, padding: `${ds.pad(22)}px ${ds.pad(44)}px ${ds.pad(18)}px` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '20pt', fontWeight: 800, color: '#1A1A1A', lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '9pt', color: '#888', marginTop: 3, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
          </div>
          <div style={{ textAlign: 'right', fontSize: '8.5pt', color: '#555', lineHeight: 1.8 }}>
            {[pi.phone, pi.location, pi.website].filter(Boolean).map((c, i) => <div key={i}>{c}</div>)}
          </div>
        </div>
      </div>
      {/* Body */}
      <div style={{ padding: `${ds.pad(24)}px ${ds.pad(44)}px`, flex: 1 }}>
        <div style={{ marginBottom: 20, lineHeight: 1.6 }}>
          {ji.hiringManager && <div style={{ fontWeight: 600 }}>{ji.hiringManager}</div>}
          {ji.company && <div style={{ color: '#555' }}>{ji.company}</div>}
        </div>
        <div style={{ marginBottom: 14 }}>{clGreeting(d)}</div>
        {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
        {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
        {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
        <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
      </div>
      {/* Pink bottom line */}
      <div style={{ height: 3, background: accent, margin: `0 ${ds.pad(44)}px 16px` }} />
    </div>
  );
};

// Template 8 — Centered (name + contact centered, subject line feature)
const CLCentered = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#1A1A1A';
  const body = pi.bodyColor || '#1A1A1A';
  const ds = useCLDesign(pi, { font: '"Palatino Linotype", Palatino, Georgia, serif' });
  const contacts = [pi.location, pi.email, pi.phone].filter(Boolean);
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.pad(32)}px ${ds.pad(44)}px`, height: '100%', boxSizing: 'border-box', color: body }}>
      {/* Centered header */}
      <div style={{ textAlign: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: '20pt', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase', color: accent, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '9.5pt', fontStyle: 'italic', color: '#555', marginTop: 4 }}>{pi.jobTitle}</div>}
      </div>
      {/* Centered short divider */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
        <div style={{ width: 60, height: 1.5, background: accent }} />
      </div>
      {/* Centered contact block */}
      <div style={{ textAlign: 'center', fontSize: '8.5pt', color: '#555', lineHeight: 1.9, marginBottom: 8 }}>
        {contacts.map((c, i) => <div key={i}>{c}</div>)}
      </div>
      <div style={{ borderBottom: '1px solid #CCC', marginBottom: 18 }} />
      {/* Date + Subject */}
      <div style={{ fontSize: '9pt', color: '#555', marginBottom: 12 }}>{clFormatDate(d.letterDate)}</div>
      {ji.role && <div style={{ fontWeight: 700, marginBottom: 14 }}>Subject: {ji.role}</div>}
      {/* Recipient */}
      {ji.hiringManager && (
        <div style={{ marginBottom: 14, lineHeight: 1.6 }}>
          {ji.hiringManager && <div style={{ fontWeight: 600 }}>{ji.hiringManager}</div>}
          {ji.company && <div style={{ color: '#555' }}>{ji.company}</div>}
        </div>
      )}
      <div style={{ marginBottom: 14 }}>{clGreeting(d)}</div>
      {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
      {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
      {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
      <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <div style={{ width: '50%', height: 1, background: '#CCC' }} />
      </div>
    </div>
  );
};

// Template 9 — Compact (top/bottom accent lines, stacked header left, bold recipient block)
const CLCompact = ({ d }) => {
  const pi = d.personalInfo || {};
  const ji = d.jobInfo || {};
  const co = d.content || {};
  const accent = pi.accentColor || '#1D4ED8';
  const body = pi.bodyColor || '#1A1A1A';
  const ds = useCLDesign(pi, { font: '"Helvetica Neue", Arial, sans-serif' });
  return (
    <div style={{ fontFamily: ds.font, fontSize: ds.fontSize, lineHeight: ds.lineHeight, height: '100%', boxSizing: 'border-box', color: body, display: 'flex', flexDirection: 'column' }}>
      {/* Top accent line */}
      <div style={{ height: 3, background: accent }} />
      <div style={{ padding: `${ds.pad(28)}px ${ds.pad(44)}px`, flex: 1 }}>
        {/* Name + contact header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '18pt', fontWeight: 900, color: '#111', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
          <div style={{ fontSize: '8.5pt', color: '#555', marginTop: 5, lineHeight: 1.8 }}>
            {[pi.phone, pi.email, pi.location].filter(Boolean).map((c, i) => <div key={i}>{c}</div>)}
          </div>
        </div>
        <div style={{ borderBottom: `2px solid ${body}`, marginBottom: 18 }} />
        {/* Recipient block */}
        <div style={{ marginBottom: 18, lineHeight: 1.6 }}>
          {ji.hiringManager && <div style={{ fontWeight: 700 }}>{ji.hiringManager}</div>}
          {ji.role && <div style={{ color: '#555' }}>Hiring Representative, {ji.company}</div>}
          {[pi.phone, pi.email, pi.location].filter(Boolean).map((c, i) => <div key={i} style={{ color: '#555' }}>{c}</div>)}
        </div>
        <div style={{ marginBottom: 14 }}>{clGreeting(d)}</div>
        {co.opening && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.opening}</p>}
        {co.body && co.body.split('\n').filter(Boolean).map((p, i) => <p key={i} style={{ textAlign: 'justify', margin: '0 0 10px' }}>{p}</p>)}
        {co.closing && <p style={{ textAlign: 'justify', margin: '0 0 10px' }}>{co.closing}</p>}
        <CLSignatureBlock d={d} accentColor={accent} bodyColor={body} />
      </div>
      {/* Bottom accent line */}
      <div style={{ height: 3, background: accent }} />
    </div>
  );
};

const CL_MAP = {
  cl_classic:      CLClassic,
  cl_modern:       CLModern,
  cl_professional: CLProfessional,
  cl_bold:         CLBold,
  cl_minimal:      CLMinimal,
  cl_executive:    CLExecutive,
  cl_elegant:      CLElegant,
  cl_centered:     CLCentered,
  cl_compact:      CLCompact,
};

const CoverLetterPreview = ({ d }) => {
  const Tmpl = CL_MAP[d.template] || CLClassic;
  return <Tmpl d={d} />;
};

// ── EUROPASS ──────────────────────────────────────────────────────────────────
const EuropassTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#333333', body: '#1F2937' });
  const accent = c.accent;
  const section = c.section;
  const linkColor = c.link;
  const body = c.body;
  const ds = useDesign(pi, { fontFamily: '"Calibri", "Arial", sans-serif', fontSize: 9.5, lineHeight: 1.6, padding: 30 });
  const pad = ds.padding;
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const sym = pi.bulletSymbol || '•';

  const secHead = (title) => (
    <div style={{ marginTop: 16, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: section, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '9.5pt', color: body, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
        <div style={{ height: 2, background: section, marginTop: 2 }} />
      </div>
    </div>
  );

  // Build inline contact tokens separated by |
  const infoTokens = [];
  if (pi.showDOB && pi.dateOfBirth)       infoTokens.push(<span key="dob"><strong>Date of birth:</strong> {pi.dateOfBirth}</span>);
  if (pi.showNationality && pi.nationality) infoTokens.push(<span key="nat"><strong>Nationality:</strong> {pi.nationality}</span>);
  if (pi.showSex && pi.sex)               infoTokens.push(<span key="sex"><strong>Sex:</strong> {pi.sex}</span>);
  if (pi.phone)                           infoTokens.push(<span key="ph"><strong>Phone:</strong> {pi.phone}</span>);
  if (pi.email)                           infoTokens.push(<span key="em"><strong>Email:</strong> <a href={`mailto:${pi.email}`} style={{ color: linkColor }}>{pi.email}</a></span>);
  if (pi.linkedin)                        infoTokens.push(<span key="li"><strong>LinkedIn:</strong> <a href={pi.linkedin} style={{ color: linkColor }}>{pi.linkedin}</a></span>);

  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: pad, minHeight: minH, background: 'white', position: 'relative', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Right-side accent strip */}
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 7, background: section, borderRadius: '0 3px 3px 0' }} />
      {/* HEADER */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, background: section + '15', marginTop: -pad, marginLeft: -pad, marginRight: -pad, paddingTop: pad + 14, paddingBottom: 14, paddingLeft: pad + 16, paddingRight: pad + 16 }}>
        <Photo pi={pi} border={`3px solid ${section}`} fallbackSize={88} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '18pt', fontWeight: 700, color: body, marginBottom: 4 }}>{pi.fullName}</div>
          <div style={{ height: 2, background: section, marginBottom: 8, borderRadius: 1 }} />
          {infoTokens.length > 0 && (
            <div style={{ fontSize: '8.5pt', lineHeight: 1.7 }}>
              {infoTokens.map((tok, i) => (
                <React.Fragment key={i}>
                  {tok}
                  {i < infoTokens.length - 1 && <span style={{ color: '#9CA3AF', margin: '0 6px' }}>|</span>}
                </React.Fragment>
              ))}
            </div>
          )}
          {pi.location && (
            <div style={{ fontSize: '8.5pt', marginTop: 4 }}><strong>Address:</strong> {pi.location}</div>
          )}
        </div>
      </div>

      {/* SUMMARY */}
      {pi.summary && (
        <div data-resume-section style={{ marginBottom: 4, ...noBreak }}>
          {secHead(pi.summaryTitle || 'About Myself')}
          <div style={{ paddingLeft: 15 }}>
            <p style={{ margin: 0, textAlign: 'justify', fontSize: '9.5pt', lineHeight: 1.65 }}>{pi.summary}</p>
          </div>
        </div>
      )}

      {/* ORDERED SECTIONS */}
      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'experience', 'Work Experience'))}
              <div style={{ paddingLeft: 15 }}>
                {d.experience.map((e, i) => (
                  <div key={e.id} data-resume-entry style={{ marginBottom: i < d.experience.length - 1 ? 14 : 0, ...noBreak }}>
                    <div style={{ fontWeight: 700 }}>{e.title}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
                      <div style={{ fontWeight: 700, fontStyle: 'italic', fontSize: '9pt', color: '#374151' }}>{e.company}</div>
                      <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>[ {fmtDate(e.startDate)} – {e.current ? 'Current' : fmtDate(e.endDate)} ]</div>
                    </div>
                    {e.location && <div style={{ fontSize: '8.5pt', color: '#4B5563', marginBottom: 3 }}><strong>City:</strong> {e.location}</div>}
                    {e.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#374151' }}>{e.description}</p>}
                    <BulletList bullets={e.bullets} symbol={sym} />
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          case 'education': return d.education?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'education', 'Education and Training'))}
              <div style={{ paddingLeft: 15 }}>
                {d.education.map((edu, i) => (
                  <div key={edu.id} data-resume-entry style={{ marginBottom: i < d.education.length - 1 ? 14 : 0, ...noBreak }}>
                    <div style={{ fontWeight: 700 }}>{edu.degree}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
                      <div style={{ fontWeight: 700, fontStyle: 'italic', fontSize: '9pt', color: '#374151' }}>{edu.school}</div>
                      <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>[ {edu.startDate ? fmtDate(edu.startDate) + ' – ' : ''}{fmtDate(edu.endDate)} ]</div>
                    </div>
                    {edu.location && <div style={{ fontSize: '8.5pt', color: '#4B5563' }}><strong>Address:</strong> {edu.location}</div>}
                    {edu.gpa && <div style={{ fontSize: '8.5pt', color: '#4B5563' }}><strong>GPA:</strong> {edu.gpa}</div>}
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          case 'languages': return d.languages?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'languages', 'Language Skills'))}
              <div style={{ paddingLeft: 15 }}>
                {d.languages.map((l, i) => (
                  <div key={l.id} data-resume-entry style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: i < d.languages.length - 1 ? 4 : 0, ...noBreak }}>
                    <span style={{ fontWeight: 700, fontSize: '9.5pt' }}>{l.language}</span>
                    {l.proficiency && <span style={{ fontSize: '9pt', color: '#4B5563' }}>— {l.proficiency}</span>}
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          case 'skills': return d.skills?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'skills', 'Digital Skills'))}
              <div style={{ paddingLeft: 15 }}>
                <p style={{ margin: 0, fontSize: '9.5pt', lineHeight: 1.65 }}>
                  {d.skills.flatMap(s => s.items.split(',').map(t => t.trim()).filter(Boolean)).join(' / ')}
                </p>
              </div>
            </div>
          ) : null;

          case 'projects': return d.projects?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'projects', 'Projects'))}
              <div style={{ paddingLeft: 15 }}>
                {d.projects.map((p, i) => (
                  <div key={p.id} data-resume-entry style={{ marginBottom: i < d.projects.length - 1 ? 16 : 0, ...noBreak }}>
                    {p.date && <div style={{ fontSize: '8.5pt', color: '#6B7280', marginBottom: 2 }}>[ {fmtDate(p.date)} ]</div>}
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.title}</div>
                    {p.skills && <div style={{ fontSize: '8.5pt', color: '#6B7280', marginBottom: 2 }}>{p.skills}</div>}
                    {p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}
                    <BulletList bullets={p.bullets} symbol={sym} />
                    {p.link && <div style={{ marginTop: 4, fontSize: '9pt' }}><strong>Link:</strong> <a href={p.link} style={{ color: linkColor }}>{p.link}</a></div>}
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          case 'achievements': return d.achievements?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'achievements', 'Achievements'))}
              <div style={{ paddingLeft: 15 }}>
                {d.achievements.map((a, i) => (
                  <div key={a.id} data-resume-entry style={{ marginBottom: i < d.achievements.length - 1 ? 8 : 0, ...noBreak }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <div style={{ fontWeight: 700 }}>{a.title}</div>
                      {a.date && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>[ {fmtDate(a.date)} ]</div>}
                    </div>
                    {a.description && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{a.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          case 'certifications': return d.certifications?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'certifications', 'Certifications'))}
              <div style={{ paddingLeft: 15 }}>
                {d.certifications.map((cert, i) => (
                  <div key={cert.id} data-resume-entry style={{ marginBottom: i < d.certifications.length - 1 ? 8 : 0, ...noBreak }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <div style={{ fontWeight: 700 }}>{cert.name}</div>
                      {cert.date && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>[ {fmtDate(cert.date)} ]</div>}
                    </div>
                    {cert.issuer && <div style={{ fontSize: '8.5pt', color: '#4B5563', fontStyle: 'italic' }}>{cert.issuer}</div>}
                  </div>
                ))}
              </div>
            </div>
          ) : null;

          case 'awards': return d.awards?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'awards', 'Awards'))}
              <div style={{ paddingLeft: 15 }}><AwardsItems awards={d.awards} color={linkColor} /></div>
            </div>
          ) : null;

          case 'volunteering': return d.volunteering?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'volunteering', 'Volunteering'))}
              <div style={{ paddingLeft: 15 }}><VolunteeringItems volunteering={d.volunteering} color={linkColor} bulletSymbol={sym} /></div>
            </div>
          ) : null;

          case 'publications': return d.publications?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'publications', 'Publications'))}
              <div style={{ paddingLeft: 15 }}><PublicationsItems publications={d.publications} /></div>
            </div>
          ) : null;

          case 'strengths': return d.strengths ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'strengths', 'Key Strengths'))}
              <div style={{ paddingLeft: 15 }}><p style={{ margin: 0, fontSize: '9.5pt' }}>{d.strengths}</p></div>
            </div>
          ) : null;

          case 'goals': return d.goals ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'goals', 'Career Goals'))}
              <div style={{ paddingLeft: 15 }}><p style={{ margin: 0, fontSize: '9.5pt' }}>{d.goals}</p></div>
            </div>
          ) : null;

          case 'hobbies': return d.hobbies ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'hobbies', 'Hobbies & Interests'))}
              <div style={{ paddingLeft: 15 }}><p style={{ margin: 0, fontSize: '9.5pt' }}>{d.hobbies}</p></div>
            </div>
          ) : null;

          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {secHead(secT(d, 'references', 'References'))}
              <div style={{ paddingLeft: 15 }}><ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={linkColor} /></div>
            </div>
          ) : null;

          case 'customBlocks': return (
            <React.Fragment key={id}>
              {(d.customBlocks || []).map(blk => (
                <div key={blk.id} data-resume-section style={{ ...noBreak }}>
                  {secHead(blk.title)}
                  <div style={{ paddingLeft: 15 }}>
                    {blk.description && <p style={{ margin: '0 0 4px', fontSize: '9.5pt' }}>{blk.description}</p>}
                    {blk.showDate && blk.date && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(blk.date)}</div>}
                    {blk.showLink && blk.link && <a href={blk.link} style={{ fontSize: '8.5pt', color: linkColor }}>{blk.link}</a>}
                    {blk.showBullets && <BulletList bullets={blk.bullets} symbol={sym} />}
                  </div>
                </div>
              ))}
            </React.Fragment>
          );

          default: return null;
        }
      })}
    </div>
  );
};

// ── ACADEMIC MODERN (sidebar) ─────────────────────────────────────────────────
const AcademicModernTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#2D3547', body: '#1F2937' });
  const sidebarColor = c.section;
  const sideBg = pi.sidebarColor || c.accent;
  const ds = useDesign(pi, { fontFamily: '"Segoe UI", Arial, sans-serif', fontSize: 9.5, lineHeight: 1.55 });

  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const pageFillH = React.useContext(PageFillContext);
  const minH = pageFillH || defaultMinH;

  const allSkills = (d.skills || []).flatMap(s =>
    (s.items || '').split(',').map(t => t.trim()).filter(Boolean)
  );

  const getIcon = (sectionId, DefaultIcon) => {
    const iconId = (d.sectionIcons || {})[sectionId];
    return (iconId && PREVIEW_ICON_MAP[iconId]) || DefaultIcon;
  };

  // Education always before experience in this template
  const academicSectionOrder = (() => {
    const order = [...getSectionOrder(d)];
    const eduIdx = order.indexOf('education');
    const expIdx = order.indexOf('experience');
    if (eduIdx !== -1 && expIdx !== -1 && eduIdx > expIdx) {
      order.splice(eduIdx, 1);
      order.splice(expIdx, 0, 'education');
    }
    return order;
  })();

  return (
    <div style={{ fontFamily: ds.fontFamily, fontSize: ds.fontSize, lineHeight: ds.lineHeight, color: c.body, display: 'flex', minHeight: minH, background: 'white', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {/* ── LEFT SIDEBAR ── */}
      <div style={{ width: 230, flexShrink: 0, background: sideBg, minHeight: minH, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: `${ds.padding}px ${Math.round(ds.padding * 0.65)}px`, overflowX: 'hidden', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
        <AcademicModernPhotoSlot pi={pi} sidebarColor={sideBg} />

        {/* ABOUT ME / SUMMARY */}
        {pi.summary && (
          <div data-resume-section style={{ width: '100%', marginTop: 24, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {React.createElement(getIcon('summary', UserCircle2), { size: 20, color: 'white', strokeWidth: 1.8, style: { flexShrink: 0 } })}
              <span style={{ color: 'white', fontWeight: 700, fontSize: scaledPt(11, pi), letterSpacing: 0.3, textTransform: sectionTransform(pi) }}>{pi.summaryTitle || 'About Me'}</span>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: scaledPt(9, pi), lineHeight: 1.7, margin: 0, wordSpacing: 'normal', textAlign: 'left' }}>{pi.summary}</p>
          </div>
        )}

        {/* CONTACT */}
        {(pi.phone || pi.email || pi.location || pi.website || pi.linkedin) && (
          <div data-resume-section style={{ width: '100%', marginTop: 20, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {React.createElement(getIcon('contact', UserCheck), { size: 20, color: 'white', strokeWidth: 1.8, style: { flexShrink: 0 } })}
              <span style={{ color: 'white', fontWeight: 700, fontSize: scaledPt(11, pi), letterSpacing: 0.3, textTransform: sectionTransform(pi) }}>Contact</span>
            </div>
            {[
              { Icon: Phone,  val: pi.phone },
              { Icon: Mail,   val: pi.email },
              { Icon: MapPin, val: pi.location },
              { Icon: Globe,  val: pi.website },
              { Icon: Link2,  val: pi.linkedin },
            ].filter(x => x.val).map(({ Icon, val }, i) => (
              <div key={i} data-resume-entry style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 9 }}>
                <Icon size={13} color="rgba(255,255,255,0.7)" strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: scaledPt(8.5, pi), lineHeight: 1.5, wordBreak: 'break-all' }}>{val}</span>
              </div>
            ))}
          </div>
        )}

        {/* SKILLS */}
        {allSkills.length > 0 && (
          <div data-resume-section style={{ width: '100%', marginTop: 20, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {React.createElement(getIcon('skills', Settings), { size: 16, color: 'white', strokeWidth: 1.8, style: { flexShrink: 0 } })}
              <span style={{ color: 'white', fontWeight: 700, fontSize: scaledPt(11, pi), letterSpacing: 0.3, textTransform: sectionTransform(pi) }}>{st(d, 'skills', 'Skills')}</span>
            </div>
            {allSkills.map((skill, i) => (
              <div key={i} data-resume-entry style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: scaledPt(9, pi), flexShrink: 0, marginTop: 1 }}>•</span>
                <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: scaledPt(9, pi), lineHeight: 1.4 }}>{skill}</span>
              </div>
            ))}
          </div>
        )}

        {/* LANGUAGES */}
        {d.languages?.length > 0 && (
          <div data-resume-section style={{ width: '100%', marginTop: 20, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {React.createElement(getIcon('languages', Languages), { size: 16, color: 'white', strokeWidth: 1.8, style: { flexShrink: 0 } })}
              <span style={{ color: 'white', fontWeight: 700, fontSize: scaledPt(11, pi), letterSpacing: 0.3, textTransform: sectionTransform(pi) }}>{st(d, 'languages', 'Language')}</span>
            </div>
            {d.languages.map((l) => (
              <div key={l.id} data-resume-entry style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: scaledPt(9, pi), flexShrink: 0, marginTop: 1 }}>•</span>
                <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: scaledPt(9, pi), lineHeight: 1.4 }}>{l.language}{l.proficiency ? ` (${l.proficiency})` : ''}</span>
              </div>
            ))}
          </div>
        )}

        {/* KEY STRENGTHS */}
        {d.strengths ? (
          <div data-resume-section style={{ width: '100%', marginTop: 20, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {React.createElement(getIcon('strengths', Zap), { size: 16, color: 'white', strokeWidth: 1.8, style: { flexShrink: 0 } })}
              <span style={{ color: 'white', fontWeight: 700, fontSize: scaledPt(11, pi), letterSpacing: 0.3, textTransform: sectionTransform(pi) }}>{st(d, 'strengths', 'Key Strengths')}</span>
            </div>
            {String(d.strengths).split(',').map(t => t.trim()).filter(Boolean).map((s, i) => (
              <div key={i} data-resume-entry style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: scaledPt(9, pi), flexShrink: 0, marginTop: 1 }}>•</span>
                <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: scaledPt(9, pi), lineHeight: 1.4 }}>{s}</span>
              </div>
            ))}
          </div>
        ) : null}

        {/* HOBBIES */}
        {d.hobbies ? (
          <div data-resume-section style={{ width: '100%', marginTop: 20, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {React.createElement(getIcon('hobbies', Smile), { size: 16, color: 'white', strokeWidth: 1.8, style: { flexShrink: 0 } })}
              <span style={{ color: 'white', fontWeight: 700, fontSize: scaledPt(11, pi), letterSpacing: 0.3, textTransform: sectionTransform(pi) }}>{st(d, 'hobbies', 'Hobbies')}</span>
            </div>
            {String(d.hobbies).split(',').map(t => t.trim()).filter(Boolean).map((h, i) => (
              <div key={i} data-resume-entry style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: scaledPt(9, pi), flexShrink: 0, marginTop: 1 }}>•</span>
                <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: scaledPt(9, pi), lineHeight: 1.4 }}>{h}</span>
              </div>
            ))}
          </div>
        ) : null}

        {/* CAREER GOALS */}
        {d.goals ? (
          <div data-resume-section style={{ width: '100%', marginTop: 20, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {React.createElement(getIcon('goals', Target), { size: 16, color: 'white', strokeWidth: 1.8, style: { flexShrink: 0 } })}
              <span style={{ color: 'white', fontWeight: 700, fontSize: scaledPt(11, pi), letterSpacing: 0.3, textTransform: sectionTransform(pi) }}>{st(d, 'goals', 'Career Goals')}</span>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: scaledPt(9, pi), lineHeight: 1.6, margin: 0 }}>{d.goals}</p>
          </div>
        ) : null}
      </div>

      {/* ── RIGHT CONTENT ── */}
      <div style={{ flex: 1, background: 'white', minHeight: minH, padding: `0 ${ds.padding + 8}px`, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
        {/* Name + Job Title — min-height matches sidebar photo block so sections align with About Me */}
        {(() => {
          const photoPresent = !!pi.photo && pi.showPhoto !== false;
          const photoSize = photoPresent ? (pi.photoSize || 120) : 0;
          // sidebar paddingTop(ds.padding) + photo marginTop(32) + photo height(photoSize) + photo marginBottom(28)
          const nameBlockMinH = photoPresent ? ds.padding + 32 + photoSize + 28 : 0;
          return (
            <div style={{ paddingTop: 60, paddingBottom: 24, minHeight: nameBlockMinH }}>
              <div style={{ fontSize: scaledPt(42, pi), fontWeight: 800, color: sidebarColor, textTransform: 'uppercase', lineHeight: 1.15, letterSpacing: 0.5 }}>{sampleProfileName(pi)}</div>
              {pi.jobTitle && <div style={{ fontSize: scaledPt(13, pi), color: '#6B7280', marginTop: 8, fontWeight: 400 }}>{pi.jobTitle}</div>}
            </div>
          );
        })()}

        {/* Sections */}
        <div style={{ marginTop: 24 }}>
        {academicSectionOrder.map(id => {
          const secHead = (Icon, title) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <Icon size={22} color={sidebarColor} strokeWidth={2} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{title}</span>
            </div>
          );
          switch (id) {
            case 'education': return d.education?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                {/* Header + entries share the same 22px left column so icon and dots align */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('education', GraduationCap), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'education', 'Education')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.education.map((edu) => (
                    <div key={edu.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      {/* dot-line-dot centered in same 22px column */}
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>
                          ({edu.startDate ? `${fmtDate(edu.startDate, 'year')} -` : ''}{edu.endDate ? fmtDate(edu.endDate, 'year') : 'Present'})
                        </div>
                        <div style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: scaledPt(9, pi), color: '#1F2937' }}>{edu.school}{edu.location ? ` | ${edu.location}` : ''}</div>
                        <div style={{ fontSize: '9pt', color: '#4B5563' }}>{edu.degree}</div>
                        {edu.gpa && <div style={{ fontSize: '9pt', color: '#6B7280' }}>{edu.gpa}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'experience': return d.experience?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('experience', Briefcase), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'experience', 'Professional Experience')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.experience.map((e) => (
                    <div key={e.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>
                          ({e.startDate ? `${fmtDate(e.startDate, 'year')} -` : ''}{e.current ? 'Present' : (e.endDate ? fmtDate(e.endDate, 'year') : '')})
                        </div>
                        <div style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: scaledPt(9, pi), color: '#1F2937' }}>{e.title}</div>
                        <div style={{ fontSize: '9pt', color: '#4B5563' }}>{e.company}{e.location ? ` · ${e.location}` : ''}</div>
                        {e.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#4B5563' }}>{e.description}</p>}
                        <BulletList bullets={e.bullets} symbol={pi.bulletSymbol} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'projects': return d.projects?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('projects', Folder), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'projects', 'Projects')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.projects.map((p) => (
                    <div key={p.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{p.title}{p.date && <span style={{ fontWeight: 400, fontSize: '8.5pt', color: '#6B7280' }}> · {fmtDate(p.date)}</span>}</div>
                        {p.skills && <div style={{ fontSize: '8.5pt', color: '#6B7280', fontStyle: 'italic' }}>{p.skills}</div>}
                        {p.summary && <p style={{ margin: '2px 0', fontSize: '9pt', color: '#4B5563' }}>{p.summary}</p>}
                        <BulletList bullets={p.bullets} symbol={pi.bulletSymbol} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'achievements': return d.achievements?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('achievements', Trophy), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'achievements', 'Achievements')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.achievements.map(a => (
                    <div key={a.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{a.title}{a.date && <span style={{ fontWeight: 400, fontSize: '8.5pt', color: '#6B7280' }}> · {fmtDate(a.date)}</span>}</div>
                        {a.description && <p style={{ margin: '2px 0', fontSize: '9pt', color: '#4B5563' }}>{a.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'awards': return d.awards?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('awards', Award), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'awards', 'Awards')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.awards.map(a => (
                    <div key={a.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{a.title}{a.date && <span style={{ fontWeight: 400, fontSize: '8.5pt', color: '#6B7280' }}> · {fmtDate(a.date)}</span>}</div>
                        {a.issuer && <div style={{ fontSize: '9pt', color: c.link, fontStyle: 'italic' }}>{a.issuer}</div>}
                        {a.description && <p style={{ margin: '2px 0', fontSize: '9pt', color: '#4B5563' }}>{a.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'certifications': return d.certifications?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('certifications', BookOpen), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'certifications', 'Certifications')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.certifications.map(cert => (
                    <div key={cert.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{cert.name}</div>
                        {cert.issuer && <div style={{ fontSize: '9pt', color: '#4B5563' }}>{cert.issuer}{cert.date ? ` · ${fmtDate(cert.date)}` : ''}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'volunteering': return d.volunteering?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('volunteering', HeartHandshake), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'volunteering', 'Volunteering')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.volunteering.map(v => (
                    <div key={v.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{v.role}{(v.startDate || v.endDate) && <span style={{ fontWeight: 400, fontSize: '8.5pt', color: '#6B7280' }}> · {fmtDate(v.startDate)} – {v.current ? 'Present' : fmtDate(v.endDate)}</span>}</div>
                        <div style={{ fontSize: '9pt', color: c.link }}>{v.organization}{v.location ? ` · ${v.location}` : ''}</div>
                        {v.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt', color: '#4B5563' }}>{v.description}</p>}
                        <BulletList bullets={v.bullets} symbol={pi.bulletSymbol} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'publications': return d.publications?.length > 0 ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('publications', BookOpen), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'publications', 'Publications')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {d.publications.map(p => (
                    <div key={p.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                      <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1, fontSize: '9.5pt' }}>
                        {p.authors && <span style={{ color: '#4B5563' }}>{p.authors} </span>}
                        {p.year && <span style={{ color: '#6B7280' }}>({p.year}). </span>}
                        "{p.title}."
                        {p.venue && <> <em style={{ color: '#4B5563' }}>{p.venue}</em>.</>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

            case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? (
              <div key={id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                  <div style={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {React.createElement(getIcon('references', UserCheck), { size: 22, color: sidebarColor, strokeWidth: 2 })}
                  </div>
                  <span style={{ fontSize: scaledPt(12, pi), fontWeight: 700, color: sidebarColor, textTransform: sectionTransform(pi) }}>{st(d, 'references', 'References')}</span>
                </div>
                {d.referencesAvailableOnRequest
                  ? <p style={{ margin: 0, fontSize: '9.5pt', fontStyle: 'italic', color: '#6B7280', paddingLeft: 32 }}>References available upon request.</p>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {d.references.map(r => (
                        <div key={r.id} data-resume-entry style={{ display: 'flex', gap: 10, ...noBreak }}>
                          <div style={{ width: 22, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                            <div style={{ flex: 1, width: 2, background: sidebarColor }} />
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: sidebarColor, flexShrink: 0 }} />
                          </div>
                          <div style={{ flex: 1, fontSize: '9pt' }}>
                            <div style={{ fontWeight: 700 }}>{r.name}</div>
                            {r.relationship && <div style={{ fontSize: '8pt', color: c.link, fontStyle: 'italic' }}>{r.relationship}</div>}
                            {r.title && <div style={{ color: '#4B5563' }}>{r.title}{r.company ? ` · ${r.company}` : ''}</div>}
                            {r.email && <div style={{ color: '#6B7280' }}>{r.email}</div>}
                            {r.phone && <div style={{ color: '#6B7280' }}>{r.phone}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                }
              </div>
            ) : null;

            case 'customBlocks': return (
              <React.Fragment key={id}>
                {(d.customBlocks || []).map(blk => (
                  <div key={blk.id} data-resume-section style={{ marginBottom: 20, ...noBreak }}>
                    {secHead(Layers, blk.title)}
                    {blk.description && <p style={{ margin: '0 0 4px', fontSize: '9.5pt' }}>{blk.description}</p>}
                    {blk.showDate && blk.date && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>{fmtDate(blk.date)}</div>}
                    {blk.showLink && blk.link && <a href={blk.link} style={{ fontSize: '8.5pt', color: c.link }}>{blk.link}</a>}
                    {blk.showBullets && <BulletList bullets={blk.bullets} symbol={pi.bulletSymbol} />}
                  </div>
                ))}
              </React.Fragment>
            );

            // Sidebar items — skip in right column
            case 'skills':
            case 'languages':
            case 'strengths':
            case 'hobbies':
            case 'goals':
              return null;
            default: return null;
          }
        })}
        </div>
      </div>
    </div>
  );
};

// ── CRIMSON TWO-COLUMN ────────────────────────────────────────────────────────
const CrimsonTwoColTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#7B1C1C', body: '#1F2937' });
  const accent = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Calibri", "Segoe UI", Arial, sans-serif', fontSize: 9.5, lineHeight: 1.55 });

  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const pageFillH = React.useContext(PageFillContext);
  const minH = pageFillH || defaultMinH;

  const SecHead = ({ secId, title }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: scaledPt(9.5, pi), fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 0.8 }}>
        {secT(d, secId, title)}
      </div>
      <div style={{ height: 1.5, background: c.section, marginTop: 3 }} />
    </div>
  );

  const contactItems = [pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean);
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));

  // Right-column sections: education, skills, certifications, languages, strengths
  const rightIds = new Set(['education', 'skills', 'languages', 'certifications', 'strengths', 'goals']);

  const headerH = 90 + (contactItems.length > 0 ? 22 : 0);
  const bodyMinH = minH - headerH;

  return (
    <div style={{ fontFamily: ds.fontFamily, fontSize: ds.fontSize, lineHeight: ds.lineHeight, color: c.body, background: 'white', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word' }}>

      {/* ── HEADER ── */}
      <div style={{ background: accent, padding: `${ds.padding + 6}px ${ds.padding + 14}px ${ds.padding}px` }}>
        <div style={{ fontSize: scaledPt(28, pi), fontWeight: 800, color: 'white', letterSpacing: 0.2, lineHeight: 1.15 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(12, pi), color: 'rgba(255,255,255,0.82)', marginTop: 5, fontWeight: 400 }}>{pi.jobTitle}</div>}
        {contactItems.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 14px', marginTop: 10, fontSize: scaledPt(8, pi), color: 'rgba(255,255,255,0.72)' }}>
            {contactItems.map((item, i) => <span key={i}>{item}</span>)}
          </div>
        )}
      </div>

      {/* ── BODY ── */}
      <div style={{ display: 'flex', minHeight: bodyMinH }}>

        {/* Left main column */}
        <div style={{ flex: '0 0 62%', padding: `${ds.padding}px ${ds.padding}px`, background: 'white', boxSizing: 'border-box', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
          {/* Summary */}
          {pi.summary && (
            <div data-resume-section style={{ marginBottom: 18, ...noBreak }}>
              <SecHead secId="summary" title={pi.summaryTitle || 'Professional Summary'} />
              <p style={{ margin: '4px 0 0', fontSize: scaledPt(9, pi), color: '#374151', lineHeight: 1.65 }}>{pi.summary}</p>
            </div>
          )}

          {/* Left sections */}
          {getSectionOrder(d).filter(id => !rightIds.has(id)).map(id => {
            switch (id) {
              case 'experience': return d.experience?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="experience" title="Experience" />
                  {d.experience.map(e => (
                    <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{e.title}</span>
                        <span style={{ fontSize: scaledPt(8, pi), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>
                          {e.startDate ? fmtDate(e.startDate) : ''}{(e.startDate || e.endDate) ? ' – ' : ''}{e.current ? 'Present' : (e.endDate ? fmtDate(e.endDate) : '')}
                        </span>
                      </div>
                      <div style={{ fontSize: scaledPt(9, pi), color: c.link, fontWeight: 500 }}>{e.company}{e.location ? ` · ${e.location}` : ''}</div>
                      {e.description && <p style={{ margin: '3px 0 2px', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{e.description}</p>}
                      <BulletList bullets={e.bullets} symbol={pi.bulletSymbol} />
                    </div>
                  ))}
                </div>
              ) : null;

              case 'projects': return d.projects?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="projects" title="Projects" />
                  {d.projects.map(p => (
                    <div key={p.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{p.title}</span>
                        {p.date && <span style={{ fontSize: scaledPt(8, pi), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(p.date)}</span>}
                      </div>
                      {p.skills && <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280', fontStyle: 'italic' }}>Tech: {p.skills}</div>}
                      {p.summary && <p style={{ margin: '3px 0 2px', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{p.summary}</p>}
                      <BulletList bullets={p.bullets} symbol={pi.bulletSymbol} />
                      {p.link && <div style={{ fontSize: scaledPt(8, pi), color: c.link }}>{p.link}</div>}
                    </div>
                  ))}
                </div>
              ) : null;

              case 'achievements': return d.achievements?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="achievements" title="Achievements" />
                  {d.achievements.map(a => (
                    <div key={a.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{a.title}</span>
                        {a.date && <span style={{ fontSize: scaledPt(8, pi), color: '#6B7280', marginLeft: 8 }}>{fmtDate(a.date)}</span>}
                      </div>
                      {a.description && <p style={{ margin: '2px 0', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{a.description}</p>}
                    </div>
                  ))}
                </div>
              ) : null;

              case 'awards': return d.awards?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="awards" title="Awards" />
                  {d.awards.map(a => (
                    <div key={a.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{a.title}</span>
                        {a.date && <span style={{ fontSize: scaledPt(8, pi), color: '#6B7280', marginLeft: 8 }}>{fmtDate(a.date)}</span>}
                      </div>
                      {a.issuer && <div style={{ fontSize: scaledPt(9, pi), color: c.link }}>{a.issuer}</div>}
                      {a.description && <p style={{ margin: '2px 0', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{a.description}</p>}
                    </div>
                  ))}
                </div>
              ) : null;

              case 'volunteering': return d.volunteering?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="volunteering" title="Volunteering" />
                  {d.volunteering.map(v => (
                    <div key={v.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{v.role}</span>
                        <span style={{ fontSize: scaledPt(8, pi), color: '#6B7280', marginLeft: 8 }}>{v.startDate ? fmtDate(v.startDate) : ''}{(v.startDate || v.endDate) ? ' – ' : ''}{v.current ? 'Present' : (v.endDate ? fmtDate(v.endDate) : '')}</span>
                      </div>
                      <div style={{ fontSize: scaledPt(9, pi), color: c.link }}>{v.organization}{v.location ? ` · ${v.location}` : ''}</div>
                      {v.description && <p style={{ margin: '3px 0 2px', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{v.description}</p>}
                      <BulletList bullets={v.bullets} symbol={pi.bulletSymbol} />
                    </div>
                  ))}
                </div>
              ) : null;

              case 'publications': return d.publications?.length > 0 ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="publications" title="Publications" />
                  {d.publications.map(p => (
                    <div key={p.id} data-resume-entry style={{ marginBottom: 8, fontSize: scaledPt(9, pi), color: '#374151', ...noBreak }}>
                      {p.authors && <span style={{ color: '#4B5563' }}>{p.authors} </span>}
                      {p.year && <span style={{ color: '#6B7280' }}>({p.year}). </span>}
                      "{p.title}."{p.venue && <> <em style={{ color: '#4B5563' }}>{p.venue}</em>.</>}
                    </div>
                  ))}
                </div>
              ) : null;

              case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="references" title="References" />
                  {d.referencesAvailableOnRequest
                    ? <p style={{ margin: 0, fontSize: scaledPt(9, pi), fontStyle: 'italic', color: '#6B7280' }}>References available upon request.</p>
                    : (d.references || []).map(r => (
                        <div key={r.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}>
                          <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{r.name}</div>
                          {r.title && <div style={{ fontSize: scaledPt(9, pi), color: '#4B5563' }}>{r.title}{r.company ? ` · ${r.company}` : ''}</div>}
                          {r.email && <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280' }}>{r.email}</div>}
                        </div>
                      ))
                  }
                </div>
              ) : null;

              case 'hobbies': return d.hobbies ? (
                <div key={id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                  <SecHead secId="hobbies" title="Hobbies & Interests" />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {String(d.hobbies).split(',').map(t => t.trim()).filter(Boolean).map((h, i) => (
                      <span key={i} style={{ background: '#F3F4F6', borderRadius: 4, padding: '2px 8px', fontSize: scaledPt(8.5, pi), color: '#374151' }}>{h}</span>
                    ))}
                  </div>
                </div>
              ) : null;

              case 'customBlocks': return (
                <React.Fragment key={id}>
                  {(d.customBlocks || []).map(blk => (
                    <div key={blk.id} data-resume-section style={{ marginBottom: 18, ...noBreak }}>
                      <SecHead secId={blk.id} title={blk.title} />
                      {blk.description && <p style={{ margin: '0 0 4px', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{blk.description}</p>}
                      {blk.showBullets && <BulletList bullets={blk.bullets} symbol={pi.bulletSymbol} />}
                    </div>
                  ))}
                </React.Fragment>
              );

              default: return null;
            }
          })}
        </div>

        {/* Right sidebar */}
        <div style={{ flex: '0 0 38%', background: '#FFF8F5', padding: `${ds.padding}px ${ds.padding - 4}px`, boxSizing: 'border-box', borderLeft: `1px solid #F0E8E4`, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>

          {/* Education */}
          {d.education?.length > 0 && (
            <div data-resume-section style={{ marginBottom: 18, ...noBreak }}>
              <SecHead secId="education" title="Education" />
              {d.education.map(edu => (
                <div key={edu.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}>
                  <div style={{ fontWeight: 700, fontSize: scaledPt(9.5, pi), color: '#1F2937' }}>{edu.degree}</div>
                  <div style={{ fontSize: scaledPt(9, pi), color: c.link, fontWeight: 500 }}>{edu.school}{edu.location ? ` · ${edu.location}` : ''}</div>
                  <div style={{ fontSize: scaledPt(8, pi), color: '#6B7280', marginTop: 1 }}>
                    {edu.endDate ? fmtDate(edu.endDate, 'longMonth') : (edu.startDate ? fmtDate(edu.startDate, 'longMonth') : '')}
                    {edu.gpa ? ` · GPA: ${edu.gpa}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Skills */}
          {(d.skills || []).length > 0 && (
            <div data-resume-section style={{ marginBottom: 18, ...noBreak }}>
              <SecHead secId="skills" title="Skills" />
              {(d.skills || []).map(s => {
                const items = (s.items || '').split(',').map(t => t.trim()).filter(Boolean);
                if (!items.length) return null;
                return (
                  <div key={s.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}>
                    {s.category && <div style={{ fontWeight: 700, fontSize: scaledPt(9, pi), color: '#1F2937' }}>{s.category}</div>}
                    <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280' }}>{items.join(', ')}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Certifications */}
          {d.certifications?.length > 0 && (
            <div data-resume-section style={{ marginBottom: 18, ...noBreak }}>
              <SecHead secId="certifications" title="Certifications" />
              {d.certifications.map(cert => (
                <div key={cert.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}>
                  <div style={{ fontWeight: 700, fontSize: scaledPt(9, pi), color: '#1F2937' }}>{cert.name}</div>
                  {cert.issuer && <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280' }}>{cert.issuer}{cert.date ? ` · ${fmtDate(cert.date, 'year')}` : ''}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Languages */}
          {d.languages?.length > 0 && (
            <div data-resume-section style={{ marginBottom: 18, ...noBreak }}>
              <SecHead secId="languages" title="Languages" />
              {d.languages.map(l => (
                <div key={l.id} data-resume-entry style={{ display: 'flex', justifyContent: 'space-between', fontSize: scaledPt(9, pi), marginBottom: 5, ...noBreak }}>
                  <span style={{ fontWeight: 600, color: '#1F2937' }}>{l.language}</span>
                  {l.proficiency && <span style={{ color: '#6B7280', fontStyle: 'italic' }}>{l.proficiency}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Strengths */}
          {d.strengths ? (
            <div data-resume-section style={{ marginBottom: 18, ...noBreak }}>
              <SecHead secId="strengths" title="Key Strengths" />
              {String(d.strengths).split(',').map(t => t.trim()).filter(Boolean).map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'flex-start' }}>
                  <span style={{ color: accent, fontSize: scaledPt(9, pi), flexShrink: 0 }}>•</span>
                  <span style={{ fontSize: scaledPt(9, pi), color: '#374151' }}>{s}</span>
                </div>
              ))}
            </div>
          ) : null}

          {/* Career Goals */}
          {d.goals ? (
            <div data-resume-section style={{ marginBottom: 18, ...noBreak }}>
              <SecHead secId="goals" title="Career Goals" />
              <p style={{ margin: 0, fontSize: scaledPt(9, pi), color: '#4B5563', lineHeight: 1.6 }}>{d.goals}</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Harvard Standard: compact, single-column, ATS-friendly resume layout.
const HarvardStandardTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#111827', body: '#111827' });
  const accent = c.accent;
  const body = c.body;
  const ds = useDesign(pi, { fontFamily: '"Times New Roman", Georgia, serif', fontSize: 10.2, lineHeight: 1.38, padding: 34 });
  const sym = pi.bulletSymbol || '*';
  const contactItems = [pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean);

  const sectionTitle = (secId, title) => (
    <div style={{ margin: '13px 0 6px', paddingBottom: 5, borderBottom: `1px solid ${c.section}`, fontSize: scaledPt(10.2, pi), fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 0 }}>
      {secT(d, secId, title)}
    </div>
  );

  const dateRange = (start, end, current) => {
    const left = start ? fmtDate(start) : '';
    const right = current ? 'Present' : (end ? fmtDate(end) : '');
    return [left, right].filter(Boolean).join(' - ');
  };

  const HeaderLine = ({ left, right, sub, children }) => (
    <div data-resume-entry style={{ marginBottom: 5, ...noBreak }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ fontWeight: 700, color: body }}>{left}</div>
        {right && <div style={{ fontSize: scaledPt(9.2, pi), color: '#374151', whiteSpace: 'nowrap' }}>{right}</div>}
      </div>
      {sub && <div style={{ fontSize: scaledPt(9.5, pi), color: '#374151', fontStyle: 'italic' }}>{sub}</div>}
      {children}
    </div>
  );

  const PlainBullets = ({ bullets }) => bullets?.filter(Boolean).length > 0 ? (
    <ul style={{ margin: '3px 0 0 18px', padding: 0, listStyle: 'none' }}>
      {bullets.filter(Boolean).map((b, i) => (
        <li key={i} style={{ marginBottom: 1, display: 'flex', gap: 6, fontSize: scaledPt(9.6, pi) }}>
          <span style={{ flexShrink: 0 }}>{sym}</span><span>{b}</span>
        </li>
      ))}
    </ul>
  ) : null;

  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.padding}px ${ds.padding + 6}px`, background: 'white', minHeight: pi.pageSize === 'letter' ? 1056 : 1122, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: scaledPt(20, pi), fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0, color: accent }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), marginTop: 2, color: '#374151' }}>{pi.jobTitle}</div>}
        {contactItems.length > 0 && (
          <div style={{ fontSize: scaledPt(9.1, pi), color: '#374151', marginTop: 5 }}>
            {contactItems.join(' | ')}
          </div>
        )}
        {(pi.showDOB || pi.showNationality || pi.showSex) && (
          <div style={{ fontSize: scaledPt(8.8, pi), color: '#4B5563', marginTop: 3 }}>
            {[pi.showDOB && pi.dateOfBirth && `DOB: ${pi.dateOfBirth}`, pi.showNationality && pi.nationality && `Nationality: ${pi.nationality}`, pi.showSex && pi.sex && `Sex: ${pi.sex}`].filter(Boolean).join(' | ')}
          </div>
        )}
      </div>

      {pi.summary && (
        <div data-resume-section style={{ ...noBreak }}>
          {sectionTitle('summary', pi.summaryTitle || 'Professional Summary')}
          <p style={{ margin: 0, fontSize: scaledPt(9.7, pi), textAlign: 'justify' }}>{pi.summary}</p>
        </div>
      )}

      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {sectionTitle('experience', 'Experience')}
              {d.experience.map(e => (
                <HeaderLine key={e.id} left={e.title} right={dateRange(e.startDate, e.endDate, e.current)} sub={[e.company, e.location].filter(Boolean).join(', ')}>
                  {e.description && <p style={{ margin: '2px 0 0', fontSize: scaledPt(9.6, pi) }}>{e.description}</p>}
                  <PlainBullets bullets={e.bullets} />
                </HeaderLine>
              ))}
            </div>
          ) : null;

          case 'education': return d.education?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {sectionTitle('education', 'Education')}
              {d.education.map(edu => (
                <HeaderLine key={edu.id} left={edu.school} right={dateRange(edu.startDate, edu.endDate, false)} sub={[edu.degree, edu.location].filter(Boolean).join(', ')}>
                  {edu.gpa && <div style={{ fontSize: scaledPt(9.3, pi) }}>GPA: {edu.gpa}</div>}
                </HeaderLine>
              ))}
            </div>
          ) : null;

          case 'skills': return d.skills?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {sectionTitle('skills', 'Skills')}
              {d.skills.map(s => (
                <div key={s.id} data-resume-entry style={{ marginBottom: 3, fontSize: scaledPt(9.6, pi), ...noBreak }}>
                  {s.category && <strong>{s.category}: </strong>}{s.items}
                </div>
              ))}
            </div>
          ) : null;

          case 'projects': return d.projects?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {sectionTitle('projects', 'Projects')}
              {d.projects.map(p => (
                <HeaderLine key={p.id} left={p.title} right={p.date ? fmtDate(p.date) : ''} sub={p.skills}>
                  {p.summary && <p style={{ margin: '2px 0 0', fontSize: scaledPt(9.6, pi) }}>{p.summary}</p>}
                  <PlainBullets bullets={p.bullets} />
                  {p.link && <div style={{ fontSize: scaledPt(9, pi), color: '#374151' }}>{p.link}</div>}
                </HeaderLine>
              ))}
            </div>
          ) : null;

          case 'certifications': return d.certifications?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {sectionTitle('certifications', 'Certifications')}
              {d.certifications.map(cert => (
                <HeaderLine key={cert.id} left={cert.name} right={cert.date ? fmtDate(cert.date) : ''} sub={cert.issuer} />
              ))}
            </div>
          ) : null;

          case 'achievements': return d.achievements?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {sectionTitle('achievements', 'Achievements')}
              {d.achievements.map(a => (
                <HeaderLine key={a.id} left={a.title} right={a.date ? fmtDate(a.date) : ''}>
                  {a.description && <p style={{ margin: '2px 0 0', fontSize: scaledPt(9.6, pi) }}>{a.description}</p>}
                </HeaderLine>
              ))}
            </div>
          ) : null;

          case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sectionTitle('awards', 'Awards')}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
          case 'languages': return d.languages?.length > 0 ? (
            <div key={id} data-resume-section style={{ ...noBreak }}>
              {sectionTitle('languages', 'Languages')}
              <div style={{ fontSize: scaledPt(9.6, pi) }}>{d.languages.map(l => [l.language, l.proficiency].filter(Boolean).join(' - ')).join(' | ')}</div>
            </div>
          ) : null;
          case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sectionTitle('volunteering', 'Volunteering')}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
          case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sectionTitle('publications', 'Publications')}<PublicationsItems publications={d.publications} /></div> : null;
          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sectionTitle('references', 'References')}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
          case 'strengths': return d.strengths ? <div key={id} data-resume-section style={{ ...noBreak }}>{sectionTitle('strengths', 'Key Strengths')}<div style={{ fontSize: scaledPt(9.6, pi) }}>{d.strengths}</div></div> : null;
          case 'goals': return d.goals ? <div key={id} data-resume-section style={{ ...noBreak }}>{sectionTitle('goals', 'Career Goals')}<p style={{ margin: 0, fontSize: scaledPt(9.6, pi) }}>{d.goals}</p></div> : null;
          case 'hobbies': return d.hobbies ? <div key={id} data-resume-section style={{ ...noBreak }}>{sectionTitle('hobbies', 'Hobbies & Interests')}<div style={{ fontSize: scaledPt(9.6, pi) }}>{d.hobbies}</div></div> : null;
          case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
          default: return null;
        }
      })}
    </div>
  );
};

// Blue Analyst: image-inspired compact professional resume with blue rules.
const BlueAnalystTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1D6FD6', body: '#243244' });
  const blue = c.accent;
  const body = c.body;
  const ds = useDesign(pi, { fontFamily: '"Arial", "Helvetica Neue", sans-serif', fontSize: 9.2, lineHeight: 1.28, padding: 50 });
  const pageFillH = React.useContext(PageFillContext);
  const minH = pageFillH || (pi.pageSize === 'letter' ? 1056 : 1122);
  const contactLine = [pi.location, pi.phone, pi.email, pi.website || pi.linkedin].filter(Boolean).join(' | ');

  const Section = ({ id, title, children }) => children ? (
    <div data-resume-section style={{ marginTop: 14, ...noBreak }}>
      <div style={{ borderTop: `2px solid ${c.section}`, borderBottom: `2px solid ${c.section}`, paddingTop: 5, paddingBottom: 5, marginBottom: 6 }}>
        <div style={{ color: c.section, fontWeight: 800, fontSize: scaledPt(11.3, pi), textTransform: sectionTransform(pi), letterSpacing: 0 }}>
          {secT(d, id, title)}
        </div>
      </div>
      {children}
    </div>
  ) : null;

  const dateRange = (start, end, current) => {
    const left = start ? fmtDate(start) : '';
    const right = current ? 'Present' : (end ? fmtDate(end) : '');
    return [left, right].filter(Boolean).join(' - ');
  };

  const Role = ({ title, company, location, dates, children }) => (
    <div data-resume-entry style={{ marginBottom: 9, ...noBreak }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ fontWeight: 800, color: '#111827', fontSize: scaledPt(9.5, pi) }}>{title}</div>
        {dates && <div style={{ fontWeight: 800, color: '#111827', fontSize: scaledPt(8.6, pi), whiteSpace: 'nowrap' }}>{dates}</div>}
      </div>
      {(company || location) && <div style={{ color: '#374151', fontSize: scaledPt(8.8, pi), fontWeight: 600 }}>{[company, location].filter(Boolean).join(', ')}</div>}
      {children}
    </div>
  );

  const CompactBullets = ({ bullets }) => bullets?.filter(Boolean).length > 0 ? (
    <ul style={{ margin: '3px 0 0 14px', padding: 0, listStyle: 'none' }}>
      {bullets.filter(Boolean).map((b, i) => (
        <li key={i} style={{ marginBottom: 1, display: 'flex', gap: 5, color: body, fontSize: scaledPt(8.55, pi), lineHeight: 1.24 }}>
          <span style={{ flexShrink: 0 }}>{pi.bulletSymbol || '•'}</span><span>{b}</span>
        </li>
      ))}
    </ul>
  ) : null;

  const allSkillItems = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  const additionalRows = [
    d.languages?.length > 0 && ['Languages', d.languages.map(l => [l.language, l.proficiency].filter(Boolean).join(' - ')).join(', ')],
    d.certifications?.length > 0 && ['Certificates', d.certifications.map(c => [c.name, c.issuer].filter(Boolean).join(', ')).join('; ')],
    d.awards?.length > 0 && ['Awards/Activities', d.awards.map(a => a.title).join(', ')],
    d.achievements?.length > 0 && ['Achievements', d.achievements.map(a => a.title).join(', ')],
    d.hobbies && ['Interests', d.hobbies],
  ].filter(Boolean);

  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.padding}px ${ds.padding + 6}px`, background: 'white', minHeight: minH, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, marginBottom: 12 }}>
        <div style={{ flex: 1, paddingTop: 6, minWidth: 0 }}>
          <div style={{ color: blue, fontSize: scaledPt(26, pi), lineHeight: 1, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ color: '#172033', fontSize: scaledPt(15, pi), fontWeight: 800, textTransform: 'uppercase', marginTop: 9, letterSpacing: 0 }}>{pi.jobTitle}</div>}
          {contactLine && <div style={{ color: '#374151', fontSize: scaledPt(8.5, pi), marginTop: 6 }}>{contactLine}</div>}
        </div>
        {pi.photo && pi.showPhoto !== false && (
          <img src={pi.photo} alt="" style={{ width: pi.photoSize || 94, height: pi.photoSize || 94, objectFit: 'cover', objectPosition: '50% 18%', borderRadius: 0, flexShrink: 0 }} />
        )}
      </div>

      {pi.summary && (
        <Section id="summary" title={pi.summaryTitle || 'Summary'}>
          <p style={{ margin: 0, fontSize: scaledPt(8.8, pi), color: body }}>{pi.summary}</p>
        </Section>
      )}

      {d.experience?.length > 0 && (
        <Section id="experience" title="Professional Experience">
          {d.experience.map(e => (
            <Role key={e.id} title={e.title} company={e.company} location={e.location} dates={dateRange(e.startDate, e.endDate, e.current)}>
              {e.description && <p style={{ margin: '3px 0 0', fontSize: scaledPt(8.55, pi) }}>{e.description}</p>}
              <CompactBullets bullets={e.bullets} />
            </Role>
          ))}
        </Section>
      )}

      {d.education?.length > 0 && (
        <Section id="education" title="Education">
          {d.education.map(edu => (
            <Role key={edu.id} title={edu.degree} company={edu.school} location={edu.location} dates={dateRange(edu.startDate, edu.endDate, false)}>
              {edu.gpa && <ul style={{ margin: '3px 0 0 14px', padding: 0 }}><li style={{ fontSize: scaledPt(8.55, pi) }}>GPA: {edu.gpa}</li></ul>}
            </Role>
          ))}
        </Section>
      )}

      {allSkillItems.length > 0 && (
        <Section id="skills" title="Technical Skills">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px 18px', fontSize: scaledPt(8.25, pi), color: '#374151' }}>
            {allSkillItems.map((skill, i) => <div key={i}>{skill}</div>)}
          </div>
        </Section>
      )}

      {(d.projects?.length > 0 || d.volunteering?.length > 0 || d.publications?.length > 0 || d.references?.length > 0 || d.referencesAvailableOnRequest) && (
        <Section id="projects" title="Additional Experience">
          {d.projects?.map(p => (
            <Role key={p.id} title={p.title} dates={p.date ? fmtDate(p.date) : ''} company={p.skills}>
              {p.summary && <p style={{ margin: '3px 0 0', fontSize: scaledPt(8.55, pi) }}>{p.summary}</p>}
              <CompactBullets bullets={p.bullets} />
            </Role>
          ))}
          {d.volunteering?.map(v => (
            <Role key={v.id} title={v.role} company={v.organization} location={v.location} dates={dateRange(v.startDate, v.endDate, v.current)}>
              {v.description && <p style={{ margin: '3px 0 0', fontSize: scaledPt(8.55, pi) }}>{v.description}</p>}
              <CompactBullets bullets={v.bullets} />
            </Role>
          ))}
          {d.publications?.length > 0 && <PublicationsItems publications={d.publications} />}
          {(d.references?.length > 0 || d.referencesAvailableOnRequest) && <ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} />}
        </Section>
      )}

      {additionalRows.length > 0 && (
        <Section id="additional" title="Additional Information">
          {additionalRows.map(([label, value]) => (
            <div key={label} style={{ fontSize: scaledPt(8.55, pi), marginBottom: 3 }}>
              <strong>{label}:</strong> {value}
            </div>
          ))}
        </Section>
      )}

      {(d.strengths || d.goals || d.customBlocks?.length > 0) && (
        <Section id="customBlocks" title="More">
          {d.strengths && <div style={{ fontSize: scaledPt(8.55, pi), marginBottom: 3 }}><strong>{st(d, 'strengths', 'Key Strengths')}:</strong> {d.strengths}</div>}
          {d.goals && <div style={{ fontSize: scaledPt(8.55, pi), marginBottom: 3 }}><strong>{st(d, 'goals', 'Career Goals')}:</strong> {d.goals}</div>}
          <CustomBlocks blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />
        </Section>
      )}
    </div>
  );
};

// Russian Classic: refined serif resume with a left label/date rail.
const RussianClassicTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#9CA3AF', section: '#242424', body: '#242424' });
  const ink = c.body;
  const muted = '#6B7280';
  const rule = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Times New Roman", Georgia, serif', fontSize: 8.7, lineHeight: 1.33, padding: 54 });
  const pageFillH = React.useContext(PageFillContext);
  const minH = pageFillH || (pi.pageSize === 'letter' ? 1056 : 1122);

  const dateRange = (start, end, current) => {
    const left = start ? fmtDate(start) : '';
    const right = current ? 'Present' : (end ? fmtDate(end) : '');
    return [left, right].filter(Boolean).join(' - ');
  };

  const SectionLabel = ({ id, title }) => (
    <div style={{ fontSize: scaledPt(8.2, pi), fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 0.8 }}>
      {title ? secT(d, id, title) : ''}
    </div>
  );

  const Section = ({ id, title, children }) => children ? (
    <div data-resume-section style={{ display: 'grid', gridTemplateColumns: '116px 1fr', columnGap: 24, borderTop: `1px solid ${rule}`, paddingTop: 11, marginTop: 16, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
      <SectionLabel id={id} title={title} />
      <div>{children}</div>
    </div>
  ) : null;

  const TimelineSection = ({ id, title, children }) => children ? (
    <div data-resume-section style={{ borderTop: `1px solid ${rule}`, paddingTop: 11, marginTop: 16, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
      {children(SectionLabel)}
    </div>
  ) : null;

  const TimelineItem = ({ date, title, org, location, children, label }) => (
    <div data-resume-entry style={{ display: 'grid', gridTemplateColumns: '116px 1fr', columnGap: 24, marginBottom: 12, ...noBreak }}>
      <div>
        {label}
        {date && <div style={{ color: '#4B5563', fontSize: scaledPt(7.6, pi), paddingTop: label ? 7 : 2 }}>{date}</div>}
      </div>
      <div style={{ minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'baseline' }}>
          <div style={{ fontSize: scaledPt(10, pi), color: ink, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{title}</div>
          {location && <div style={{ color: muted, fontSize: scaledPt(7.4, pi), maxWidth: 180, overflowWrap: 'anywhere', wordBreak: 'break-word', textAlign: 'right' }}>{location}</div>}
        </div>
        {org && <div style={{ color: '#374151', fontSize: scaledPt(7.8, pi), marginTop: 1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{org}</div>}
        {children}
      </div>
    </div>
  );

  const SmallBullets = ({ bullets }) => bullets?.filter(Boolean).length > 0 ? (
    <ul style={{ margin: '5px 0 0 14px', padding: 0, listStyle: 'none' }}>
      {bullets.filter(Boolean).map((b, i) => (
        <li key={i} style={{ fontSize: scaledPt(7.9, pi), marginBottom: 1, color: ink, display: 'flex', gap: 5 }}>
          <span style={{ flexShrink: 0 }}>{pi.bulletSymbol || '•'}</span><span>{b}</span>
        </li>
      ))}
    </ul>
  ) : null;

  const skills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  const skillRows = skills.length
    ? Array.from({ length: Math.ceil(skills.length / 2) }, (_, i) => [skills[i], skills[i + Math.ceil(skills.length / 2)]].filter(Boolean))
    : [];
  const identityLine = [
    pi.showDOB && pi.dateOfBirth && `Date of birth: ${pi.dateOfBirth}`,
    pi.showNationality && pi.nationality && `Nationality: ${pi.nationality}`,
    pi.showSex && pi.sex && `Gender: ${pi.sex}`,
  ].filter(Boolean).join(' - ');

  return (
    <div style={{ fontFamily: ds.fontFamily, color: ink, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${ds.padding}px ${ds.padding + 52}px`, background: 'white', minHeight: minH, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: scaledPt(14, pi), fontWeight: 700 }}>{[pi.fullName, pi.jobTitle].filter(Boolean).join(', ')}</div>
        <div style={{ marginTop: 5, color: '#374151', fontSize: scaledPt(7.8, pi) }}>
          {[pi.location, pi.phone, pi.email, pi.website || pi.linkedin].filter(Boolean).join(' - ')}
        </div>
        {identityLine && (
          <div style={{ marginTop: 3, color: '#374151', fontSize: scaledPt(7.6, pi) }}>
            {identityLine}
          </div>
        )}
      </div>

      {pi.summary && (
        <Section id="summary" title={pi.summaryTitle || 'Profile'}>
          <p style={{ margin: 0, fontSize: scaledPt(7.9, pi), textAlign: 'justify' }}>{pi.summary}</p>
        </Section>
      )}

      {d.education?.length > 0 && (
        <TimelineSection id="education" title="Education">
          {Label => d.education.map((edu, i) => (
            <TimelineItem key={edu.id} label={i === 0 ? <Label id="education" title="Education" /> : null} date={dateRange(edu.startDate, edu.endDate, false)} title={edu.degree} org={edu.school} location={edu.location}>
              {edu.gpa && <SmallBullets bullets={[`GPA: ${edu.gpa}`]} />}
            </TimelineItem>
          ))}
        </TimelineSection>
      )}

      {d.experience?.length > 0 && (
        <TimelineSection id="experience" title="Experience">
          {Label => d.experience.map((e, i) => (
            <TimelineItem key={e.id} label={i === 0 ? <Label id="experience" title="Experience" /> : null} date={dateRange(e.startDate, e.endDate, e.current)} title={e.title} org={e.company} location={e.location}>
              {e.description && <p style={{ margin: '5px 0 0', fontSize: scaledPt(7.9, pi), textAlign: 'justify' }}>{e.description}</p>}
              <SmallBullets bullets={e.bullets} />
            </TimelineItem>
          ))}
        </TimelineSection>
      )}

      {skillRows.length > 0 && (
        <Section id="skills" title="Skills">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 44, rowGap: 5 }}>
            {skillRows.flatMap((row, rowIdx) => row.map((skill, i) => (
              <div key={`${rowIdx}-${i}`} data-resume-entry style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: scaledPt(7.8, pi), breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <span>{skill}</span>
                <span style={{ color: muted }}>{i === 0 ? 'Good' : 'Perfectly'}</span>
              </div>
            )))}
          </div>
        </Section>
      )}

      {d.projects?.length > 0 && (
        <TimelineSection id="projects" title="Projects">
          {Label => d.projects.map((p, i) => (
            <TimelineItem key={p.id} label={i === 0 ? <Label id="projects" title="Projects" /> : null} date={p.date ? fmtDate(p.date) : ''} title={p.title} org={p.skills} location={p.link}>
              {p.summary && <p style={{ margin: '5px 0 0', fontSize: scaledPt(7.9, pi), textAlign: 'justify' }}>{p.summary}</p>}
              <SmallBullets bullets={p.bullets} />
            </TimelineItem>
          ))}
        </TimelineSection>
      )}

      {d.languages?.length > 0 && (
        <Section id="languages" title="Languages">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px 28px', fontSize: scaledPt(7.8, pi) }}>
            {d.languages.map(l => (
              <div key={l.id} data-resume-entry style={{ minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <div style={{ fontWeight: 700 }}>{l.language}</div>
                {l.proficiency && <div style={{ color: muted, marginTop: 1 }}>{l.proficiency}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {d.volunteering?.length > 0 && (
        <TimelineSection id="volunteering" title="Volunteering">
          {Label => d.volunteering.map((v, i) => (
            <TimelineItem key={v.id} label={i === 0 ? <Label id="volunteering" title="Volunteering" /> : null} date={dateRange(v.startDate, v.endDate, v.current)} title={v.role} org={v.organization} location={v.location}>
              {v.description && <p style={{ margin: '5px 0 0', fontSize: scaledPt(7.9, pi), textAlign: 'justify' }}>{v.description}</p>}
              <SmallBullets bullets={v.bullets} />
            </TimelineItem>
          ))}
        </TimelineSection>
      )}

      {d.publications?.length > 0 && (
        <Section id="publications" title="Publications">
          <div style={{ fontSize: scaledPt(7.8, pi) }}>
            {d.publications.map(p => (
              <div key={p.id} data-resume-entry style={{ marginBottom: 5, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                {p.authors && <span style={{ color: '#374151' }}>{p.authors} </span>}{p.year && <span style={{ color: muted }}>({p.year}). </span>}"{p.title}."{p.venue && <> <em style={{ color: '#374151' }}>{p.venue}</em>.</>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {d.strengths && (
        <Section id="strengths" title={st(d, 'strengths', 'Key Strengths')}>
          <div style={{ fontSize: scaledPt(7.8, pi) }}>{d.strengths}</div>
        </Section>
      )}

      {d.goals && (
        <Section id="goals" title={st(d, 'goals', 'Career Goals')}>
          <p style={{ margin: 0, fontSize: scaledPt(7.9, pi), textAlign: 'justify' }}>{d.goals}</p>
        </Section>
      )}

      {(d.certifications?.length > 0 || d.awards?.length > 0 || d.achievements?.length > 0 || d.hobbies) && (
        <Section id="hobbies" title="Hobbies">
          <div style={{ fontSize: scaledPt(7.8, pi), minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {d.hobbies && <div style={{ maxWidth: '100%', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{d.hobbies}</div>}
            {d.certifications?.length > 0 && <div style={{ marginTop: 4, overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Certificates:</strong> {d.certifications.map(c => c.name).join(', ')}</div>}
            {d.awards?.length > 0 && <div style={{ marginTop: 4, overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Awards:</strong> {d.awards.map(a => a.title).join(', ')}</div>}
            {d.achievements?.length > 0 && <div style={{ marginTop: 4, overflowWrap: 'anywhere', wordBreak: 'break-word' }}><strong>Achievements:</strong> {d.achievements.map(a => a.title).join(', ')}</div>}
          </div>
        </Section>
      )}

      {(d.referencesAvailableOnRequest || d.references?.length > 0 || d.customBlocks?.length > 0) && (
        <Section id="additional" title="">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 38px', fontSize: scaledPt(7.7, pi) }}>
            {d.referencesAvailableOnRequest && <div><span style={{ color: muted }}>References</span><br />Available upon request</div>}
            {!d.referencesAvailableOnRequest && d.references?.map(r => (
              <div key={r.id} data-resume-entry style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <span style={{ color: muted }}>Reference</span><br />
                <span style={{ fontWeight: 700 }}>{r.name}</span>{r.title && <>, {r.title}</>}{r.company && <> · {r.company}</>}
                {(r.email || r.phone) && <><br />{[r.email, r.phone].filter(Boolean).join(' · ')}</>}
              </div>
            ))}
          </div>
          <CustomBlocks blocks={d.customBlocks} color={rule} divStyle="none" bulletSymbol={pi.bulletSymbol} />
        </Section>
      )}
    </div>
  );
};

// ── GREGORY SIDEBAR ──────────────────────────────────────────────────────────
// Two-column: white left (main content + circular photo header) + dark navy
// right sidebar (contact details + skill bars with orange fill).
const GregorySidebarTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1B2A4A', section: '#1F2937', body: '#1F2937' });
  const navy    = c.accent;
  const body    = c.body;
  const sideBg  = pi.sidebarColor || navy;
  const ds = useDesign(pi, { fontFamily: '"Helvetica Neue", Arial, sans-serif', fontSize: 9.5, lineHeight: 1.62, padding: 28 });
  const pageFillH     = React.useContext(PageFillContext);
  const defaultMinH   = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH          = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);

  const SecHead = ({ title }) => (
    <div style={{ marginTop: 18, marginBottom: 10 }}>
      <div style={{ fontSize: scaledPt(13, pi), fontWeight: 500, color: c.section, letterSpacing: 0, textTransform: sectionTransform(pi) }}>{title}</div>
      <div style={{ height: 1.5, background: '#D1D5DB', marginTop: 5 }} />
    </div>
  );

  const DateLine = ({ start, end, current }) => {
    const left  = start ? fmtDate(start).toUpperCase() : '';
    const right = current ? 'PRESENT' : (end ? fmtDate(end).toUpperCase() : '');
    const range = [left, right].filter(Boolean).join(' — ');
    return range ? (
      <div style={{ fontSize: scaledPt(7.8, pi), color: '#6B7280', letterSpacing: 0.8, marginTop: 2, marginBottom: 5 }}>{range}</div>
    ) : null;
  };

  const photoShown  = !!pi.photo && pi.showPhoto !== false;
  const photoSize   = pi.photoSize || 68;
  const photoRadius = (pi.photoShape === 'circle') ? '50%' : (pi.photoShape === 'rounded') ? '8px' : '4px';
  const sidebarTopPad = ds.padding + (photoShown ? photoSize + 18 : 18);

  const skillCategories = (d.skills || []).filter(s => s.category);

  return (
    <div style={{ fontFamily: ds.fontFamily, fontSize: ds.fontSize, lineHeight: ds.lineHeight, color: body, display: 'flex', minHeight: minH, background: 'white', position: 'relative', overflowWrap: 'break-word', wordBreak: 'break-word' }}>

      {/* Page-gap covers for multi-page fills — widths must match actual column widths (sidebar=196) */}
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, right: 196, top: (i + 1) * defaultMinH, height: 56, background: 'white',  zIndex: 5 }} />
          <div style={{ position: 'absolute', right: 0, width: 196, top: (i + 1) * defaultMinH, height: 56, background: sideBg, zIndex: 5 }} />
        </React.Fragment>
      ))}

      {/* ── LEFT MAIN ── */}
      <div style={{ flex: 1, minWidth: 0, padding: `${ds.padding}px 28px ${ds.padding}px 36px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>

        {/* Header */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 18 }}>
          {photoShown && <img
            src={pi.photo}
            alt=""
            style={{ width: photoSize, height: photoSize, objectFit: 'cover', borderRadius: photoRadius, flexShrink: 0 }}
          />}
          <div>
            <div style={{ fontSize: scaledPt(22, pi), fontWeight: 700, color: body, lineHeight: 1.15 }}>{sampleProfileName(pi)}</div>
            {pi.jobTitle && (
              <div style={{ fontSize: scaledPt(8, pi), color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 2.5, marginTop: 4 }}>{pi.jobTitle}</div>
            )}
          </div>
        </div>

        {/* Summary */}
        {pi.summary && (
          <div data-resume-section style={{ ...noBreak }}>
            <SecHead title={pi.summaryTitle || 'Profile'} />
            <p style={{ margin: 0, fontSize: scaledPt(9, pi), color: '#4B5563', lineHeight: 1.7, textAlign: 'justify' }}>{pi.summary}</p>
          </div>
        )}

        {getSectionOrder(d).map(id => {
          switch (id) {
            case 'experience': return d.experience?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'experience', 'Employment History')} />
                {d.experience.map(e => (
                  <div key={e.id} data-resume-entry style={{ marginBottom: 16, ...noBreak }}>
                    <div style={{ fontWeight: 700, fontSize: scaledPt(10, pi), color: body }}>
                      {[e.title, e.company, e.location].filter(Boolean).join(', ')}
                    </div>
                    <DateLine start={e.startDate} end={e.endDate} current={e.current} />
                    {e.description && <p style={{ margin: '0 0 4px', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{e.description}</p>}
                    <BulletList bullets={e.bullets} symbol={pi.bulletSymbol} />
                  </div>
                ))}
              </div>
            ) : null;

            case 'education': return d.education?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'education', 'Education')} />
                {d.education.map(edu => (
                  <div key={edu.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}>
                    <div style={{ fontWeight: 700, fontSize: scaledPt(10, pi), color: body }}>
                      {[edu.degree, edu.school, edu.location].filter(Boolean).join(', ')}
                    </div>
                    <DateLine start={edu.startDate} end={edu.endDate} current={false} />
                    {edu.gpa && <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280' }}>GPA: {edu.gpa}</div>}
                    <BulletList bullets={edu.bullets} symbol={pi.bulletSymbol} />
                  </div>
                ))}
              </div>
            ) : null;

            case 'projects': return d.projects?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'projects', 'Projects')} />
                {d.projects.map(p => (
                  <div key={p.id} data-resume-entry style={{ marginBottom: 14, ...noBreak }}>
                    <div style={{ fontWeight: 700, fontSize: scaledPt(10, pi), color: body }}>{p.title}</div>
                    {p.date && <DateLine start={p.date} end={null} current={false} />}
                    {p.skills && <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280', marginBottom: 3 }}>{p.skills}</div>}
                    {p.summary && <p style={{ margin: '2px 0 3px', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{p.summary}</p>}
                    <BulletList bullets={p.bullets} symbol={pi.bulletSymbol} />
                  </div>
                ))}
              </div>
            ) : null;

            case 'achievements': return d.achievements?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'achievements', 'Achievements')} />
                {d.achievements.map(a => (
                  <div key={a.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}>
                    <div style={{ fontWeight: 700, fontSize: scaledPt(10, pi), color: body }}>{a.title}</div>
                    {a.date && <DateLine start={a.date} end={null} current={false} />}
                    {a.description && <p style={{ margin: '2px 0 0', fontSize: scaledPt(9, pi), color: '#4B5563' }}>{a.description}</p>}
                  </div>
                ))}
              </div>
            ) : null;

            case 'certifications': return d.certifications?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'certifications', 'Certifications')} />
                {d.certifications.map(c => (
                  <div key={c.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}>
                    <div style={{ fontWeight: 700, fontSize: scaledPt(10, pi) }}>{c.name}</div>
                    {c.issuer && <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280' }}>{c.issuer}{c.date && ` — ${fmtDate(c.date).toUpperCase()}`}</div>}
                  </div>
                ))}
              </div>
            ) : null;

            case 'awards': return d.awards?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'awards', 'Awards')} />
                <AwardsItems awards={d.awards} color={'#6B7280'} />
              </div>
            ) : null;

            case 'volunteering': return d.volunteering?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'volunteering', 'Volunteering')} />
                <VolunteeringItems volunteering={d.volunteering} color={'#6B7280'} bulletSymbol={pi.bulletSymbol} />
              </div>
            ) : null;

            case 'publications': return d.publications?.length > 0 ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'publications', 'Publications')} />
                <PublicationsItems publications={d.publications} />
              </div>
            ) : null;

            case 'goals': return d.goals ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <GoalsDisplay goals={d.goals} d={d} color={'#6B7280'} transform={sectionTransform(pi)} sec={t => <SecHead title={t} />} />
              </div>
            ) : null;

            case 'strengths': return d.strengths ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <StrengthsDisplay strengths={d.strengths} d={d} color={'#6B7280'} transform={sectionTransform(pi)} sec={t => <SecHead title={t} />} />
              </div>
            ) : null;

            case 'hobbies': return d.hobbies ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <HobbiesDisplay hobbies={d.hobbies} d={d} color={'#6B7280'} transform={sectionTransform(pi)} sec={t => <SecHead title={t} />} />
              </div>
            ) : null;

            case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? (
              <div key={id} data-resume-section style={{ ...noBreak }}>
                <SecHead title={secT(d, 'references', 'References')} />
                <ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={'#6B7280'} />
              </div>
            ) : null;

            case 'customBlocks': return (
              <React.Fragment key={id}>
                {(d.customBlocks || []).map(blk => (
                  <div key={blk.id} data-resume-section style={{ ...noBreak }}>
                    <SecHead title={blk.title} />
                    {blk.description && <p style={{ margin: '0 0 4px', fontSize: scaledPt(9, pi) }}>{blk.description}</p>}
                    {blk.showDate && blk.date && <div style={{ fontSize: scaledPt(8, pi), color: '#6B7280' }}>{fmtDate(blk.date).toUpperCase()}</div>}
                    {blk.showLink && blk.link && <a href={blk.link} style={{ fontSize: scaledPt(8.5, pi), color: c.link }}>{blk.link}</a>}
                    {blk.showBullets && <BulletList bullets={blk.bullets} symbol={pi.bulletSymbol} />}
                  </div>
                ))}
              </React.Fragment>
            );

            // Sidebar sections rendered on the right — skip in left column
            case 'skills':
            case 'languages':
              return null;

            default: return null;
          }
        })}
      </div>

      {/* ── RIGHT SIDEBAR ── */}
      <div style={{ width: 196, flexShrink: 0, background: sideBg, padding: `${sidebarTopPad}px 16px 28px`, display: 'flex', flexDirection: 'column', gap: 22, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>

        {/* Contact Details */}
        <div data-resume-section style={{ ...noBreak }}>
          <div style={{ fontSize: scaledPt(11.5, pi), fontWeight: 600, color: 'white', marginBottom: 10, letterSpacing: 0.3 }}>Details</div>
          <div style={{ fontSize: scaledPt(8.4, pi), color: 'rgba(255,255,255,0.78)', lineHeight: 1.75 }}>
            {pi.location    && <div>{pi.location}</div>}
            {pi.phone       && <div style={{ marginTop: 5 }}>{pi.phone}</div>}
            {pi.email       && <div style={{ marginTop: 5, wordBreak: 'break-all' }}>{pi.email}</div>}
            {pi.website     && <div style={{ marginTop: 5, wordBreak: 'break-all' }}>{pi.website}</div>}
            {pi.linkedin    && <div style={{ marginTop: 5, wordBreak: 'break-all' }}>{pi.linkedin}</div>}
            {pi.showDOB         && pi.dateOfBirth  && <div style={{ marginTop: 5 }}><span style={{ opacity: 0.55 }}>Date of Birth: </span>{pi.dateOfBirth}</div>}
            {pi.showNationality && pi.nationality  && <div style={{ marginTop: 5 }}><span style={{ opacity: 0.55 }}>Nationality: </span>{pi.nationality}</div>}
            {pi.showSex         && pi.sex          && <div style={{ marginTop: 5 }}><span style={{ opacity: 0.55 }}>Sex: </span>{pi.sex}</div>}
          </div>
        </div>

        {/* Skills with progress bars */}
        {skillCategories.length > 0 && (
          <div data-resume-section>
            <div style={{ fontSize: scaledPt(11.5, pi), fontWeight: 600, color: 'white', marginBottom: 10, letterSpacing: 0.3 }}>
              {secT(d, 'skills', 'Skills')}
            </div>
            {skillCategories.map((s) => {
              const pct = Math.round(((s.level ?? 4) / 5) * 100);
              return (
                <div key={s.id} data-resume-entry style={{ marginBottom: 11, ...noBreak }}>
                  <div style={{ fontSize: scaledPt(8.4, pi), color: 'rgba(255,255,255,0.88)', marginBottom: 4 }}>{s.category}</div>
                  <div style={{ height: 3, background: 'rgba(255,255,255,0.22)', borderRadius: 2 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: 'white', borderRadius: 2 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Languages */}
        {d.languages?.length > 0 && (
          <div data-resume-section>
            <div style={{ fontSize: scaledPt(11.5, pi), fontWeight: 600, color: 'white', marginBottom: 10, letterSpacing: 0.3 }}>
              {secT(d, 'languages', 'Languages')}
            </div>
            {d.languages.map(l => (
              <div key={l.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}>
                <div style={{ fontSize: scaledPt(8.4, pi), color: 'rgba(255,255,255,0.88)', fontWeight: 600 }}>{l.language}</div>
                {l.proficiency && <div style={{ fontSize: scaledPt(7.8, pi), color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>{l.proficiency}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── SHARED STANDARD SECTIONS (used by newer templates) ───────────────────────
// Renders the full section-order switch with consistent entry layouts.
// `sec` is the template's heading renderer; `skip` lets split-layout templates
// pull sections out into a sidebar.
const StdSections = ({ d, pi, c, sec, skip = [] }) => (
  getSectionOrder(d).filter(id => !skip.includes(id)).map(id => {
    switch (id) {
      case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience', 'Experience'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 700 }}>{e.title}</div><div style={{ color: c.link, fontSize: '9.5pt' }}>{e.company}{e.location && ` · ${e.location}`}</div></div><div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div>{e.description && <p style={{ margin: '3px 0 2px', fontSize: '9pt' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} color={c.body} /></div>)}</div> : null;
      case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'education', 'Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div><div style={{ fontWeight: 700 }}>{edu.degree}</div><div style={{ color: c.link, fontSize: '9.5pt' }}>{edu.school}{edu.location && `, ${edu.location}`}</div></div><div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(edu.startDate)} – {fmtDate(edu.endDate)}</div></div>{edu.gpa && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>GPA: {edu.gpa}</div>}</div>)}</div> : null;
      case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'skills', 'Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ fontSize: '9.5pt', marginBottom: 3, ...noBreak }}><strong>{s.category}:</strong> {pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : ` ${s.items}`}</div>)}</div> : null;
      case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'projects', 'Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 9, ...noBreak }}><div style={{ fontWeight: 700 }}>{p.title}{p.date && <span style={{ fontWeight: 'normal', color: '#6B7280', fontSize: '8.5pt' }}> · {fmtDate(p.date)}</span>}</div>{p.skills && <div style={{ fontSize: '8.5pt', color: c.link }}>{p.skills}</div>}{p.summary && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} color={c.body} /></div>)}</div> : null;
      case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements', 'Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 5, fontSize: '9.5pt', ...noBreak }}><strong>{a.title}</strong>{a.date && <span style={{ color: '#6B7280', fontSize: '8.5pt' }}> · {fmtDate(a.date)}</span>}{a.description && <span>: {a.description}</span>}</div>)}</div> : null;
      case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'certifications', 'Certifications'))}<CertificationsItems certifications={d.certifications} color={c.link} /></div> : null;
      case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'languages', 'Languages'))}<LanguagesItems languages={d.languages} /></div> : null;
      case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards', 'Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
      case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering', 'Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
      case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications', 'Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
      case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
      case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
      case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
      case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references', 'References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
      case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
      default: return null;
    }
  })
);

// ── SLATE SIDEBAR (light-gray left column, photo) ────────────────────────────
const SlateSidebarTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#334155', body: '#1F2937', sidebar: '#F1F5F9' });
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sec = t => <><div style={{ fontWeight: 700, color: c.section, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 1.5, ...sectionBorderStyle(pi, c.section + '33', 2), paddingBottom: 5, marginBottom: 8, marginTop: 16 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  const sideSec = t => <div style={{ fontWeight: 700, color: c.section, fontSize: '8.5pt', textTransform: sectionTransform(pi), letterSpacing: 1.5, margin: '16px 0 8px' }}>{t}</div>;
  const sidebar = new Set(['skills','languages','certifications','strengths']);
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, position: 'relative', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 205, top: (i + 1) * defaultMinH, height: 56, background: pi.sidebarColor || '#F1F5F9', zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 205, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      <div style={{ width: 205, background: pi.sidebarColor || '#F1F5F9', padding: '26px 18px', flexShrink: 0, boxSizing: 'border-box', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border={`3px solid ${c.section}33`} fallbackSize={84} /></div>}
        <div style={{ fontSize: '15pt', fontWeight: 800, lineHeight: 1.2, color: c.section }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: '9pt', color: '#64748B', marginTop: 3 }}>{pi.jobTitle}</div>}
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: '8pt', color: '#475569', marginBottom: 4, overflowWrap: 'anywhere' }}>{v}</div>)}
        <IdentityRow pi={pi} />
        {getSectionOrder(d).filter(id => sidebar.has(id)).map(id => {
          switch (id) {
            case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d,'skills', 'Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><div style={{ fontSize: '8pt', fontWeight: 700, color: '#334155' }}>{s.category}</div><div style={{ fontSize: '8pt', color: '#64748B' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}</div> : null;
            case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d,'languages', 'Languages'))}{d.languages.map(l => <div key={l.id} data-resume-entry style={{ fontSize: '8pt', color: '#475569', marginBottom: 3, ...noBreak }}>{l.language}{l.proficiency && <span style={{ color: '#94A3B8' }}> · {l.proficiency}</span>}</div>)}</div> : null;
            case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d,'certifications', 'Certifications'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: '8pt', color: '#475569', marginBottom: 4, ...noBreak }}><div style={{ fontWeight: 600 }}>{cert.name}</div>{cert.issuer && <div style={{ color: '#94A3B8' }}>{cert.issuer}</div>}</div>)}</div> : null;
            case 'strengths': return d.strengths ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d,'strengths', 'Key Strengths'))}<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <span key={i} style={{ background: 'white', border: `1px solid ${c.section}33`, color: '#475569', borderRadius: 4, padding: '1px 7px', fontSize: '7.5pt' }}>{s}</span>)}</div></div> : null;
            default: return null;
          }
        })}
      </div>
      <div style={{ flex: 1, padding: '26px 24px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.summary && <><div style={{ fontWeight: 700, color: c.section, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 1.5, ...sectionBorderStyle(pi, c.section + '33', 2), paddingBottom: 5, marginBottom: 8 }}>{pi.summaryTitle || 'Profile'}</div><p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills','languages','certifications','strengths']} />
      </div>
    </div>
  );
};

// ── MONO PRO (ATS-friendly black & white, no photo) ──────────────────────────
const MonoProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#111827', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: '"Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const sec = t => <><div style={{ fontWeight: 800, color: c.section, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 2, borderTop: `2.5px solid ${c.section}`, paddingTop: 5, marginBottom: 8, marginTop: 18 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '30px 36px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: '23pt', fontWeight: 800, letterSpacing: -0.5, color: c.section }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: '#4B5563', marginTop: 2 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 6 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  |  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Summary')}<p style={{ margin: 0, fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── IVORY SERIF (centered headings, refined serif, no photo) ─────────────────
const IvorySerifTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#7C6A46', body: '#292524' });
  const ds = useDesign(pi, { fontFamily: '"Playfair Display",Georgia,serif', fontSize: 9.5, lineHeight: 1.65 });
  const pageFillH = React.useContext(PageFillContext);
  const minH = pageFillH || (pi.pageSize === 'letter' ? 1056 : 1122);
  const sec = t => <><div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 9px' }}><div style={{ flex: 1, height: 1, background: c.section + '55' }} /><div style={{ fontWeight: 700, color: c.section, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 3, whiteSpace: 'nowrap' }}>{t}</div><div style={{ flex: 1, height: 1, background: c.section + '55' }} /></div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '34px 40px', background: pi.sidebarColor || '#FFFDF8', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        {/* Guard: a white name (set by older color presets) is invisible on this light background */}
        <div style={{ fontSize: '24pt', fontWeight: 700, letterSpacing: 1, color: pi.nameColor && pi.nameColor.toLowerCase() !== '#ffffff' ? pi.nameColor : '#292524' }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: '10pt', fontStyle: 'italic', color: c.link, marginTop: 3 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: '8.5pt', color: '#78716C', marginTop: 7 }}>{[pi.email, pi.phone, pi.location, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: 0, fontSize: '9.5pt', textAlign: 'center', color: '#44403C' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── TEAL BANNER (colored header band with photo, single column) ──────────────
const TealBannerTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0F766E', name: '#ffffff', body: '#1F2937' });
  const ds = useDesign(pi, { fontFamily: '"Lato","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const sec = t => <><div style={{ fontWeight: 700, color: c.section, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 1.5, borderLeft: `4px solid ${c.section}`, paddingLeft: 9, marginBottom: 9, marginTop: 16 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ background: c.accent, color: 'white', padding: '24px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontSize: '21pt', fontWeight: 800, color: c.name }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: '10.5pt', opacity: 0.85, marginTop: 2, color: c.name }}>{pi.jobTitle}</div>}
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: '8pt', opacity: 0.85, flexWrap: 'wrap' }}>
            {[pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
          </div>
          {(pi.showDOB || pi.showNationality || pi.showSex) && <div style={{ fontSize: '8pt', opacity: 0.75, marginTop: 4 }}>{[pi.showDOB && pi.dateOfBirth && `Born: ${pi.dateOfBirth}`, pi.showNationality && pi.nationality, pi.showSex && pi.sex].filter(Boolean).join('  ·  ')}</div>}
        </div>
        <Photo pi={pi} border="3px solid rgba(255,255,255,0.45)" fallbackSize={76} />
      </div>
      <div style={{ padding: '18px 30px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── SWISS GRID (label column left, content right, no photo) ──────────────────
const SwissGridTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#B91C1C', body: '#18181B' });
  const ds = useDesign(pi, { fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  // sec renders the left label; StdSections wraps each section, so we lay out
  // label + content with a grid via the heading itself spanning the row.
  const sec = t => <><div style={{ fontWeight: 800, color: c.section, fontSize: '8.5pt', textTransform: sectionTransform(pi), letterSpacing: 2.5, ...sectionBorderStyle(pi, '#E4E4E7', 1), paddingBottom: 4, marginBottom: 9, marginTop: 18 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '32px 38px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: `3px solid ${c.section}`, paddingBottom: 14, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: '22pt', fontWeight: 800, letterSpacing: -0.5 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: '10pt', color: c.link, fontWeight: 600, marginTop: 2 }}>{pi.jobTitle}</div>}
          <IdentityRow pi={pi} />
        </div>
        <div style={{ fontSize: '8pt', color: '#71717A', textAlign: 'right', lineHeight: 1.7 }}>
          {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <div key={i}>{v}</div>)}
        </div>
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: 0, fontSize: '9.5pt', color: '#3F3F46' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── OXFORD CLASSIC (centered serif, crimson rule, single-column) ──────────────
const OxfordClassicTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#7B1C1C', body: '#1a1a1a' });
  const crimson = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Georgia","Times New Roman",serif', fontSize: 10.5, lineHeight: 1.7 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 8 }}>
      <div style={{ fontSize: '10pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 2, ...sectionBorderStyle(pi, c.section, 1.5), paddingBottom: 4 }}>{t}</div>
      <SectionDividerLine pi={pi} color={c.section + '88'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '36px 48px', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: '22pt', fontWeight: 700, letterSpacing: 1, color: body }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '11pt', color: crimson, marginTop: 5, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
        <div style={{ borderTop: `2px solid ${crimson}`, borderBottom: `2px solid ${crimson}`, margin: '10px auto', padding: '5px 0', maxWidth: 460 }}>
          <div style={{ fontSize: '8.5pt', color: '#555', letterSpacing: 0.3 }}>{[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).join('  ·  ')}</div>
        </div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Research Profile')}<p style={{ margin: '0 0 4px', textAlign: 'justify', fontSize: '10pt' }}>{pi.summary}</p></>}
      {getSectionOrder(d).map(id => {
        switch (id) {
          case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience','Academic Positions'))}{d.experience.map(e => <div key={e.id} data-resume-entry style={{ marginBottom: 12, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}><div><span style={{ fontWeight: 700 }}>{e.title}</span>{e.company && <span style={{ fontStyle: 'italic', color: '#444' }}>, {e.company}</span>}{e.location && <span style={{ color: '#777', fontSize: '9.5pt' }}>, {e.location}</span>}</div><div style={{ fontSize: '9pt', color: '#777', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div>{e.description && <p style={{ margin: '3px 0 2px', fontSize: '9.5pt' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'education','Education'))}{d.education.map(edu => <div key={edu.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}><div><span style={{ fontWeight: 700 }}>{edu.degree}</span>{edu.school && <span style={{ fontStyle: 'italic', color: '#444' }}>, {edu.school}</span>}{edu.location && <span style={{ color: '#777', fontSize: '9.5pt' }}>, {edu.location}</span>}</div><div style={{ fontSize: '9pt', color: '#777', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(edu.startDate)} – {fmtDate(edu.endDate)}</div></div>{edu.gpa && <div style={{ fontSize: '9pt', color: '#555' }}>GPA: {edu.gpa}</div>}</div>)}</div> : null;
          case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications','Publications'))}<PublicationsItems publications={d.publications} /></div> : null;
          case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'skills','Research Methods & Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 3, ...noBreak }}><strong>{s.category}:</strong> {pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : ` ${s.items}`}</div>)}</div> : null;
          case 'projects': return d.projects?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'projects','Research Projects'))}{d.projects.map(p => <div key={p.id} data-resume-entry style={{ marginBottom: 10, ...noBreak }}><span style={{ fontWeight: 700 }}>{p.title}</span>{p.date && <span style={{ color: '#777', fontSize: '9pt' }}> ({fmtDate(p.date)})</span>}{p.summary && <p style={{ margin: '2px 0', fontSize: '9.5pt' }}>{p.summary}</p>}<BulletList bullets={p.bullets} symbol={pi.bulletSymbol} /></div>)}</div> : null;
          case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'achievements','Awards & Honours'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ marginBottom: 8, ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontWeight: 700 }}>{a.title}</span>{a.date && <span style={{ color: '#777', fontSize: '9pt' }}>{fmtDate(a.date)}</span>}</div>{a.description && <p style={{ margin: '2px 0', fontSize: '9.5pt' }}>{a.description}</p>}</div>)}</div> : null;
          case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'certifications','Certifications'))}<CertificationsItems certifications={d.certifications} color={c.link} /></div> : null;
          case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'languages','Languages'))}<LanguagesItems languages={d.languages} /></div> : null;
          case 'awards': return d.awards?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'awards','Additional Awards'))}<AwardsItems awards={d.awards} color={c.link} /></div> : null;
          case 'volunteering': return d.volunteering?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'volunteering','Service & Volunteering'))}<VolunteeringItems volunteering={d.volunteering} color={c.link} bulletSymbol={pi.bulletSymbol} /></div> : null;
          case 'goals': return d.goals ? <GoalsDisplay key={id} goals={d.goals} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'strengths': return d.strengths ? <StrengthsDisplay key={id} strengths={d.strengths} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'hobbies': return d.hobbies ? <HobbiesDisplay key={id} hobbies={d.hobbies} d={d} color={c.section} transform={sectionTransform(pi)} sec={sec} /> : null;
          case 'references': return (d.references?.length > 0 || d.referencesAvailableOnRequest) ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'references','References'))}<ReferencesItems references={d.references} referencesAvailableOnRequest={d.referencesAvailableOnRequest} color={c.link} /></div> : null;
          case 'customBlocks': return <CustomBlocks key={id} blocks={d.customBlocks} color={c.section} divStyle={pi.sectionDivider} bulletSymbol={pi.bulletSymbol} />;
          default: return null;
        }
      })}
    </div>
  );
};

// ── CAMBRIDGE BLUE (deep navy sidebar, white content, academic) ───────────────
const CambridgeBlueTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#003366', body: '#1F2937' });
  const navy = c.accent;
  const sideBg = pi.sidebarColor || navy;
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Calibri",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 7 }}>
      <div style={{ fontSize: '9.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.8, borderLeft: `3px solid ${c.section}`, paddingLeft: 8, lineHeight: 1.3 }}>{t}</div>
    </div>
  );
  const sideSec = t => <div style={{ fontSize: '8pt', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 2, marginTop: 18, marginBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: 4 }}>{t}</div>;
  return (
    <div style={{ fontFamily: ds.fontFamily, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 210, top: (i + 1) * defaultMinH, height: 56, background: sideBg, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 210, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      <div style={{ width: 210, background: sideBg, flexShrink: 0, padding: '28px 16px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border="3px solid rgba(255,255,255,0.25)" fallbackSize={80} /></div>}
        <div style={{ fontSize: '14pt', fontWeight: 700, color: 'white', lineHeight: 1.2, marginBottom: 4 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '8.5pt', color: 'rgba(255,255,255,0.65)', fontStyle: 'italic', marginBottom: 6 }}>{pi.jobTitle}</div>}
        <IdentityRow pi={pi} />
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.8)', marginBottom: 5, overflowWrap: 'anywhere' }}>{v}</div>)}
        {allSkills.length > 0 && <>{sideSec(st(d,'skills','Skills'))}{allSkills.map((sk, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.8)', marginBottom: 4, display: 'flex', gap: 5 }}><span style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>›</span><span>{sk}</span></div>)}</>}
        {d.languages?.length > 0 && <>{sideSec(st(d,'languages','Languages'))}{d.languages.map(l => <div key={l.id} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>{l.language}{l.proficiency && <span style={{ color: 'rgba(255,255,255,0.5)' }}> · {l.proficiency}</span>}</div>)}</>}
        {d.strengths && <>{sideSec(st(d,'strengths','Key Strengths'))}{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>· {s}</div>)}</>}
      </div>
      <div style={{ flex: 1, background: 'white', color: c.body, padding: '28px 24px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.summary && <><div style={{ borderLeft: `3px solid ${c.section}`, paddingLeft: 8, marginBottom: 8 }}><div style={{ fontSize: '9.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.8 }}>{pi.summaryTitle || 'Research Profile'}</div></div><p style={{ margin: '0 0 6px', fontSize: '9.5pt', color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills','languages','strengths']} />
      </div>
    </div>
  );
};

// ── STEM SCHOLAR (teal accent, ATS single-column, clean sans) ─────────────────
const StemScholarTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0F766E', body: '#111827' });
  const teal = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 10, lineHeight: 1.6 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ height: 2, width: 18, background: c.section, flexShrink: 0, borderRadius: 1 }} />
        <div style={{ fontSize: '9.5pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.8 }}>{t}</div>
        <div style={{ flex: 1, height: 1, background: c.section + '33', borderRadius: 1 }} />
      </div>
      <SectionDividerLine pi={pi} color={c.section + '88'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ background: teal, padding: '22px 36px 18px', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '22pt', fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 18px', marginTop: 10, fontSize: '8pt', color: 'rgba(255,255,255,0.75)' }}>
          {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
        </div>
      </div>
      <div style={{ padding: '4px 36px 30px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Research Summary')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── EMERALD ACADEMIC (emerald sidebar, scholarly two-column) ──────────────────
const EmeraldAcademicTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#065F46', body: '#1F2937' });
  const emerald = c.accent;
  const sideBg = pi.sidebarColor || emerald;
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Calibri",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 7 }}>
      <div style={{ fontSize: '9pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5, ...sectionBorderStyle(pi, c.section + '44', 1.5), paddingBottom: 5 }}>{t}</div>
      <SectionDividerLine pi={pi} color={c.section + '66'} />
    </div>
  );
  const sideSec = t => <div style={{ fontSize: '7.5pt', fontWeight: 800, color: 'white', textTransform: 'uppercase', letterSpacing: 2.5, marginTop: 20, marginBottom: 8, opacity: 0.7 }}>{t}</div>;
  return (
    <div style={{ fontFamily: ds.fontFamily, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 195, top: (i + 1) * defaultMinH, height: 56, background: sideBg, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 195, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      <div style={{ width: 195, background: sideBg, flexShrink: 0, padding: '26px 14px', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 12 }}><Photo pi={pi} border="3px solid rgba(255,255,255,0.25)" fallbackSize={76} /></div>}
        <div style={{ fontSize: '13pt', fontWeight: 700, color: 'white', lineHeight: 1.2, marginBottom: 3 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', marginBottom: 4 }}>{pi.jobTitle}</div>}
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.85)', marginBottom: 5, overflowWrap: 'anywhere' }}>{v}</div>)}
        {allSkills.length > 0 && <>{sideSec(st(d,'skills','Skills'))}{allSkills.map((sk, i) => <div key={i} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.85)', marginBottom: 4, paddingLeft: 8, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'rgba(255,255,255,0.4)' }}>›</span>{sk}</div>)}</>}
        {d.languages?.length > 0 && <>{sideSec(st(d,'languages','Languages'))}{d.languages.map(l => <div key={l.id} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.85)', marginBottom: 4 }}>{l.language}{l.proficiency && <span style={{ color: 'rgba(255,255,255,0.5)' }}> ({l.proficiency})</span>}</div>)}</>}
        {d.certifications?.length > 0 && <>{sideSec(st(d,'certifications','Certifications'))}{d.certifications.map(cert => <div key={cert.id} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}><div style={{ fontWeight: 700 }}>{cert.name}</div>{cert.issuer && <div style={{ color: 'rgba(255,255,255,0.6)' }}>{cert.issuer}</div>}</div>)}</>}
      </div>
      <div style={{ flex: 1, background: 'white', color: c.body, padding: '26px 22px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.summary && <><div style={{ ...sectionBorderStyle(pi, c.section + '44', 1.5), paddingBottom: 5, marginBottom: 7 }}><div style={{ fontSize: '9pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{pi.summaryTitle || 'Academic Profile'}</div></div><p style={{ margin: '0 0 6px', fontSize: '9.5pt', color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills','languages','certifications']} />
      </div>
    </div>
  );
};

// ── COBALT TIMELINE (left cobalt rule, timeline dots, single-column) ───────────
const CobaltTimelineTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1E40AF', body: '#1F2937' });
  const cobalt = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.section, flexShrink: 0 }} />
        <div style={{ fontSize: '9.5pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.8 }}>{t}</div>
        <div style={{ flex: 1, height: 1, background: c.section + '33' }} />
      </div>
    </div>
  );
  const timelineEntry = (content) => (
    <div style={{ position: 'relative', paddingLeft: 22, marginBottom: 12 }}>
      <div style={{ position: 'absolute', left: 0, top: 5, width: 8, height: 8, borderRadius: '50%', border: `2px solid ${cobalt}`, background: 'white' }} />
      <div style={{ position: 'absolute', left: 3.5, top: 14, bottom: -4, width: 1, background: cobalt + '33' }} />
      {content}
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ borderLeft: `5px solid ${cobalt}`, padding: '24px 32px 20px 28px', background: cobalt + '07' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '22pt', fontWeight: 900, color: body, letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: cobalt, fontWeight: 600, marginTop: 3 }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginTop: 10, fontSize: '8.5pt', color: '#6B7280' }}>
          {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 4, height: 4, borderRadius: '50%', background: cobalt, display: 'inline-block', flexShrink: 0 }} />{v}</span>)}
        </div>
      </div>
      <div style={{ padding: '4px 32px 30px 28px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Research Focus')}<p style={{ margin: '0 0 4px 22px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        {getSectionOrder(d).map(id => {
          switch (id) {
            case 'experience': return d.experience?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'experience','Academic Positions'))}{d.experience.map(e => timelineEntry(<div key={e.id} data-resume-entry style={{ ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}><div style={{ fontWeight: 700 }}>{e.title}</div><div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(e.startDate)} – {e.current ? 'Present' : fmtDate(e.endDate)}</div></div><div style={{ fontSize: '9pt', color: c.link }}>{e.company}{e.location && ` · ${e.location}`}</div>{e.description && <p style={{ margin: '2px 0', fontSize: '9pt' }}>{e.description}</p>}<BulletList bullets={e.bullets} symbol={pi.bulletSymbol} /></div>))}</div> : null;
            case 'education': return d.education?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'education','Education'))}{d.education.map(edu => timelineEntry(<div key={edu.id} data-resume-entry style={{ ...noBreak }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}><div style={{ fontWeight: 700 }}>{edu.degree}</div><div style={{ fontSize: '8.5pt', color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmtDate(edu.startDate)} – {fmtDate(edu.endDate)}</div></div><div style={{ fontSize: '9pt', color: c.link }}>{edu.school}{edu.location && `, ${edu.location}`}</div>{edu.gpa && <div style={{ fontSize: '8.5pt', color: '#6B7280' }}>GPA: {edu.gpa}</div>}</div>))}</div> : null;
            case 'publications': return d.publications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sec(secT(d,'publications','Publications'))}<div style={{ paddingLeft: 22 }}><PublicationsItems publications={d.publications} /></div></div> : null;
            default: return <StdSections key={id} d={{ ...d, sectionOrder: [id] }} pi={pi} c={c} sec={sec} />;
          }
        })}
      </div>
    </div>
  );
};

// ── MANUSCRIPT (warm ivory, serif, publication-style, humanities) ─────────────
const ManuscriptTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#2D2416', section: '#7C4D19', body: '#1a1205' });
  const ink = c.accent;
  const rust = c.section;
  const ds = useDesign(pi, { fontFamily: '"Palatino Linotype","Palatino","Book Antiqua",serif', fontSize: 10.5, lineHeight: 1.75 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 20, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, height: 1, background: rust + '55' }} />
        <div style={{ fontSize: '8.5pt', fontWeight: 700, color: rust, textTransform: sectionTransform(pi), letterSpacing: 3 }}>{t}</div>
        <div style={{ flex: 1, height: 1, background: rust + '55' }} />
      </div>
      <SectionDividerLine pi={pi} color={rust + '66'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '40px 52px', background: '#FEFCF5', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ borderTop: `0.5px solid ${rust}88`, paddingTop: 14, marginBottom: 8 }}>
          <div style={{ fontSize: '26pt', fontWeight: 700, letterSpacing: 2, color: ink }}>{pi.fullName || 'Your Name'}</div>
          {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: rust, marginTop: 6, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
        </div>
        <div style={{ fontSize: '8.5pt', color: '#6B5B3E', letterSpacing: 0.5 }}>{[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
        <div style={{ borderBottom: `0.5px solid ${rust}88`, paddingBottom: 14, marginTop: 8 }} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Academic Profile')}<p style={{ margin: '0 0 4px', textAlign: 'justify', fontSize: '10pt', fontStyle: 'italic' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── NORDIC SCHOLAR (dark header band, clean sans, minimal) ────────────────────
const NordicScholarTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1C2B39', body: '#1F2937' });
  const charcoal = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ fontSize: '9.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 2, borderBottom: `2px solid ${c.section}22`, paddingBottom: 4 }}>{t}</div>
      <SectionDividerLine pi={pi} color={c.section + '44'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ background: charcoal, padding: '30px 36px 26px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: 180, height: '100%', background: 'rgba(255,255,255,0.03)', clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0% 100%)' }} />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '22pt', fontWeight: 900, color: 'white', letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '10pt', color: 'rgba(255,255,255,0.65)', marginTop: 5, fontWeight: 500 }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 16px', marginTop: 12, fontSize: '8pt', color: 'rgba(255,255,255,0.55)' }}>
              {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
            </div>
          </div>
        </div>
      </div>
      <div style={{ padding: '8px 36px 30px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#4B5563' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── AMBER EXECUTIVE (warm amber gradient header, serif elegance) ──────────────
const AmberExecutiveTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#B45309', body: '#1C1917' });
  const amber = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Georgia","Times New Roman",serif', fontSize: 10, lineHeight: 1.65 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
        <div style={{ width: 22, height: 2, background: c.section, flexShrink: 0 }} />
        <div style={{ fontSize: '9pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 2 }}>{t}</div>
        <div style={{ flex: 1, height: 1, background: c.section + '33' }} />
      </div>
      <SectionDividerLine pi={pi} color={c.section + '77'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${amber} 0%, ${shadeHex(amber, -0.28)} 100%)`, padding: '28px 34px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '24pt', fontWeight: 700, color: 'white', letterSpacing: 0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
          {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: 'rgba(255,255,255,0.8)', marginTop: 5, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 14px', marginTop: 10, fontSize: '8pt', color: 'rgba(255,255,255,0.75)' }}>
            {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
          </div>
          <IdentityRow pi={pi} />
        </div>
        {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border="3px solid rgba(255,255,255,0.35)" fallbackSize={76} />}
      </div>
      <div style={{ padding: '6px 34px 30px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#44403C' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── VIOLET SIDEBAR (right sidebar for contact/skills, content on left) ────────
const VioletSidebarTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#5B21B6', body: '#111827' });
  const violet = c.accent;
  const sideBg = pi.sidebarColor || violet;
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 6 }}>
      <div style={{ fontSize: '9pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.8, borderBottom: `2px solid ${c.section}22`, paddingBottom: 4 }}>{t}</div>
      <SectionDividerLine pi={pi} color={c.section + '66'} />
    </div>
  );
  const sideSec = t => <div style={{ fontSize: '7.5pt', fontWeight: 800, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 2.5, marginTop: 20, marginBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: 4 }}>{t}</div>;
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  return (
    <div style={{ fontFamily: ds.fontFamily, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, right: 195, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
          <div style={{ position: 'absolute', right: 0, width: 195, top: (i + 1) * defaultMinH, height: 56, background: sideBg, zIndex: 3 }} />
        </React.Fragment>
      ))}
      {/* LEFT: main content */}
      <div style={{ flex: 1, color: c.body, padding: '28px 22px 28px 28px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '22pt', fontWeight: 900, color: '#111827', letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
          {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: violet, fontWeight: 600, marginTop: 3 }}>{pi.jobTitle}</div>}
          <IdentityRow pi={pi} />
        </div>
        {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills','languages','certifications','strengths','hobbies']} />
      </div>
      {/* RIGHT: sidebar */}
      <div style={{ width: 195, background: sideBg, flexShrink: 0, padding: '28px 14px', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border="3px solid rgba(255,255,255,0.25)" fallbackSize={78} /></div>}
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.82)', marginBottom: 6, overflowWrap: 'anywhere' }}>{v}</div>)}
        {allSkills.length > 0 && <>{sideSec(st(d,'skills','Skills'))}{allSkills.map((sk, i) => <div key={i} style={{ display: 'flex', gap: 5, fontSize: '8pt', color: 'rgba(255,255,255,0.82)', marginBottom: 5 }}><span style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>›</span><span>{sk}</span></div>)}</>}
        {d.languages?.length > 0 && <>{sideSec(st(d,'languages','Languages'))}{d.languages.map(l => <div key={l.id} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.82)', marginBottom: 5 }}>{l.language}{l.proficiency && <span style={{ color: 'rgba(255,255,255,0.5)' }}> · {l.proficiency}</span>}</div>)}</>}
        {d.strengths && <>{sideSec(st(d,'strengths','Strengths'))}{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.82)', marginBottom: 4 }}>· {s}</div>)}</>}
        {d.hobbies && <>{sideSec(st(d,'hobbies','Interests'))}{d.hobbies.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.82)', marginBottom: 4 }}>· {s}</div>)}</>}
      </div>
    </div>
  );
};

// ── BOLD STRIPE (banded section headers, high-contrast, modern) ───────────────
const BoldStripeTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0369A1', body: '#0F172A' });
  const ds = useDesign(pi, { fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const sec = t => (
    <div style={{ background: c.section + '0F', borderLeft: `4px solid ${c.section}`, padding: '6px 10px', margin: '18px -10px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ fontSize: '8.5pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 2 }}>{t}</div>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '0', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Header band */}
      <div style={{ borderTop: `6px solid ${c.section}`, padding: '22px 30px 18px', background: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '23pt', fontWeight: 900, color: c.body, letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: c.section, fontWeight: 700, marginTop: 4 }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', marginTop: 10, fontSize: '8.5pt', color: '#475569' }}>
          {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.section, flexShrink: 0, display: 'inline-block' }} />{v}
            </span>
          ))}
        </div>
      </div>
      <div style={{ padding: '0 30px 28px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Summary')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#334155' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── ROSE GOLD (warm rose accent, right-aligned header, elegant) ───────────────
const RoseGoldTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#9F3054', body: '#1C1917' });
  const rose = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Lato","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const body = c.body;
  const sideBg = pi.sidebarColor || rose + '0A';
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 6 }}>
      <div style={{ fontSize: '9pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5, borderBottom: `1.5px solid ${c.section}33`, paddingBottom: 5 }}>{t}</div>
      <SectionDividerLine pi={pi} color={c.section + '55'} />
    </div>
  );
  const sideSec = t => <div style={{ fontSize: '7.5pt', fontWeight: 800, color: c.section, textTransform: 'uppercase', letterSpacing: 2.5, marginTop: 18, marginBottom: 7, paddingBottom: 5, borderBottom: `1px solid ${c.section}22` }}>{t}</div>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 190, top: (i + 1) * defaultMinH, height: 56, background: sideBg, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 190, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      {/* LEFT light-rose sidebar */}
      <div style={{ width: 190, background: sideBg, borderRight: `2px solid ${rose}18`, flexShrink: 0, padding: '28px 14px', boxSizing: 'border-box', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border={`3px solid ${rose}44`} fallbackSize={80} /></div>}
        <div style={{ fontSize: '13.5pt', fontWeight: 800, color: body, lineHeight: 1.2, marginBottom: 3 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '8.5pt', color: rose, fontWeight: 600, marginBottom: 10 }}>{pi.jobTitle}</div>}
        <IdentityRow pi={pi} />
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: '7.5pt', color: '#57534E', marginBottom: 5, overflowWrap: 'anywhere' }}>{v}</div>)}
        {allSkills.length > 0 && <>{sideSec(st(d,'skills','Skills'))}{allSkills.map((sk, i) => <div key={i} style={{ fontSize: '8pt', color: '#44403C', marginBottom: 4, paddingLeft: 8, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: rose }}>›</span>{sk}</div>)}</>}
        {d.languages?.length > 0 && <>{sideSec(st(d,'languages','Languages'))}{d.languages.map(l => <div key={l.id} style={{ fontSize: '8pt', color: '#57534E', marginBottom: 4 }}>{l.language}{l.proficiency && <span style={{ color: '#A8A29E' }}> · {l.proficiency}</span>}</div>)}</>}
        {d.certifications?.length > 0 && <>{sideSec(st(d,'certifications','Certifications'))}{d.certifications.map(cert => <div key={cert.id} style={{ fontSize: '7.5pt', color: '#57534E', marginBottom: 5 }}><div style={{ fontWeight: 700 }}>{cert.name}</div>{cert.issuer && <div style={{ color: '#A8A29E' }}>{cert.issuer}</div>}</div>)}</>}
      </div>
      {/* RIGHT main content */}
      <div style={{ flex: 1, padding: '28px 22px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.summary && <><div style={{ borderLeft: `3px solid ${c.section}`, paddingLeft: 8, marginBottom: 6 }}><div style={{ fontSize: '9pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{pi.summaryTitle || 'Profile'}</div></div><p style={{ margin: '0 0 6px', fontSize: '9.5pt', color: '#44403C' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills','languages','certifications']} />
      </div>
    </div>
  );
};

// ── FOREST PRO (forest green left rule, single column, clean ATS) ─────────────
const ForestProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#166534', body: '#111827' });
  const green = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Calibri",Arial,sans-serif', fontSize: 10, lineHeight: 1.65 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ borderLeft: `4px solid ${c.section}`, paddingLeft: 10, display: 'flex', alignItems: 'center' }}>
        <div style={{ fontSize: '9.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{t}</div>
      </div>
      <div style={{ height: 1, background: c.section + '22', margin: '5px 0 2px' }} />
      <SectionDividerLine pi={pi} color={c.section + '66'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '28px 36px', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, paddingBottom: 16, borderBottom: `3px solid ${green}`, marginBottom: 2 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '24pt', fontWeight: 800, color: body, letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
          {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: green, fontWeight: 600, marginTop: 4 }}>{pi.jobTitle}</div>}
          <IdentityRow pi={pi} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 16px', marginTop: 8, fontSize: '8.5pt', color: '#4B5563' }}>
            {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
          </div>
        </div>
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── SKY PRO (sky-blue gradient header, pill dot contacts, single column) ──────
const SkyProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0284C7', body: '#0F172A' });
  const sky = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 28, height: 3, background: c.section, borderRadius: 2, flexShrink: 0 }} />
        <div style={{ fontSize: '9pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{t}</div>
      </div>
      <div style={{ height: 1, background: c.section + '22', marginTop: 4 }} />
      <SectionDividerLine pi={pi} color={c.section + '55'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ background: `linear-gradient(135deg,${sky} 0%,${shadeHex(sky, -0.22)} 100%)`, padding: '26px 32px 22px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -20, left: 120, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, position: 'relative' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '22pt', fontWeight: 900, color: 'white', letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '10pt', color: 'rgba(255,255,255,0.82)', fontWeight: 500, marginTop: 5 }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 10, fontSize: '8pt', color: 'rgba(255,255,255,0.72)' }}>
              {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.45)', flexShrink: 0, display: 'inline-block' }} />{v}
                </span>
              ))}
            </div>
          </div>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border="3px solid rgba(255,255,255,0.35)" fallbackSize={74} />}
        </div>
      </div>
      <div style={{ padding: '6px 32px 28px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'About Me')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#334155' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── CHARCOAL SPLIT (dark left sidebar, amber accent, content on right) ────────
const CharcoalSplitTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#D97706', body: '#111827', sidebar: '#1E293B' });
  const charcoal = c.sidebar;
  const accent = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 6 }}>
      <div style={{ fontSize: '8.5pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 2, borderBottom: `2px solid ${c.section}33`, paddingBottom: 5 }}>{t}</div>
      <SectionDividerLine pi={pi} color={c.section + '55'} />
    </div>
  );
  const sideSec = t => <div style={{ fontSize: '7.5pt', fontWeight: 800, color: c.section, textTransform: 'uppercase', letterSpacing: 2.5, marginTop: 18, marginBottom: 7 }}>{t}</div>;
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 195, top: (i + 1) * defaultMinH, height: 56, background: charcoal, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 195, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      <div style={{ width: 195, background: charcoal, flexShrink: 0, padding: '28px 16px', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border={`3px solid ${accent}55`} fallbackSize={80} /></div>}
        <div style={{ fontSize: '13pt', fontWeight: 800, color: 'white', lineHeight: 1.2, marginBottom: 3 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '8.5pt', color: accent, fontWeight: 600, marginBottom: 10 }}>{pi.jobTitle}</div>}
        <IdentityRow pi={pi} />
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.website, pi.linkedin].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.7)', marginBottom: 5, overflowWrap: 'anywhere' }}>{v}</div>)}
        {allSkills.length > 0 && <>{sideSec(st(d,'skills','Skills'))}{allSkills.map((sk, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.75)', marginBottom: 4, paddingLeft: 8, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: accent }}>›</span>{sk}</div>)}</>}
        {d.languages?.length > 0 && <>{sideSec(st(d,'languages','Languages'))}{d.languages.map(l => <div key={l.id} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>{l.language}{l.proficiency && <span style={{ color: 'rgba(255,255,255,0.4)' }}> · {l.proficiency}</span>}</div>)}</>}
        {d.certifications?.length > 0 && <>{sideSec(st(d,'certifications','Certifications'))}{d.certifications.map(cert => <div key={cert.id} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.7)', marginBottom: 5 }}><div style={{ fontWeight: 700 }}>{cert.name}</div>{cert.issuer && <div style={{ color: 'rgba(255,255,255,0.4)' }}>{cert.issuer}</div>}</div>)}</>}
        {d.strengths && <>{sideSec(st(d,'strengths','Strengths'))}{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>· {s}</div>)}</>}
        {d.hobbies && <>{sideSec(st(d,'hobbies','Interests'))}{d.hobbies.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <div key={i} style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>· {s}</div>)}</>}
      </div>
      <div style={{ flex: 1, padding: '28px 22px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills','languages','certifications','strengths','hobbies']} />
      </div>
    </div>
  );
};

// ── TERRACOTTA (burnt-orange italic serif header, warm earthy tones) ───────────
const TerracottaTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#B54A2B', body: '#1C1917' });
  const terra = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Georgia","Times New Roman",serif', fontSize: 10, lineHeight: 1.65 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: '8.5pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 2.5 }}>{t}</div>
        <div style={{ flex: 1, height: 1, background: c.section + '44' }} />
      </div>
      <SectionDividerLine pi={pi} color={c.section + '66'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ background: `linear-gradient(135deg,${terra} 0%,${shadeHex(terra, -0.28)} 100%)`, padding: '30px 36px 24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 80% 50%,rgba(255,255,255,0.07) 0%,transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, position: 'relative' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '25pt', fontWeight: 700, color: 'white', letterSpacing: 0.5, lineHeight: 1.1, fontStyle: 'italic' }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: 'rgba(255,255,255,0.8)', marginTop: 6 }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 16px', marginTop: 10, fontSize: '8pt', color: 'rgba(255,255,255,0.7)' }}>
              {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
            </div>
          </div>
        </div>
      </div>
      <div style={{ padding: '8px 36px 30px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#44403C' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── STARTUP CLEAN (pill section labels, gradient accent line, modern sans) ─────
const StartupCleanTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#6366F1', body: '#111827' });
  const ds = useDesign(pi, { fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const sec = t => (
    <div style={{ marginTop: 20, marginBottom: 8 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: c.section + '12', borderRadius: 5, padding: '3px 10px' }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: c.section, flexShrink: 0 }} />
        <div style={{ fontSize: '8.5pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{t}</div>
      </div>
      <div style={{ height: 1, background: c.section + '18', marginTop: 5 }} />
      <SectionDividerLine pi={pi} color={c.section + '55'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '36px 36px 28px', boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '28pt', fontWeight: 900, color: '#111827', letterSpacing: -1, lineHeight: 1 }}>{pi.fullName || 'Your Name'}</div>
          {pi.jobTitle && <div style={{ fontSize: '11pt', color: c.section, fontWeight: 600, marginTop: 5 }}>{pi.jobTitle}</div>}
          <IdentityRow pi={pi} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 9, fontSize: '8.5pt', color: '#6B7280' }}>
            {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: c.section + '88', display: 'inline-block', flexShrink: 0 }} />{v}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div style={{ height: 2, background: `linear-gradient(90deg,${c.section},${c.section}00)`, borderRadius: 2, marginBottom: 4 }} />
      {pi.summary && <>{sec(pi.summaryTitle || 'About')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── CHAMPAGNE (warm gold band header, centered serif, symmetrical dividers) ───
const ChampagneTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#A37E2C', body: '#1C1917' });
  const gold = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Garamond","Georgia","Times New Roman",serif', fontSize: 10, lineHeight: 1.65 });
  const body = c.body;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 1, background: c.section + '55' }} />
        <div style={{ fontSize: '8pt', fontWeight: 700, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 3.5, flexShrink: 0 }}>{t}</div>
        <div style={{ flex: 1, height: 1, background: c.section + '55' }} />
      </div>
      <SectionDividerLine pi={pi} color={c.section + '66'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      <div style={{ background: `linear-gradient(180deg,${gold}1C 0%,${gold}38 55%,${gold}10 100%)`, padding: '28px 36px 20px', borderBottom: `2px solid ${gold}44`, textAlign: 'center' }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><Photo pi={pi} border={`3px solid ${gold}77`} fallbackSize={80} /></div>}
        <div style={{ fontSize: '26pt', fontWeight: 700, color: '#1C1917', letterSpacing: 1, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: gold, marginTop: 5, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
        <IdentityRow pi={pi} />
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '3px 16px', marginTop: 10, fontSize: '8pt', color: '#78716C' }}>
          {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
        </div>
      </div>
      <div style={{ padding: '6px 36px 30px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px 0', fontSize: '9.5pt', color: '#44403C', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── EDITORIAL (magazine serif, hairline rules, scarlet kicker) ────────────────
const EditorialTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1C1917', name: '#1C1917', body: '#292524', section: '#1C1917', link: '#9F1239' });
  const ds = useDesign(pi, { fontFamily: '"Playfair Display",Georgia,serif', fontSize: 9.5, lineHeight: 1.6 });
  const sec = t => <><div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '18px 0 8px' }}><span style={{ color: c.link, fontWeight: 700, fontSize: '9pt' }}>—</span><span style={{ fontWeight: 700, color: c.section, fontSize: '10pt', textTransform: sectionTransform(pi), letterSpacing: 2.5 }}>{t}</span><div style={{ flex: 1, height: 1, background: '#D6D3D1' }} /></div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '32px 38px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ borderTop: `3px solid ${c.section}`, borderBottom: `1px solid ${c.section}`, padding: '16px 2px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          {/* Guard: a white name (set by older color presets) is invisible on this light background */}
          <div style={{ fontSize: '25pt', fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.12, color: pi.nameColor && pi.nameColor.toLowerCase() !== '#ffffff' ? pi.nameColor : '#1C1917' }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: '10pt', fontStyle: 'italic', color: c.link, marginTop: 4 }}>{pi.jobTitle}</div>}
          <div style={{ fontSize: '8pt', color: '#78716C', marginTop: 7, fontFamily: '"Inter","Segoe UI",Arial,sans-serif' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('   ·   ')}</div>
          <IdentityRow pi={pi} />
        </div>
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: 0, fontSize: '9.5pt', color: '#44403C', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── SAGE (soft sage green, airy single column, dot markers) ───────────────────
const SageTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#52796F', body: '#2F3E36', section: '#3F5A50', link: '#52796F' });
  const ds = useDesign(pi, { fontFamily: '"Nunito","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const pageFillH = React.useContext(PageFillContext);
  const minH = pageFillH || (pi.pageSize === 'letter' ? 1056 : 1122);
  const sec = t => <><div style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '17px 0 8px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: c.section, flexShrink: 0 }} /><span style={{ fontWeight: 800, color: c.section, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 1.8 }}>{t}</span><div style={{ flex: 1, height: 1, background: c.section + '33' }} /></div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '30px 36px', background: pi.sidebarColor || '#F6F9F4', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        {/* Guard: a white name (set by older color presets) is invisible on this light background */}
        <div style={{ fontSize: '22pt', fontWeight: 800, letterSpacing: 0.3, color: pi.nameColor && pi.nameColor.toLowerCase() !== '#ffffff' ? pi.nameColor : '#2F3E36' }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ display: 'inline-block', fontSize: '9pt', fontWeight: 700, color: c.section, background: c.section + '16', borderRadius: 20, padding: '3px 14px', marginTop: 6 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: '8.5pt', color: '#6B7F72', marginTop: 8 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'About Me')}<p style={{ margin: 0, fontSize: '9.5pt', color: '#3D4F44' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── GRAPHITE (charcoal text, yellow highlighter accents) ──────────────────────
const GraphiteTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#FACC15', name: '#111827', body: '#1F2937', section: '#111827', link: '#4B5563' });
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const hl = c.accent; // highlighter color
  const marker = `linear-gradient(180deg, transparent 62%, ${hl}99 62%)`;
  const sec = t => <><div style={{ marginTop: 17, marginBottom: 9 }}><span style={{ fontWeight: 800, color: c.section, fontSize: '9.5pt', textTransform: sectionTransform(pi), letterSpacing: 2, background: marker, padding: '0 4px 1px 1px' }}>{t}</span></div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: '32px 36px', overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          {/* Guard: a white name (set by older color presets) is invisible on this light background */}
          <div style={{ fontSize: '23pt', fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.18 }}>
            <span style={{ background: marker, padding: '0 6px 2px 2px', color: pi.nameColor && pi.nameColor.toLowerCase() !== '#ffffff' ? pi.nameColor : '#111827' }}>{pi.fullName}</span>
          </div>
          {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: '#4B5563', marginTop: 6, fontWeight: 600 }}>{pi.jobTitle}</div>}
          <div style={{ fontSize: '8.5pt', color: '#6B7280', marginTop: 6 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  |  ')}</div>
          <IdentityRow pi={pi} />
        </div>
        <Photo pi={pi} border={`3px solid ${hl}`} fallbackSize={74} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Summary')}<p style={{ margin: 0, fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── MED PRO BLUE (ATS-friendly, blue header, USA nursing standard) ─────────────
const MedProBlueTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1E4D8C', body: '#1F2937', section: '#1E4D8C', link: '#1E6AB0' });
  const ds = useDesign(pi, { fontFamily: '"Calibri","Segoe UI",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ marginTop: 14, marginBottom: 7, borderBottom: `2px solid ${c.section}`, paddingBottom: 5 }}>
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 0.8 }}>{t}</span>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ background: c.accent, color: 'white', padding: `22px ${pad}px 18px` }}>
        <div style={{ fontSize: scaledPt(22, pi), fontWeight: 700, letterSpacing: 0.4 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(11, pi), opacity: 0.9, marginTop: 3 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: scaledPt(8.5, pi), marginTop: 8, opacity: 0.85 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  |  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      <div style={{ padding: `18px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── CLINICAL TEAL (two-column, teal sidebar, certifications left) ─────────────
const ClinicalTealTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0F766E', body: '#1F2937', section: '#0F766E', link: '#0F766E', sidebar: '#F0FDFA' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sidebarBg = c.sidebar;
  const sideSec = t => <div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(8.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1, borderBottom: `1.5px solid ${c.section}55`, paddingBottom: 5, marginTop: 16, marginBottom: 7 }}>{t}</div>;
  const sec = t => <><div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1, borderBottom: `2px solid ${c.section}`, paddingBottom: 5, marginTop: 14, marginBottom: 7 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '88'} /></>;
  const sidebarSections = new Set(['skills', 'certifications', 'languages', 'strengths']);
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 210, top: (i + 1) * defaultMinH, height: 56, background: sidebarBg, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 210, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      {/* Left sidebar */}
      <div style={{ width: 210, background: sidebarBg, padding: '24px 16px', flexShrink: 0, boxSizing: 'border-box', minWidth: 0 }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border={`3px solid ${c.section}44`} fallbackSize={80} /></div>}
        <div style={{ fontSize: scaledPt(14, pi), fontWeight: 800, color: c.section, lineHeight: 1.2 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(8.5, pi), color: '#374151', marginTop: 3, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: scaledPt(7.8, pi), color: '#374151', marginBottom: 4, overflowWrap: 'anywhere' }}>{v}</div>)}
        <IdentityRow pi={pi} />
        {getSectionOrder(d).filter(id => sidebarSections.has(id)).map(id => {
          switch (id) {
            case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'skills', 'Clinical Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 5, ...noBreak }}><div style={{ fontSize: scaledPt(7.8, pi), fontWeight: 700, color: '#1F2937' }}>{s.category}</div><div style={{ fontSize: scaledPt(7.8, pi), color: '#4B5563' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}</div> : null;
            case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'certifications', 'Licenses & Certifications'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), marginBottom: 5, ...noBreak }}><div style={{ fontWeight: 700, color: '#1F2937' }}>{cert.name}</div>{cert.issuer && <div style={{ color: '#4B5563' }}>{cert.issuer}</div>}{cert.date && <div style={{ color: '#6B7280' }}>{fmtDate(cert.date)}</div>}</div>)}</div> : null;
            case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'languages', 'Languages'))}{d.languages.map(l => <div key={l.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), color: '#374151', marginBottom: 3, ...noBreak }}>{l.language}{l.proficiency && <span style={{ color: '#6B7280' }}> · {l.proficiency}</span>}</div>)}</div> : null;
            case 'strengths': return d.strengths ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'strengths', 'Key Strengths'))}<div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <span key={i} style={{ background: c.section + '18', border: `1px solid ${c.section}44`, color: '#1F2937', borderRadius: 3, padding: '1px 6px', fontSize: scaledPt(7.5, pi) }}>{s}</span>)}</div></div> : null;
            default: return null;
          }
        })}
      </div>
      {/* Right content */}
      <div style={{ flex: 1, padding: '24px 22px 28px', minWidth: 0, background: 'white' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={[...sidebarSections]} />
      </div>
    </div>
  );
};

// ── NURSE RX (clean single-column, red rule headers, USA standard) ────────────
const NurseRxTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#B91C1C', body: '#1F2937', section: '#B91C1C', link: '#1D4ED8' });
  const ds = useDesign(pi, { fontFamily: '"Arial","Helvetica Neue",sans-serif', fontSize: 10, lineHeight: 1.55, padding: 36 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 15, marginBottom: 7 }}>
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), whiteSpace: 'nowrap', letterSpacing: 0.5 }}>{t}</span>
      <div style={{ flex: 1, height: 1.5, background: c.section + '55' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `28px ${pad}px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: 14, borderBottom: `3px solid ${c.section}`, paddingBottom: 12 }}>
        <div style={{ fontSize: scaledPt(22, pi), fontWeight: 700, color: c.body, letterSpacing: 0.3 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: c.section, fontWeight: 600, marginTop: 2 }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563', marginTop: 6 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── HEALTHCARE PRO (navy band header, gold accent rules, specialist-grade) ─────
const HealthcareProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  // accent = navy header; section/link = navy for headings; gold is fixed design colour, not user-controlled
  const c = useColors(pi, { accent: '#0C2340', body: '#1F2937', section: '#1A3A5C', link: '#1A5276' });
  const gold = '#A37E2C';
  const ds = useDesign(pi, { fontFamily: '"Georgia","Times New Roman",serif', fontSize: 10, lineHeight: 1.6, padding: 32 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      <div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 1.5, borderLeft: `4px solid ${gold}`, paddingLeft: 8, paddingBottom: 1 }}>{t}</div>
      <div style={{ height: 1, background: gold + '66', marginTop: 4 }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Navy header band */}
      <div style={{ background: c.accent, padding: `24px ${pad}px 18px`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <div style={{ fontSize: scaledPt(23, pi), fontWeight: 700, color: 'white', letterSpacing: 0.5 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: gold, marginTop: 3, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
        </div>
        {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`3px solid ${gold}`} fallbackSize={72} />}
      </div>
      {/* Gold contact bar */}
      <div style={{ background: gold, padding: `6px ${pad}px` }}>
        <div style={{ fontSize: scaledPt(8.5, pi), color: 'white' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  |  ')}</div>
      </div>
      <div style={{ padding: `18px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── CRITICAL CARE (dark navy sidebar, white panel, senior nurse/NP grade) ──────
const CriticalCareTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  // section: clear blue for right-panel headings (distinct from near-black body text)
  // link: teal-blue for company/school names on white background
  const c = useColors(pi, { accent: '#0C2340', body: '#1F2937', section: '#1E4D8C', link: '#1A5276' });
  // Use accent color (not sidebarColor) for dark sidebar bg — presets set sidebarColor to light
  // colors designed for light sidebars, which would make white text invisible here
  const navyBg = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const accentGold = '#C9A84C';
  // Sidebar headings: user can control via Link Color; fall back to gold for contrast on dark bg
  const sideHeadColor = pi.linkColor || accentGold;
  const sideSec = t => <div style={{ fontWeight: 700, color: sideHeadColor, fontSize: scaledPt(8, pi), textTransform: sectionTransform(pi), letterSpacing: 1.5, borderBottom: `1px solid ${sideHeadColor}66`, paddingBottom: 4, marginTop: 16, marginBottom: 7 }}>{t}</div>;
  const sec = t => <><div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1, borderBottom: `2px solid ${c.section}66`, paddingBottom: 5, marginTop: 14, marginBottom: 7 }}>{t}</div><SectionDividerLine pi={pi} color={c.section + '44'} /></>;
  const sidebarSections = new Set(['skills', 'certifications', 'languages', 'strengths', 'achievements']);
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 215, top: (i + 1) * defaultMinH, height: 56, background: navyBg, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 215, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      {/* Dark navy sidebar */}
      <div style={{ width: 215, background: navyBg, padding: '26px 16px 28px', flexShrink: 0, boxSizing: 'border-box', minWidth: 0 }}>
        {pi.photo && pi.showPhoto !== false && (
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <div style={{ display: 'inline-block', borderRadius: '50%', border: `3px solid ${sideHeadColor}`, overflow: 'hidden' }}>
              <Photo pi={pi} fallbackSize={80} />
            </div>
          </div>
        )}
        <div style={{ fontSize: scaledPt(15, pi), fontWeight: 800, color: 'white', lineHeight: 1.2 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(8.5, pi), color: sideHeadColor, marginTop: 5, fontWeight: 600, lineHeight: 1.3 }}>{pi.jobTitle}</div>}
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: scaledPt(7.8, pi), color: '#CBD5E1', marginBottom: 5, overflowWrap: 'anywhere', lineHeight: 1.4 }}>{v}</div>)}
        <IdentityRow pi={pi} />
        {getSectionOrder(d).filter(id => sidebarSections.has(id)).map(id => {
          switch (id) {
            case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'skills', 'Clinical Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 6, ...noBreak }}><div style={{ fontSize: scaledPt(7.8, pi), fontWeight: 700, color: '#E2E8F0' }}>{s.category}</div><div style={{ fontSize: scaledPt(7.8, pi), color: '#94A3B8' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={sideHeadColor} /> : s.items}</div></div>)}</div> : null;
            case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'certifications', 'Licenses & Certs'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), marginBottom: 6, ...noBreak }}><div style={{ fontWeight: 700, color: '#E2E8F0' }}>{cert.name}</div>{cert.issuer && <div style={{ color: '#94A3B8' }}>{cert.issuer}</div>}{cert.date && <div style={{ color: '#7A8FA6' }}>{fmtDate(cert.date)}</div>}</div>)}</div> : null;
            case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'languages', 'Languages'))}{d.languages.map(l => <div key={l.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), color: '#CBD5E1', marginBottom: 4, ...noBreak }}>{l.language}{l.proficiency && <span style={{ color: '#94A3B8' }}> · {l.proficiency}</span>}</div>)}</div> : null;
            case 'strengths': return d.strengths ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'strengths', 'Core Competencies'))}<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <span key={i} style={{ background: 'rgba(255,255,255,0.1)', border: `1px solid ${sideHeadColor}66`, color: '#E2E8F0', borderRadius: 4, padding: '2px 7px', fontSize: scaledPt(7.5, pi) }}>{s}</span>)}</div></div> : null;
            case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'achievements', 'Achievements'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), color: '#CBD5E1', marginBottom: 5, ...noBreak }}><span style={{ fontWeight: 700, color: '#E2E8F0' }}>{a.title}</span>{a.description && <span style={{ color: '#94A3B8' }}>: {a.description}</span>}</div>)}</div> : null;
            default: return null;
          }
        })}
      </div>
      {/* White right content */}
      <div style={{ flex: 1, padding: '26px 26px 28px', minWidth: 0, background: 'white' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 6px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={[...sidebarSections]} />
      </div>
    </div>
  );
};

// ── REALTY PRO (centered letterhead, serif, double-rule header, no color block) ──
const RealtyProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1C3A5E', body: '#1F2937', section: '#1C3A5E', link: '#1C3A5E' });
  const ds = useDesign(pi, { fontFamily: '"Georgia","Times New Roman",serif', fontSize: 10, lineHeight: 1.6, padding: 36 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, marginBottom: 8 }}>
      <div style={{ flex: 1, height: 1, background: c.section + '55' }} />
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 2, whiteSpace: 'nowrap' }}>{t}</span>
      <div style={{ flex: 1, height: 1, background: c.section + '55' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `32px ${pad}px 28px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Centered letterhead — no colored block, pure white background */}
      <div style={{ textAlign: 'center', paddingBottom: 16, marginBottom: 16, borderTop: `2px solid ${c.section}`, borderBottom: `1px solid ${c.section}55`, paddingTop: 14 }}>
        <div style={{ fontSize: scaledPt(26, pi), fontWeight: 700, color: c.section, letterSpacing: 1, fontStyle: 'italic' }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(9.5, pi), color: '#4B5563', marginTop: 5, letterSpacing: 2, textTransform: 'uppercase' }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280', marginTop: 8 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '4px 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── ESTATE SIDEBAR (RIGHT-rail sidebar — experience left, contact/skills right) ──
const EstateSidebarTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#2D5A27', body: '#1F2937', section: '#2D5A27', link: '#2D5A27', sidebar: '#F3F7F2' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sidebarBg = c.sidebar;
  const SW = 195;
  const sideSec = t => <div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(8.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1, borderBottom: `1.5px solid ${c.section}55`, paddingBottom: 5, marginTop: 14, marginBottom: 6 }}>{t}</div>;
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, marginBottom: 7 }}>
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), whiteSpace: 'nowrap', letterSpacing: 0.5 }}>{t}</span>
      <div style={{ flex: 1, height: 1.5, background: c.section + '44' }} />
    </div>
  );
  const sidebarSections = new Set(['skills', 'certifications', 'languages', 'strengths']);
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', right: 0, width: SW, top: (i + 1) * defaultMinH, height: 56, background: sidebarBg, zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 0, right: SW, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      {/* LEFT: main content */}
      <div style={{ flex: 1, padding: '24px 20px 28px', minWidth: 0, background: 'white' }}>
        <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: `2.5px solid ${c.section}` }}>
          <div style={{ fontSize: scaledPt(22, pi), fontWeight: 700, color: c.section }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: '#4B5563', marginTop: 2 }}>{pi.jobTitle}</div>}
        </div>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={[...sidebarSections]} />
      </div>
      {/* RIGHT sidebar */}
      <div style={{ width: SW, background: sidebarBg, padding: '24px 14px 28px', flexShrink: 0, boxSizing: 'border-box', minWidth: 0, borderLeft: `1px solid ${c.section}22` }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 12 }}><Photo pi={pi} border={`3px solid ${c.section}44`} fallbackSize={80} /></div>}
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => <div key={i} style={{ fontSize: scaledPt(7.8, pi), color: '#374151', marginBottom: 4, overflowWrap: 'anywhere' }}>{v}</div>)}
        <IdentityRow pi={pi} />
        {getSectionOrder(d).filter(id => sidebarSections.has(id)).map(id => {
          switch (id) {
            case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'skills', 'Core Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 5, ...noBreak }}><div style={{ fontSize: scaledPt(7.8, pi), fontWeight: 700, color: '#1F2937' }}>{s.category}</div><div style={{ fontSize: scaledPt(7.8, pi), color: '#4B5563' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}</div> : null;
            case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'certifications', 'Licenses & Certs'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), marginBottom: 5, ...noBreak }}><div style={{ fontWeight: 700, color: '#1F2937' }}>{cert.name}</div>{cert.issuer && <div style={{ color: '#4B5563' }}>{cert.issuer}</div>}{cert.date && <div style={{ color: '#6B7280' }}>{fmtDate(cert.date)}</div>}</div>)}</div> : null;
            case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'languages', 'Languages'))}{d.languages.map(l => <div key={l.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), color: '#374151', marginBottom: 3, ...noBreak }}>{l.language}{l.proficiency && <span style={{ color: '#6B7280' }}> · {l.proficiency}</span>}</div>)}</div> : null;
            case 'strengths': return d.strengths ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'strengths', 'Key Strengths'))}<div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <span key={i} style={{ background: c.section + '18', border: `1px solid ${c.section}44`, color: '#1F2937', borderRadius: 3, padding: '1px 5px', fontSize: scaledPt(7.5, pi) }}>{s}</span>)}</div></div> : null;
            default: return null;
          }
        })}
      </div>
    </div>
  );
};

// ── LUXURY BROKER (split hero header: dark left panel + light right panel) ────────
const LuxuryBrokerTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#3B2A1A', body: '#1F2937', section: '#3B2A1A', link: '#5C3D1E' });
  const warm = '#C9A96E';
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 28 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 8, paddingBottom: 6, borderBottom: `3px solid ${warm}` }}>
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 1.2 }}>{t}</span>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Split hero: left dark panel + right warm panel */}
      <div style={{ display: 'flex', minHeight: 130 }}>
        <div style={{ background: c.accent, width: '40%', padding: '22px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
          {pi.photo && pi.showPhoto !== false && <div style={{ marginBottom: 8 }}><Photo pi={pi} fallbackSize={64} border={`2px solid ${warm}`} /></div>}
          <div style={{ fontSize: scaledPt(18, pi), fontWeight: 700, color: 'white', lineHeight: 1.2 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(9, pi), color: warm, fontWeight: 600, marginTop: 2 }}>{pi.jobTitle}</div>}
        </div>
        <div style={{ flex: 1, background: '#FBF7F0', padding: '22px 20px', borderLeft: `3px solid ${warm}`, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
            <div key={i} style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563', marginBottom: 4 }}>{v}</div>
          ))}
          <IdentityRow pi={pi} />
        </div>
      </div>
      {/* Body: single column with thick-bottom-rule section headers */}
      <div style={{ padding: `14px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── RE CORPORATE (full-width band section headers, name-left contact-right) ──────
const RECorporateTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0F2940', body: '#1F2937', section: '#0F2940', link: '#1A5276' });
  const ds = useDesign(pi, { fontFamily: '"Calibri","Segoe UI",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  // Section header spans full width by cancelling the parent padding with negative margins
  const sec = t => (
    <div style={{ background: c.accent, color: 'white', padding: `5px ${pad}px`, margin: `14px -${pad}px 9px`, fontWeight: 700, fontSize: scaledPt(9, pi), textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{t}</div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `28px ${pad}px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Header: name left, contact details right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 12, marginBottom: 4, borderBottom: `3px solid ${c.accent}` }}>
        <div>
          <div style={{ fontSize: scaledPt(24, pi), fontWeight: 700, color: c.accent, letterSpacing: 0.3 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: '#4B5563', marginTop: 2 }}>{pi.jobTitle}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          {pi.photo && pi.showPhoto !== false && <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'flex-end' }}><Photo pi={pi} fallbackSize={58} /></div>}
          <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.email, pi.phone].filter(Boolean).join('  |  ')}</div>
          <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.location, pi.linkedin, pi.website].filter(Boolean).join('  |  ')}</div>
          <IdentityRow pi={pi} />
        </div>
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── PROPERTY EXEC (left accent bar via border-left, dot+rule section headers) ─────
const PropertyExecTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1A3A52', body: '#1F2937', section: '#1A3A52', link: '#1A3A52' });
  const BAR = 5;
  const ds = useDesign(pi, { fontFamily: '"Helvetica Neue","Arial",sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  // Accent bar runs the full page height via border-left on the outer wrapper
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 15, marginBottom: 7 }}>
      <div style={{ width: 9, height: 9, background: c.accent, borderRadius: '50%', flexShrink: 0 }} />
      <span style={{ fontWeight: 700, color: c.accent, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 0.8 }}>{t}</span>
      <div style={{ flex: 1, height: 1, background: c.accent + '33' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box', borderLeft: `${BAR}px solid ${c.accent}` }}>
      <div style={{ padding: `24px ${pad}px 16px`, borderBottom: `1px solid ${c.accent}22` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: scaledPt(24, pi), fontWeight: 700, color: c.accent, letterSpacing: 0.3 }}>{pi.fullName}</div>
            {pi.jobTitle && <div style={{ fontSize: scaledPt(11, pi), color: '#4B5563', marginTop: 3 }}>{pi.jobTitle}</div>}
            <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280', marginTop: 6 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
            <IdentityRow pi={pi} />
          </div>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${c.accent}44`} fallbackSize={68} />}
        </div>
      </div>
      <div style={{ padding: `14px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── SHIELD PRO (double-stripe header sandwiching white name block, tinted box sec) ─
const ShieldProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1B3A6B', body: '#1F2937', section: '#1B3A6B', link: '#1B3A6B' });
  const steel = '#4A7FC1';
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Helvetica Neue",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ marginTop: 15, marginBottom: 8, background: c.accent + '12', borderLeft: `5px solid ${c.accent}`, padding: '5px 10px' }}>
      <span style={{ fontWeight: 700, color: c.accent, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1 }}>{t}</span>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ height: 8, background: c.accent }} />
      <div style={{ padding: `18px ${pad}px 14px`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: scaledPt(24, pi), fontWeight: 700, color: c.accent, letterSpacing: 0.3 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: steel, marginTop: 2, fontWeight: 600 }}>{pi.jobTitle}</div>}
          <IdentityRow pi={pi} />
        </div>
        <div style={{ textAlign: 'right' }}>
          {pi.photo && pi.showPhoto !== false && <div style={{ marginBottom: 5, display: 'flex', justifyContent: 'flex-end' }}><Photo pi={pi} fallbackSize={62} /></div>}
          <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.email, pi.phone].filter(Boolean).join('  |  ')}</div>
          <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.location, pi.linkedin, pi.website].filter(Boolean).join('  |  ')}</div>
        </div>
      </div>
      <div style={{ height: 4, background: steel }} />
      <div style={{ padding: `16px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── PATROL BLUE (bordered ID-badge header, thin bar + silver-rule sections) ───────
const PatrolBlueTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0D2461', body: '#1F2937', section: '#0D2461', link: '#0D2461' });
  const ds = useDesign(pi, { fontFamily: '"Arial","Helvetica Neue",sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 15, marginBottom: 7 }}>
      <div style={{ width: 3, height: 14, background: c.accent, borderRadius: 2, flexShrink: 0 }} />
      <span style={{ fontWeight: 700, color: c.accent, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 0.8 }}>{t}</span>
      <div style={{ flex: 1, height: 1, background: '#94A3B8' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `${pad}px`, paddingBottom: 28, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Bordered badge/ID-card header */}
      <div style={{ border: `2px solid ${c.accent}`, borderRadius: 4, padding: '16px 18px', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: scaledPt(22, pi), fontWeight: 700, color: c.accent, letterSpacing: 0.4 }}>{pi.fullName}</div>
            {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: '#374151', marginTop: 3 }}>{pi.jobTitle}</div>}
          </div>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${c.accent}44`} fallbackSize={64} />}
        </div>
      </div>
      <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563', paddingBottom: 8, marginBottom: 4, borderBottom: `1px solid ${c.accent}33` }}>
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── SENTINEL DARK (dark full-width header + two-column body: sidebar left) ────────
const SentinelDarkTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0A1628', body: '#1F2937', section: '#1E3A5C', link: '#1E3A5C', sidebar: '#EEF2F7' });
  const amber = '#D4A017';
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.55 });
  const sidebarBg = c.sidebar;
  const sideSec = t => <div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(8.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1, borderBottom: `1.5px solid ${c.section}44`, paddingBottom: 5, marginTop: 14, marginBottom: 6 }}>{t}</div>;
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, marginBottom: 7 }}>
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 0.8, whiteSpace: 'nowrap' }}>{t}</span>
      <div style={{ flex: 1, height: 2, background: c.section + '33' }} />
    </div>
  );
  const sidebarSections = new Set(['skills', 'certifications', 'languages', 'strengths', 'achievements']);
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Dark full-width header — name + contact all in one dark band */}
      <div style={{ background: c.accent, padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: scaledPt(22, pi), fontWeight: 700, color: 'white', letterSpacing: 0.4 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10, pi), color: amber, marginTop: 3, fontWeight: 600 }}>{pi.jobTitle}</div>}
          <div style={{ fontSize: scaledPt(8.5, pi), color: 'rgba(255,255,255,0.75)', marginTop: 6 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
          <IdentityRow pi={pi} />
        </div>
        {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${amber}`} fallbackSize={62} />}
      </div>
      {/* Two-column body: light sidebar LEFT + white RIGHT */}
      <div style={{ display: 'flex' }}>
        <div style={{ width: 188, background: sidebarBg, padding: '16px 14px 28px', flexShrink: 0, boxSizing: 'border-box', borderRight: `1px solid ${c.section}22` }}>
          {getSectionOrder(d).filter(id => sidebarSections.has(id)).map(id => {
            switch (id) {
              case 'skills': return d.skills?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'skills', 'Skills'))}{d.skills.map(s => <div key={s.id} data-resume-entry style={{ marginBottom: 5, ...noBreak }}><div style={{ fontSize: scaledPt(7.8, pi), fontWeight: 700, color: '#1F2937' }}>{s.category}</div><div style={{ fontSize: scaledPt(7.8, pi), color: '#4B5563' }}>{pi.skillsAsTags ? <SkillTags items={s.items} color={c.section} /> : s.items}</div></div>)}</div> : null;
              case 'certifications': return d.certifications?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'certifications', 'Licenses & Certs'))}{d.certifications.map(cert => <div key={cert.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), marginBottom: 5, ...noBreak }}><div style={{ fontWeight: 700, color: '#1F2937' }}>{cert.name}</div>{cert.issuer && <div style={{ color: '#4B5563' }}>{cert.issuer}</div>}{cert.date && <div style={{ color: '#6B7280' }}>{fmtDate(cert.date)}</div>}</div>)}</div> : null;
              case 'languages': return d.languages?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'languages', 'Languages'))}{d.languages.map(l => <div key={l.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), color: '#374151', marginBottom: 3, ...noBreak }}>{l.language}{l.proficiency && <span style={{ color: '#6B7280' }}> · {l.proficiency}</span>}</div>)}</div> : null;
              case 'strengths': return d.strengths ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'strengths', 'Core Competencies'))}<div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => <span key={i} style={{ background: c.section + '14', border: `1px solid ${c.section}44`, color: '#1F2937', borderRadius: 3, padding: '1px 5px', fontSize: scaledPt(7.5, pi) }}>{s}</span>)}</div></div> : null;
              case 'achievements': return d.achievements?.length > 0 ? <div key={id} data-resume-section style={{ ...noBreak }}>{sideSec(secT(d, 'achievements', 'Awards'))}{d.achievements.map(a => <div key={a.id} data-resume-entry style={{ fontSize: scaledPt(7.8, pi), color: '#374151', marginBottom: 5, ...noBreak }}><span style={{ fontWeight: 700, color: '#1F2937' }}>{a.title}</span>{a.description && <div style={{ color: '#4B5563' }}>{a.description}</div>}</div>)}</div> : null;
              default: return null;
            }
          })}
        </div>
        <div style={{ flex: 1, padding: '16px 18px 28px', minWidth: 0 }}>
          {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
          <StdSections d={d} pi={pi} c={c} sec={sec} skip={[...sidebarSections]} />
        </div>
      </div>
    </div>
  );
};

// ── GUARDIAN GRID (half-pill section labels from left edge, forest green) ─────────
const GuardianGridTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1E3A1E', body: '#1F2937', section: '#1E3A1E', link: '#1E3A1E' });
  const ds = useDesign(pi, { fontFamily: '"Calibri","Segoe UI",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  // Half-pill: background extends to left edge via negative margin, rounded on right
  const sec = t => (
    <div style={{ background: c.accent, color: 'white', padding: `4px ${pad}px`, margin: `15px -${pad}px 8px`, borderRadius: '0 20px 20px 0', fontWeight: 700, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1, display: 'block' }}>{t}</div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `28px ${pad}px 28px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ paddingBottom: 14, marginBottom: 4, borderBottom: `1px solid ${c.accent}33` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: scaledPt(24, pi), fontWeight: 700, color: c.accent, letterSpacing: 0.4 }}>{pi.fullName}</div>
            {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: '#4B5563', marginTop: 2 }}>{pi.jobTitle}</div>}
            <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280', marginTop: 7 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
            <IdentityRow pi={pi} />
          </div>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${c.accent}44`} fallbackSize={66} />}
        </div>
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── TACTICAL CLEAN (olive/military, inline-width colored section title blocks) ────
const TacticalCleanTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#4A5C2A', body: '#1F2937', section: '#4A5C2A', link: '#4A5C2A' });
  const ds = useDesign(pi, { fontFamily: '"Helvetica Neue","Arial",sans-serif', fontSize: 10, lineHeight: 1.55, padding: 34 });
  const pad = ds.padding;
  // Colored block only as wide as the text — distinctly different from full-width bands
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      <span style={{ background: c.accent, color: 'white', padding: '3px 10px', fontSize: scaledPt(9, pi), fontWeight: 700, textTransform: sectionTransform(pi), letterSpacing: 1.5, display: 'inline-block' }}>{t}</span>
      <div style={{ height: 1, background: c.accent + '44', marginTop: 4 }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `28px ${pad}px 28px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ paddingBottom: 14, marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            {/* Name in uppercase — distinctive military/tactical style */}
            <div style={{ fontSize: scaledPt(22, pi), fontWeight: 700, color: c.body, letterSpacing: 2, textTransform: 'uppercase' }}>{pi.fullName}</div>
            {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: c.accent, marginTop: 4, fontWeight: 600 }}>{pi.jobTitle}</div>}
            {/* Short accent rule below title */}
            <div style={{ width: 48, height: 3, background: c.accent, marginTop: 8, marginBottom: 8 }} />
            <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
            <IdentityRow pi={pi} />
          </div>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${c.accent}66`} fallbackSize={68} />}
        </div>
        <div style={{ height: 2, background: c.accent, marginTop: 14 }} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── EXEC SUITE (split header: dark left=contact, white right=summary panel) ───────
const ExecSuiteTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1A2E4A', body: '#1F2937', section: '#1A2E4A', link: '#1A5276' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 28 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ marginTop: 15, marginBottom: 8, background: c.accent + '0E', border: `1px solid ${c.accent}22`, borderRadius: 2, padding: '5px 10px' }}>
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1 }}>{t}</span>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Split header: LEFT dark panel = contact info; RIGHT white panel = professional summary */}
      <div style={{ display: 'flex', minHeight: 148 }}>
        <div style={{ background: c.accent, width: '42%', padding: '22px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: scaledPt(20, pi), fontWeight: 700, color: 'white', lineHeight: 1.2 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(9.5, pi), color: '#93B8D8', marginTop: 5 }}>{pi.jobTitle}</div>}
          <div style={{ marginTop: 10 }}>
            {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
              <div key={i} style={{ fontSize: scaledPt(8, pi), color: 'rgba(255,255,255,0.72)', marginBottom: 3 }}>{v}</div>
            ))}
          </div>
          <IdentityRow pi={pi} />
        </div>
        <div style={{ flex: 1, padding: '22px 20px', borderLeft: `2px solid ${c.accent}22`, display: 'flex', alignItems: 'center' }}>
          {pi.photo && pi.showPhoto !== false && <div style={{ marginRight: 14, flexShrink: 0 }}><Photo pi={pi} fallbackSize={60} border={`2px solid ${c.accent}33`} /></div>}
          {pi.summary && <p style={{ margin: 0, fontSize: scaledPt(9.5, pi), color: '#374151', fontStyle: 'italic', lineHeight: 1.6 }}>{pi.summary}</p>}
        </div>
      </div>
      {/* Body — summary already shown in header panel, skip manual render */}
      <div style={{ padding: `14px ${pad}px 28px` }}>
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── BOARDROOM (three stacked header bars, numbered 01/02/03 section headers) ──────
const BoardroomTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#14213D', body: '#1F2937', section: '#14213D', link: '#1A3A6B' });
  const mid = '#2D4A7A';
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Helvetica Neue",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  let n = 0;
  const sec = t => {
    n++;
    const label = n < 10 ? `0${n}` : `${n}`;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, marginBottom: 8 }}>
        <span style={{ fontSize: scaledPt(17, pi), fontWeight: 900, color: c.accent + '2E', lineHeight: 1, flexShrink: 0, minWidth: 28 }}>{label}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 1 }}>{t}</div>
          <div style={{ height: 1.5, background: c.section + '55', marginTop: 3 }} />
        </div>
      </div>
    );
  };
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Bar 1: dark — name only */}
      <div style={{ background: c.accent, padding: `16px ${pad}px 12px`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: scaledPt(24, pi), fontWeight: 700, color: 'white', letterSpacing: 0.4 }}>{pi.fullName}</div>
        {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border="2px solid rgba(255,255,255,0.35)" fallbackSize={56} />}
      </div>
      {/* Bar 2: medium — title only */}
      <div style={{ background: mid, padding: `7px ${pad}px` }}>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>{pi.jobTitle}</div>}
      </div>
      {/* Bar 3: light — contact row */}
      <div style={{ background: '#EEF2F7', padding: `7px ${pad}px`, borderBottom: `2px solid ${c.accent}` }}>
        <div style={{ fontSize: scaledPt(8.5, pi), color: '#475569' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      <div style={{ padding: `14px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── BIZDEV PRO (name/contact inline, featured summary box, ─── text ─── sections) ─
const BizdevProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1D4ED8', body: '#1F2937', section: '#1E40AF', link: '#1D4ED8' });
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, marginBottom: 8 }}>
      <div style={{ flex: 1, height: 1, background: c.section + '44' }} />
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 1.5, whiteSpace: 'nowrap' }}>{t}</span>
      <div style={{ flex: 1, height: 1, background: c.section + '44' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `28px ${pad}px 28px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Inline header: name + title left, contact + photo right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: scaledPt(25, pi), fontWeight: 700, color: c.section, letterSpacing: 0.3 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: '#4B5563', marginTop: 2 }}>{pi.jobTitle}</div>}
          <IdentityRow pi={pi} />
        </div>
        <div style={{ textAlign: 'right' }}>
          {pi.photo && pi.showPhoto !== false && <div style={{ marginBottom: 5, display: 'flex', justifyContent: 'flex-end' }}><Photo pi={pi} fallbackSize={60} /></div>}
          <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.email, pi.phone].filter(Boolean).join('  |  ')}</div>
          <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.location, pi.linkedin, pi.website].filter(Boolean).join('  |  ')}</div>
        </div>
      </div>
      <div style={{ height: 2, background: c.section, marginBottom: 14 }} />
      {/* Featured summary box — unique treatment, not a standard sec() header */}
      {pi.summary && (
        <div style={{ background: c.section + '09', border: `1px solid ${c.section}30`, borderLeft: `4px solid ${c.section}`, padding: '11px 14px', marginBottom: 14, borderRadius: '0 4px 4px 0' }}>
          <div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(8.5, pi), textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>{pi.summaryTitle || 'Executive Profile'}</div>
          <p style={{ margin: 0, fontSize: scaledPt(9.5, pi), color: '#374151', lineHeight: 1.6 }}>{pi.summary}</p>
        </div>
      )}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── STRATEGY BLUE (centered name + double rule, faded counter number RIGHT in sec) ─
const StrategyBlueTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0039A6', body: '#1F2937', section: '#0039A6', link: '#0039A6' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Calibri",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 36 });
  const pad = ds.padding;
  let n = 0;
  const sec = t => {
    n++;
    const label = n < 10 ? `0${n}` : `${n}`;
    return (
      <div style={{ position: 'relative', marginTop: 18, marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${c.section}` }}>
        <span style={{ position: 'absolute', right: 0, top: -2, fontSize: scaledPt(22, pi), fontWeight: 900, color: c.accent + '1A', lineHeight: 1 }}>{label}</span>
        <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 1 }}>{t}</span>
      </div>
    );
  };
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Centered name + double rule (thick then thin) */}
      <div style={{ textAlign: 'center', padding: `28px ${pad}px 16px` }}>
        <div style={{ fontSize: scaledPt(28, pi), fontWeight: 700, color: c.section, letterSpacing: 1 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(10.5, pi), color: '#4B5563', marginTop: 5, letterSpacing: 0.5 }}>{pi.jobTitle}</div>}
        <div style={{ margin: '12px auto 10px', width: '65%' }}>
          <div style={{ height: 2.5, background: c.section }} />
          <div style={{ height: 1, background: c.section + '55', marginTop: 3 }} />
        </div>
        <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
        {pi.photo && pi.showPhoto !== false && <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}><Photo pi={pi} border={`2px solid ${c.section}44`} fallbackSize={64} /></div>}
      </div>
      <div style={{ padding: `0 ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── C-SUITE (oversized 30pt name, wide right margin, square-dot sections, serif) ──
const CSuiteTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0F1F3D', body: '#1F2937', section: '#0F1F3D', link: '#0F1F3D' });
  const ds = useDesign(pi, { fontFamily: '"Georgia","Times New Roman",serif', fontSize: 10, lineHeight: 1.6, padding: 38 });
  const pad = ds.padding;
  const rightExtra = 62; // wide right margin — executive whitespace on right side
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, marginBottom: 8 }}>
      <div style={{ width: 8, height: 8, background: c.accent, flexShrink: 0 }} />
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 1 }}>{t}</span>
      <div style={{ flex: 1, height: 1, background: c.section + '44' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `28px ${pad + rightExtra}px 28px ${pad}px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ flex: 1, paddingRight: 16 }}>
          {/* Oversized name — commanding presence at 30pt */}
          <div style={{ fontSize: scaledPt(30, pi), fontWeight: 700, color: c.section, lineHeight: 1.1, letterSpacing: 0.3 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(11, pi), color: '#6B7280', marginTop: 6, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
          <div style={{ height: 1, background: c.section + '33', marginTop: 10, marginBottom: 8 }} />
          <div style={{ fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
          <IdentityRow pi={pi} />
        </div>
        {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${c.section}44`} fallbackSize={72} />}
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'Professional Summary')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── BLOOM SIDEBAR (rose RIGHT sidebar, diamond-bullet sections in left main) ────────
const BloomSidebarTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#BE185D', body: '#1F2937', section: '#9D1559', link: '#BE185D' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 24 });
  const pad = ds.padding;
  const sideW = 182;
  const sidebarBg = pi.sidebarColor || '#FFF0F6';
  const order = getSectionOrder(d);
  const sideIds = order.filter(id => ['skills', 'languages', 'certifications'].includes(id));
  const sec = t => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 14, marginBottom: 7 }}>
      <div style={{ width: 8, height: 8, background: c.accent, transform: 'rotate(45deg)', flexShrink: 0 }} />
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(9.5, pi), textTransform: sectionTransform(pi), letterSpacing: 0.8 }}>{t}</span>
      <div style={{ flex: 1, height: 1, background: c.accent + '33' }} />
    </div>
  );
  return (
    <div style={{ display: 'flex', fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Main LEFT */}
      <div style={{ flex: 1, padding: `${pad}px 18px ${pad}px ${pad}px` }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: scaledPt(23, pi), fontWeight: 700, color: c.section, lineHeight: 1.15 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10, pi), color: '#9D4E7B', marginTop: 3 }}>{pi.jobTitle}</div>}
          <div style={{ height: 2, background: `linear-gradient(to right, ${c.accent}, transparent)`, marginTop: 10 }} />
        </div>
        {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={sideIds} />
      </div>
      {/* Sidebar RIGHT */}
      <div style={{ width: sideW, background: sidebarBg, padding: `${pad}px 14px`, flexShrink: 0 }}>
        {pi.photo && pi.showPhoto !== false && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <Photo pi={pi} border={`3px solid ${c.accent}55`} fallbackSize={68} />
          </div>
        )}
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
          <div key={i} style={{ fontSize: scaledPt(8, pi), color: '#6B4057', marginBottom: 4, wordBreak: 'break-all' }}>{v}</div>
        ))}
        <IdentityRow pi={pi} />
        {sideIds.map(id => {
          const items = d[id] || [];
          if (!items.length) return null;
          return (
            <div key={id} style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, fontSize: scaledPt(8.5, pi), color: c.section, textTransform: sectionTransform(pi), letterSpacing: 0.8, marginBottom: 5, paddingBottom: 5, borderBottom: `1.5px solid ${c.accent}44` }}>{secT(d, id, id)}</div>
              {id === 'skills' && pi.skillsAsTags
                ? <SkillTags items={items} color={c.accent} />
                : <BulletList items={items.map(it => it.name || it.skill || it)} color={c.accent} pi={pi} />
              }
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── SALON CHIC (editorial black & gold, gold left+bottom border section titles) ─────
const SalonChicTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#C9A84C', body: '#1F2937', section: '#8B6914', link: '#C9A84C' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Helvetica Neue",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  const gold = c.accent;
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 8, borderLeft: `3px solid ${gold}`, borderBottom: `1px solid ${gold}33`, paddingLeft: 10, paddingBottom: 4 }}>
      <span style={{ fontWeight: 700, color: gold, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{t}</span>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Dark full-width glamour header */}
      <div style={{ background: '#0D0D0D', padding: `20px ${pad}px 18px`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: scaledPt(27, pi), fontWeight: 700, color: gold, lineHeight: 1.1, letterSpacing: 0.5 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10, pi), color: 'rgba(255,255,255,0.65)', marginTop: 5, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
          <div style={{ marginTop: 8, fontSize: scaledPt(8.5, pi), color: 'rgba(255,255,255,0.45)' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
          <IdentityRow pi={pi} />
        </div>
        {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${gold}`} fallbackSize={64} />}
      </div>
      <div style={{ height: 2, background: gold }} />
      <div style={{ padding: `14px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── SPA ZEN (centered minimalist serif, ornament row, centered rule sections) ───────
const SpaZenTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#4A7C59', body: '#1F2937', section: '#2D5C3A', link: '#4A7C59' });
  const ds = useDesign(pi, { fontFamily: '"Georgia","Palatino Linotype",serif', fontSize: 10, lineHeight: 1.6, padding: 36 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ textAlign: 'center', marginTop: 18, marginBottom: 10 }}>
      <div style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 2.5 }}>{t}</div>
      <div style={{ width: 36, height: 2, background: c.accent, margin: '5px auto 0' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, padding: `32px ${pad}px 28px`, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><Photo pi={pi} border={`2px solid ${c.accent}55`} fallbackSize={72} /></div>}
        <div style={{ fontSize: scaledPt(26, pi), fontWeight: 300, color: c.section, letterSpacing: 4 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(10, pi), color: '#6B7280', marginTop: 4, fontStyle: 'italic', letterSpacing: 1 }}>{pi.jobTitle}</div>}
        {/* Ornament divider row */}
        <div style={{ color: c.accent, fontSize: scaledPt(12, pi), margin: '10px 0 8px', letterSpacing: 5 }}>· · ◆ · ·</div>
        <div style={{ fontSize: scaledPt(8.5, pi), color: '#6B7280' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      {pi.summary && <>{sec(pi.summaryTitle || 'About')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'center' }}>{pi.summary}</p></>}
      <StdSections d={d} pi={pi} c={c} sec={sec} />
    </div>
  );
};

// ── GLOW PRO (warm gradient header, centered photo, dashed underline sections) ──────
const GlowProTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#C2410C', body: '#1F2937', section: '#9A3412', link: '#C2410C' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Inter",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 8, paddingBottom: 5, borderBottom: `2px dashed ${c.accent}` }}>
      <span style={{ fontWeight: 700, color: c.section, fontSize: scaledPt(10, pi), textTransform: sectionTransform(pi), letterSpacing: 0.8 }}>{t}</span>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Warm gradient full-width header */}
      <div style={{ background: `linear-gradient(135deg, ${c.accent} 0%, #F59E6A 100%)`, padding: `24px ${pad}px 20px`, textAlign: 'center' }}>
        {pi.photo && pi.showPhoto !== false && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <Photo pi={pi} border="3px solid rgba(255,255,255,0.7)" fallbackSize={76} />
          </div>
        )}
        <div style={{ fontSize: scaledPt(26, pi), fontWeight: 700, color: 'white', lineHeight: 1.15, letterSpacing: 0.3 }}>{pi.fullName}</div>
        {pi.jobTitle && <div style={{ fontSize: scaledPt(10, pi), color: 'rgba(255,255,255,0.85)', marginTop: 5, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
        <div style={{ fontSize: scaledPt(8.5, pi), color: 'rgba(255,255,255,0.72)', marginTop: 8 }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
        <IdentityRow pi={pi} />
      </div>
      <div style={{ padding: `14px ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── BEAUTY BADGE (thin top accent strip, outline-only pill section labels) ──────────
const BeautyBadgeTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#9D4E7B', body: '#1F2937', section: '#7B2D5E', link: '#9D4E7B' });
  const ds = useDesign(pi, { fontFamily: '"Segoe UI","Helvetica Neue",Arial,sans-serif', fontSize: 10, lineHeight: 1.55, padding: 32 });
  const pad = ds.padding;
  const sec = t => (
    <div style={{ marginTop: 16, marginBottom: 9 }}>
      <span style={{ display: 'inline-block', border: `1.5px solid ${c.accent}`, borderRadius: 20, padding: '3px 14px', fontWeight: 700, color: c.accent, fontSize: scaledPt(9, pi), textTransform: sectionTransform(pi), letterSpacing: 1 }}>{t}</span>
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      <div style={{ height: 6, background: c.accent }} />
      <div style={{ padding: `18px ${pad}px 14px`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: scaledPt(24, pi), fontWeight: 700, color: c.section, lineHeight: 1.15 }}>{pi.fullName}</div>
          {pi.jobTitle && <div style={{ fontSize: scaledPt(10, pi), color: '#9D4E7B', marginTop: 3, fontStyle: 'italic' }}>{pi.jobTitle}</div>}
          <div style={{ marginTop: 7, fontSize: scaledPt(8.5, pi), color: '#4B5563' }}>{[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).join('  ·  ')}</div>
          <IdentityRow pi={pi} />
        </div>
        {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`2px solid ${c.accent}55`} fallbackSize={64} />}
      </div>
      <div style={{ height: 1, background: `${c.accent}33`, margin: `0 ${pad}px 8px` }} />
      <div style={{ padding: `0 ${pad}px 28px` }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Profile')}<p style={{ margin: '0 0 4px', fontSize: scaledPt(9.5, pi), color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── CAMPUS FRESH (teal gradient header, education-first, student layout) ────────
const CampusFreshTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0D9488', body: '#0F172A' });
  const teal = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 4, height: 16, background: teal, borderRadius: 2, flexShrink: 0 }} />
        <div style={{ fontSize: '9pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.8 }}>{t}</div>
        <div style={{ flex: 1, height: 1, background: c.section + '28' }} />
      </div>
      <SectionDividerLine pi={pi} color={c.section + '55'} />
    </div>
  );
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg,${teal} 0%,${shadeHex(teal,-0.18)} 55%,#134E4A 100%)`, padding: '28px 32px 22px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -40, right: -20, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -24, left: 80, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, position: 'relative' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '24pt', fontWeight: 900, color: 'white', letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: 'rgba(255,255,255,0.82)', fontWeight: 500, marginTop: 5 }}>{pi.jobTitle}</div>}
            <IdentityRow pi={pi} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', marginTop: 9, fontSize: '8pt', color: 'rgba(255,255,255,0.7)' }}>
              {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.4)', display: 'inline-block', flexShrink: 0 }} />{v}
                </span>
              ))}
            </div>
          </div>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border="3px solid rgba(255,255,255,0.35)" fallbackSize={72} />}
        </div>
        {/* Skill chips in header */}
        {allSkills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14, position: 'relative' }}>
            {allSkills.slice(0, 10).map((sk, i) => (
              <span key={i} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: 20, padding: '2px 10px', fontSize: '8pt', fontWeight: 600 }}>{sk}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{ padding: '6px 32px 28px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'About Me')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#334155' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills']} />
      </div>
    </div>
  );
};

// ── INTERN SPARK (indigo left sidebar, skill chips, projects-first) ──────────
const InternSparkTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#4F46E5', body: '#111827', sidebar: '#EEF2FF' });
  const indigo = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sideSec = t => <div style={{ fontSize: '8pt', fontWeight: 800, color: indigo, textTransform: 'uppercase', letterSpacing: 1.8, marginTop: 18, marginBottom: 7, borderBottom: `1.5px solid ${indigo}33`, paddingBottom: 5 }}>{t}</div>;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ fontSize: '9.5pt', fontWeight: 800, color: c.section, textTransform: sectionTransform(pi), letterSpacing: 1.5, borderLeft: `4px solid ${c.section}`, paddingLeft: 8 }}>{t}</div>
      <SectionDividerLine pi={pi} color={c.section + '55'} />
    </div>
  );
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, width: '100%', overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative', boxSizing: 'border-box' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 195, top: (i + 1) * defaultMinH, height: 56, background: pi.sidebarColor || '#EEF2FF', zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 195, right: 0, top: (i + 1) * defaultMinH, height: 56, background: 'white', zIndex: 3 }} />
        </React.Fragment>
      ))}
      {/* Left sidebar */}
      <div style={{ width: 195, background: pi.sidebarColor || '#EEF2FF', padding: '28px 16px', flexShrink: 0, boxSizing: 'border-box', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border={`3px solid ${indigo}44`} fallbackSize={80} /></div>}
        <div style={{ fontSize: '14pt', fontWeight: 900, color: indigo, lineHeight: 1.2 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '8.5pt', color: '#6366F1', fontWeight: 600, marginTop: 3 }}>{pi.jobTitle}</div>}
        <IdentityRow pi={pi} />
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
          <div key={i} style={{ fontSize: '7.5pt', color: '#374151', marginBottom: 5, overflowWrap: 'anywhere', lineHeight: 1.4 }}>{v}</div>
        ))}
        {allSkills.length > 0 && (
          <>
            {sideSec(st(d, 'skills', 'Skills'))}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {allSkills.map((sk, i) => (
                <span key={i} style={{ background: `${indigo}18`, color: indigo, border: `1px solid ${indigo}33`, borderRadius: 20, padding: '2px 9px', fontSize: '7.5pt', fontWeight: 600 }}>{sk}</span>
              ))}
            </div>
          </>
        )}
        {d.certifications?.length > 0 && (
          <>
            {sideSec(st(d, 'certifications', 'Certifications'))}
            {d.certifications.map(cert => (
              <div key={cert.id} style={{ fontSize: '7.5pt', color: '#374151', marginBottom: 5 }}>
                <div style={{ fontWeight: 700 }}>{cert.name}</div>
                {cert.issuer && <div style={{ color: '#6B7280' }}>{cert.issuer}</div>}
              </div>
            ))}
          </>
        )}
        {d.languages?.length > 0 && (
          <>
            {sideSec(st(d, 'languages', 'Languages'))}
            {d.languages.map(l => <div key={l.id} style={{ fontSize: '7.5pt', color: '#374151', marginBottom: 3 }}>{l.language}{l.proficiency && <span style={{ color: '#9CA3AF' }}> · {l.proficiency}</span>}</div>)}
          </>
        )}
        {d.hobbies && (
          <>
            {sideSec(st(d, 'hobbies', 'Interests'))}
            {d.hobbies.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => (
              <div key={i} style={{ fontSize: '7.5pt', color: '#374151', marginBottom: 3 }}>· {s}</div>
            ))}
          </>
        )}
      </div>
      {/* Right main */}
      <div style={{ flex: 1, padding: '28px 22px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Objective')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} skip={['skills', 'certifications', 'languages', 'hobbies']} />
      </div>
    </div>
  );
};

// ── SCHOLAR NAVY (navy & gold, centered header, GPA-first, academic) ─────────
const ScholarNavyTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#1E3A5F', body: '#111827' });
  const navy = c.accent;
  const gold = pi.accentColor ? shadeHex(pi.accentColor, 0.15) : '#B8960C';
  const ds = useDesign(pi, { fontFamily: '"Garamond","Georgia","Times New Roman",serif', fontSize: 10, lineHeight: 1.65 });
  const sec = t => (
    <div style={{ margin: '18px 0 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, height: 1.5, background: navy + '55' }} />
        <div style={{ fontSize: '9.5pt', fontWeight: 700, color: navy, textTransform: sectionTransform(pi), letterSpacing: 3 }}>{t}</div>
        <div style={{ flex: 1, height: 1.5, background: navy + '55' }} />
      </div>
      <SectionDividerLine pi={pi} color={navy + '66'} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {/* Header band */}
      <div style={{ background: navy, padding: '28px 36px 20px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg,${gold},${shadeHex(gold, 0.2)},${gold})` }} />
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`3px solid ${gold}`} fallbackSize={76} />}
        </div>
        <div style={{ fontSize: '26pt', fontWeight: 700, color: 'white', letterSpacing: 1, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '10.5pt', color: gold, fontWeight: 600, marginTop: 6, letterSpacing: 0.5 }}>{pi.jobTitle}</div>}
        <IdentityRow pi={pi} />
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 20px', marginTop: 10, fontSize: '8pt', color: 'rgba(255,255,255,0.72)' }}>
          {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
            <span key={i}>{v}</span>
          ))}
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${gold},${shadeHex(gold, 0.2)},${gold})` }} />
      </div>
      <div style={{ padding: '10px 40px 30px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Academic Profile')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#374151', textAlign: 'justify' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── FRESH START (orange pill section tags, clean ATS-friendly single column) ──
const FreshStartTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#EA580C', body: '#1C1917' });
  const orange = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const sec = t => (
    <div style={{ marginTop: 20, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ background: orange, color: 'white', borderRadius: 20, padding: '3px 14px', fontSize: '8pt', fontWeight: 800, letterSpacing: 1.2, textTransform: sectionTransform(pi), whiteSpace: 'nowrap', flexShrink: 0 }}>{t}</div>
      <div style={{ flex: 1, height: 1, background: orange + '30' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: ds.fontFamily, color: c.body, fontSize: ds.fontSize, lineHeight: ds.lineHeight, boxSizing: 'border-box', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {/* Header */}
      <div style={{ padding: '28px 32px 20px', borderBottom: `3px solid ${orange}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '26pt', fontWeight: 900, color: c.body, letterSpacing: -0.5, lineHeight: 1.1 }}>{pi.fullName || 'Your Name'}</div>
            {pi.jobTitle && (
              <div style={{ display: 'inline-block', background: orange + '18', color: orange, border: `1.5px solid ${orange}44`, borderRadius: 20, padding: '3px 14px', fontSize: '9.5pt', fontWeight: 700, marginTop: 7 }}>{pi.jobTitle}</div>
            )}
            <IdentityRow pi={pi} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 10, fontSize: '8pt', color: '#78716C' }}>
              {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: orange, display: 'inline-block', flexShrink: 0 }} />{v}
                </span>
              ))}
            </div>
          </div>
          {pi.photo && pi.showPhoto !== false && <Photo pi={pi} border={`3px solid ${orange}55`} fallbackSize={74} />}
        </div>
      </div>
      <div style={{ padding: '4px 32px 28px' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'About Me')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#44403C' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={pi} c={c} sec={sec} />
      </div>
    </div>
  );
};

// ── CAMPUS DARK (dark navy header, mint accent, two-column student standout) ──
const CampusDarkTemplate = ({ d }) => {
  const pi = d.personalInfo || {};
  const c = useColors(pi, { accent: '#0F766E', body: '#F0FDFA', sidebar: '#134E4A' });
  const dark = '#0A1628';
  const mint = c.accent;
  const ds = useDesign(pi, { fontFamily: '"Inter","Segoe UI",Arial,sans-serif', fontSize: 9.5, lineHeight: 1.6 });
  const pageFillH = React.useContext(PageFillContext);
  const defaultMinH = pi.pageSize === 'letter' ? 1056 : 1122;
  const minH = pageFillH || defaultMinH;
  const extraPageCount = Math.max(0, Math.ceil(minH / defaultMinH) - 1);
  const sideSec = t => <div style={{ fontSize: '7.5pt', fontWeight: 800, color: mint, textTransform: 'uppercase', letterSpacing: 2, marginTop: 18, marginBottom: 7 }}>{t}</div>;
  const sec = t => (
    <div style={{ marginTop: 18, marginBottom: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 24, height: 2.5, background: mint, borderRadius: 2, flexShrink: 0 }} />
        <div style={{ fontSize: '9pt', fontWeight: 800, color: mint, textTransform: sectionTransform(pi), letterSpacing: 1.5 }}>{t}</div>
      </div>
      <div style={{ height: 1, background: mint + '30', marginTop: 5 }} />
      <SectionDividerLine pi={pi} color={mint + '66'} />
    </div>
  );
  const allSkills = (d.skills || []).flatMap(s => (s.items || '').split(',').map(t => t.trim()).filter(Boolean));
  return (
    <div style={{ fontFamily: ds.fontFamily, color: '#F8FAFC', fontSize: ds.fontSize, lineHeight: ds.lineHeight, display: 'flex', minHeight: minH, width: '100%', overflowWrap: 'break-word', wordBreak: 'break-word', position: 'relative', background: dark, boxSizing: 'border-box' }}>
      {Array.from({ length: extraPageCount }, (_, i) => (
        <React.Fragment key={i}>
          <div style={{ position: 'absolute', left: 0, width: 200, top: (i + 1) * defaultMinH, height: 56, background: pi.sidebarColor || '#134E4A', zIndex: 3 }} />
          <div style={{ position: 'absolute', left: 200, right: 0, top: (i + 1) * defaultMinH, height: 56, background: dark, zIndex: 3 }} />
        </React.Fragment>
      ))}
      {/* Dark teal left sidebar */}
      <div style={{ width: 200, background: pi.sidebarColor || '#134E4A', padding: '28px 16px', flexShrink: 0, boxSizing: 'border-box', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {pi.photo && pi.showPhoto !== false && <div style={{ textAlign: 'center', marginBottom: 14 }}><Photo pi={pi} border={`3px solid ${mint}66`} fallbackSize={80} /></div>}
        <div style={{ fontSize: '14pt', fontWeight: 900, color: 'white', lineHeight: 1.2 }}>{pi.fullName || 'Your Name'}</div>
        {pi.jobTitle && <div style={{ fontSize: '8.5pt', color: mint, fontWeight: 600, marginTop: 4 }}>{pi.jobTitle}</div>}
        <IdentityRow pi={pi} />
        {sideSec('Contact')}
        {[pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean).map((v, i) => (
          <div key={i} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.65)', marginBottom: 5, overflowWrap: 'anywhere', lineHeight: 1.4 }}>{v}</div>
        ))}
        {allSkills.length > 0 && (
          <>
            {sideSec(st(d, 'skills', 'Skills'))}
            {allSkills.map((sk, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: mint, flexShrink: 0 }} />
                <span style={{ fontSize: '8pt', color: 'rgba(255,255,255,0.75)' }}>{sk}</span>
              </div>
            ))}
          </>
        )}
        {d.certifications?.length > 0 && (
          <>
            {sideSec(st(d, 'certifications', 'Certifications'))}
            {d.certifications.map(cert => (
              <div key={cert.id} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.65)', marginBottom: 5 }}>
                <div style={{ fontWeight: 700 }}>{cert.name}</div>
                {cert.issuer && <div style={{ color: 'rgba(255,255,255,0.4)' }}>{cert.issuer}</div>}
              </div>
            ))}
          </>
        )}
        {d.languages?.length > 0 && (
          <>
            {sideSec(st(d, 'languages', 'Languages'))}
            {d.languages.map(l => <div key={l.id} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.65)', marginBottom: 3 }}>{l.language}{l.proficiency && <span style={{ color: mint + 'AA' }}> · {l.proficiency}</span>}</div>)}
          </>
        )}
        {d.strengths && (
          <>
            {sideSec(st(d, 'strengths', 'Strengths'))}
            {d.strengths.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => (
              <div key={i} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.65)', marginBottom: 3 }}>› {s}</div>
            ))}
          </>
        )}
        {d.hobbies && (
          <>
            {sideSec(st(d, 'hobbies', 'Interests'))}
            {d.hobbies.split(',').map(s => s.trim()).filter(Boolean).map((s, i) => (
              <div key={i} style={{ fontSize: '7.5pt', color: 'rgba(255,255,255,0.65)', marginBottom: 3 }}>· {s}</div>
            ))}
          </>
        )}
      </div>
      {/* Dark right main content */}
      <div style={{ flex: 1, padding: '28px 24px', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', color: '#E2E8F0' }}>
        {pi.summary && <>{sec(pi.summaryTitle || 'Objective')}<p style={{ margin: '0 0 4px', fontSize: '9.5pt', color: '#CBD5E1' }}>{pi.summary}</p></>}
        <StdSections d={d} pi={{ ...pi, bodyColor: '#CBD5E1' }} c={{ ...c, body: '#E2E8F0', link: mint }} sec={sec} skip={['skills', 'certifications', 'languages', 'strengths', 'hobbies']} />
      </div>
    </div>
  );
};

const MAP = { modern: ModernTemplate, classic: ClassicTemplate, executive: ExecutiveTemplate, minimal: MinimalTemplate, creative: CreativeTemplate, tech: TechTemplate, elegant: ElegantTemplate, corporate: CorporateTemplate, academic: AcademicTemplate, blue_corporate: BlueCorporateTemplate, navy_timeline: NavyTimelineTemplate, europass: EuropassTemplate, academic_modern: AcademicModernTemplate, crimson: CrimsonTwoColTemplate, harvard_standard: HarvardStandardTemplate, blue_analyst: BlueAnalystTemplate, russian_classic: RussianClassicTemplate, gregory_sidebar: GregorySidebarTemplate, slate_sidebar: SlateSidebarTemplate, mono_pro: MonoProTemplate, ivory_serif: IvorySerifTemplate, teal_banner: TealBannerTemplate, swiss_grid: SwissGridTemplate, oxford_classic: OxfordClassicTemplate, cambridge_blue: CambridgeBlueTemplate, stem_scholar: StemScholarTemplate, emerald_academic: EmeraldAcademicTemplate, cobalt_timeline: CobaltTimelineTemplate, manuscript: ManuscriptTemplate, nordic_scholar: NordicScholarTemplate, amber_executive: AmberExecutiveTemplate, violet_sidebar: VioletSidebarTemplate, bold_stripe: BoldStripeTemplate, rose_gold: RoseGoldTemplate, forest_pro: ForestProTemplate, sky_pro: SkyProTemplate, charcoal_split: CharcoalSplitTemplate, terracotta: TerracottaTemplate, startup_clean: StartupCleanTemplate, champagne: ChampagneTemplate, editorial: EditorialTemplate, sage: SageTemplate, graphite: GraphiteTemplate, med_pro_blue: MedProBlueTemplate, clinical_teal: ClinicalTealTemplate, nurse_rx: NurseRxTemplate, healthcare_pro: HealthcareProTemplate, critical_care: CriticalCareTemplate, realty_pro: RealtyProTemplate, estate_sidebar: EstateSidebarTemplate, luxury_broker: LuxuryBrokerTemplate, re_corporate: RECorporateTemplate, property_exec: PropertyExecTemplate, shield_pro: ShieldProTemplate, patrol_blue: PatrolBlueTemplate, sentinel_dark: SentinelDarkTemplate, guardian_grid: GuardianGridTemplate, tactical_clean: TacticalCleanTemplate, exec_suite: ExecSuiteTemplate, boardroom: BoardroomTemplate, bizdev_pro: BizdevProTemplate, strategy_blue: StrategyBlueTemplate, c_suite: CSuiteTemplate, bloom_sidebar: BloomSidebarTemplate, salon_chic: SalonChicTemplate, spa_zen: SpaZenTemplate, glow_pro: GlowProTemplate, beauty_badge: BeautyBadgeTemplate, campus_fresh: CampusFreshTemplate, intern_spark: InternSparkTemplate, scholar_navy: ScholarNavyTemplate, fresh_start: FreshStartTemplate, campus_dark: CampusDarkTemplate };

const GOOGLE_FONT_MAP = {
  roboto: 'Roboto', lato: 'Lato', raleway: 'Raleway',
  merriweather: 'Merriweather', playfair: 'Playfair+Display',
  sourcesans: 'Source+Sans+3', nunito: 'Nunito', opensans: 'Open+Sans',
};

// A4 = 297mm, Letter = 279.4mm — at 96dpi these are ~1122px and ~1056px
const PAGE_H_PX = { a4: 1122, letter: 1056 };

const NATURAL_W = 794; // A4 at 96 dpi
const PREVIEW_PAGE_SEP = 8;

const ResumePreview = ({ data, id = 'resume-preview', singlePage = false, watermark = false }) => {
  const fontFamily = data?.personalInfo?.fontFamily;
  const wrapRef = useRef(null);
  const outerRef = useRef(null);
  const wrapHeightRef = useRef(0);
  const [breakLines, setBreakLines] = useState([]);
  const [containerWidth, setContainerWidth] = useState(NATURAL_W);
  const [wrapHeight, setWrapHeight] = useState(0);

  useEffect(() => {
    const sigId = 'gfont-dancing-script';
    if (!document.getElementById(sigId)) {
      const link = document.createElement('link');
      link.id = sigId; link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const name = GOOGLE_FONT_MAP[fontFamily];
    if (!name) return;
    const linkId = `gfont-${fontFamily}`;
    if (document.getElementById(linkId)) return;
    const link = document.createElement('link');
    link.id = linkId; link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${name}:wght@400;500;600;700;800&display=swap`;
    document.head.appendChild(link);
  }, [fontFamily]);

  const pageSize = data?.personalInfo?.pageSize || 'a4';
  const pageH = PAGE_H_PX[pageSize] || 1122;
  const previewScale = containerWidth >= NATURAL_W ? 1 : containerWidth / NATURAL_W;

  // One big centered logo watermark per page. The outer box is pinned exactly to
  // the parent (inset: 0) so it never enlarges the parent's measured scrollHeight
  // (used for pagination); each logo is placed at a fixed pixel offset (i * pageH),
  // not a percentage of measured height, so there's no feedback loop with pagination.
  const watermarkPages = singlePage ? 1 : (wrapHeight ? Math.ceil(wrapHeight / pageH) : 1);
  const watermarkOverlay = watermark ? (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20, overflow: 'hidden' }}>
      {Array.from({ length: watermarkPages }).map((_, i) => (
        <img key={i} src="/assets/mars-logo.png" alt="" style={{
          position: 'absolute', top: i * pageH + pageH / 2, left: '50%',
          transform: 'translate(-50%, -50%)', width: NATURAL_W * 0.42, height: 'auto', opacity: 0.14,
        }} />
      ))}
    </div>
  ) : null;

  // Track the rendered height of the content (at natural width) for the outer container sizing
  useEffect(() => {
    if (singlePage || !wrapRef.current) return;
    const el = wrapRef.current;
    const compute = () => {
      const h = el.scrollHeight;
      const prev = wrapHeightRef.current;
      const prevPages = prev ? Math.max(1, Math.ceil(prev / pageH)) : 0;
      const prevFill = prevPages * pageH;
      const effectiveH = prevFill && h > prevFill && h - prevFill < 40 ? prev : h;
      wrapHeightRef.current = effectiveH;
      setWrapHeight(effectiveH);
      const count = Math.max(0, Math.ceil(effectiveH / pageH) - 1);
      setBreakLines(Array.from({ length: count }, (_, i) => (i + 1) * pageH));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [singlePage, pageH, data]);

  // Track how wide the outer container is so we can compute the scale
  useEffect(() => {
    if (!outerRef.current) return;
    const el = outerRef.current;
    const update = () => setContainerWidth(el.offsetWidth || NATURAL_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [singlePage]);

  useEffect(() => {
    if (singlePage || !wrapRef.current) return;

    const root = wrapRef.current;
    const pageTopGap = PREVIEW_PAGE_SEP * 2 + 40;
    const pageBottomGap = PREVIEW_PAGE_SEP * 2 + 30;
    const maxWholeItemShiftHeight = 120;

    const resetShift = (node) => {
      if (node.dataset.pageSafeOriginalMarginTop === undefined) return;
      node.style.marginTop = node.dataset.pageSafeOriginalMarginTop;
      delete node.dataset.pageSafeOriginalMarginTop;
    };

    const shiftBlock = (node, offset) => {
      if (node.dataset.pageSafeOriginalMarginTop === undefined) {
        node.dataset.pageSafeOriginalMarginTop = node.style.marginTop || '';
      }
      const base = parseFloat(getComputedStyle(node).marginTop) || 0;
      node.style.marginTop = `${Math.ceil(base + offset)}px`;
    };

    const isUsefulBlock = (node) => {
      if (!node || node === root || !node.isConnected) return false;
      if (node.closest('[data-page-safe-ignore]')) return false;
      // Sections are pushed as units by shiftResumeSections — skip their children here
      if (node.closest('[data-resume-section]')) return false;

      const rect = node.getBoundingClientRect();
      const styles = getComputedStyle(node);
      const text = (node.textContent || '').trim();
      if (!text || rect.height < 4 || rect.width < 20) return false;
      if (styles.display === 'inline' || styles.position === 'absolute' || styles.position === 'fixed') return false;
      if (rect.height > pageH * 0.45) return false;

      const isHeadingTag = /^H[1-4]$/.test(node.tagName);
      const numericWeight = parseInt(styles.fontWeight, 10) || 400;
      const normalizedText = text.replace(/\s+/g, ' ').toLowerCase();
      const compactTitle = normalizedText.replace(/[^a-z& ]/g, '').trim();
      const sectionTitleLike = PAGE_SAFE_SECTION_TITLES.has(compactTitle);
      const headingLike = isHeadingTag || sectionTitleLike ||
        (text.length <= 90 && (numericWeight >= 600 || parseFloat(styles.letterSpacing) > 0.5));
      if (!headingLike) return false;

      const childBlocks = Array.from(node.children).filter((child) => {
        const childStyle = getComputedStyle(child);
        const childRect = child.getBoundingClientRect();
        return childStyle.display !== 'inline' && childRect.height > 4 && childRect.width > 10 && (child.textContent || '').trim();
      });
      if (childBlocks.length === 0) return true;
      if (!sectionTitleLike && childBlocks.length > 3) return false;
      return childBlocks.every((child) => child.getBoundingClientRect().height <= 38);
    };

    // Returns the push offset needed to move a block away from a page cut.
    // maxH: skip blocks taller than this.
    const getEntryOffset = (node, rootTop, maxH) => {
      const rect = node.getBoundingClientRect();
      const top = (rect.top - rootTop) / (previewScale || 1);
      const bottom = (rect.bottom - rootTop) / (previewScale || 1);
      const height = bottom - top;
      if (top <= 0 || height > maxH) return 0;
      const pageBottom = (Math.floor(top / pageH) + 1) * pageH;
      // Push content that straddles a page boundary down to the next page
      if (top < pageBottom && bottom > pageBottom - pageBottomGap) {
        return pageBottom + pageTopGap - top;
      }
      // Orphan-top: push content that starts too close to the top of a non-first page
      // (it would land inside the cover zone and be hidden)
      const posOnPage = top % pageH;
      if (top >= pageH && posOnPage > 0 && posOnPage < pageTopGap) {
        return pageTopGap - posOnPage;
      }
      return 0;
    };

    // Second pass: shift content blocks (data-resume-entry) away from page cuts.
    // Uses a higher height cap (60% page) than the heading-only pass so that section
    // blocks (skill tables, language grids, entry cards) are pushed to the next page
    // when sliced. Runs AFTER the heading pass so headings settle first.
    const shiftResumeEntries = () => {
      const entryMaxH = pageH * 0.6;
      let rootTop = root.getBoundingClientRect().top;

      for (let pass = 0; pass < 40; pass += 1) {
        const candidates = Array.from(root.querySelectorAll('[data-resume-entry]'))
          .filter(node => {
            if (!node.isConnected || node.closest('[data-page-safe-ignore]')) return false;
            const rect = node.getBoundingClientRect();
            if (rect.height < 4 || rect.height > entryMaxH) return false;
            const styles = getComputedStyle(node);
            return styles.position !== 'absolute' && styles.position !== 'fixed';
          })
          .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

        const node = candidates.find(n => getEntryOffset(n, rootTop, entryMaxH) > 0);
        if (!node) break;
        shiftBlock(node, getEntryOffset(node, rootTop, entryMaxH));
        rootTop = root.getBoundingClientRect().top;
      }
    };

    // Third pass: keep section headings together with their first entry.
    // A section that straddles a page cut is only pushed to the next page when its
    // "head" (heading + first entry) would not fit before the cut — otherwise the
    // section is left in place and the entry pass splits it at entry boundaries.
    // This avoids the huge end-of-page gaps that pushing whole sections caused.
    // Small sections (≤ 35% of a page) are still pushed whole so they never split.
    const shiftResumeSections = () => {
      const keepWholeMaxH = pageH * 0.35;
      let rootTop = root.getBoundingClientRect().top;

      for (let pass = 0; pass < 30; pass += 1) {
        const candidates = Array.from(root.querySelectorAll('[data-resume-section]'))
          .filter(node => {
            if (!node.isConnected || node.closest('[data-page-safe-ignore]')) return false;
            const rect = node.getBoundingClientRect();
            if (rect.height < 4) return false;
            const styles = getComputedStyle(node);
            return styles.position !== 'absolute' && styles.position !== 'fixed';
          })
          .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

        const node = candidates.find(n => {
          const offset = getEntryOffset(n, rootTop, Infinity);
          if (offset <= 0) return false;
          const rect = n.getBoundingClientRect();
          const top = (rect.top - rootTop) / (previewScale || 1);
          const height = (rect.bottom - rect.top) / (previewScale || 1);
          if (height <= keepWholeMaxH) return true;
          // Orphan-top: section starts inside the reserved zone of a new page — always fix
          const posOnPage = top % pageH;
          if (top >= pageH && posOnPage > 0 && posOnPage < pageTopGap) return true;
          // Tall straddling section: only push if heading + first entry can't fit before the cut
          const pageBottom = (Math.floor(top / pageH) + 1) * pageH;
          const firstEntry = n.querySelector('[data-resume-entry]');
          const headBottomPx = firstEntry ? firstEntry.getBoundingClientRect().bottom : rect.bottom;
          const headBottom = (headBottomPx - rootTop) / (previewScale || 1);
          return headBottom > pageBottom - pageBottomGap;
        });
        if (!node) break;
        shiftBlock(node, getEntryOffset(node, rootTop, Infinity));
        rootTop = root.getBoundingClientRect().top;
      }
    };

    const applyNavyPageOffsets = () => {
      Array.from(root.querySelectorAll('[data-page-safe-original-margin-top]')).forEach(resetShift);
      const nodes = Array.from(root.querySelectorAll('[data-navy-page-safe]'));
      const flowWords = Array.from(root.querySelectorAll('[data-navy-flow-word]'));
      nodes.forEach(node => node.style.setProperty('--navy-page-offset', '0px'));
      flowWords.forEach(word => {
        word.style.display = '';
        word.style.marginTop = '';
      });

      let rootTop = root.getBoundingClientRect().top;
      const getOffset = (node) => {
        const rect = node.getBoundingClientRect();
        const top = (rect.top - rootTop) / (previewScale || 1);
        const bottom = (rect.bottom - rootTop) / (previewScale || 1);
        const height = bottom - top;
        if (top <= 0) return 0;

        const y = top % pageH;
        const pageBottom = (Math.floor(top / pageH) + 1) * pageH;
        const canShiftWholeItem = height <= maxWholeItemShiftHeight;
        let offset = 0;
        if (y < pageTopGap && top >= pageH) offset = pageTopGap - y;
        if (canShiftWholeItem && y > pageH - pageBottomGap) offset = pageH + pageTopGap - y;
        if (canShiftWholeItem && top < pageBottom && bottom > pageBottom - pageBottomGap) {
          offset = Math.max(offset, pageBottom + pageTopGap - top);
        }
        return offset;
      };

      for (let pass = 0; pass < 80; pass += 1) {
        const blockCandidates = Array.from(root.querySelectorAll('h1,h2,h3,h4,div,p,li'))
          .filter(isUsefulBlock)
          .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
        const node = blockCandidates.find(candidate => getOffset(candidate) > 0);
        if (!node) break;
        shiftBlock(node, getOffset(node));
        rootTop = root.getBoundingClientRect().top;
      }

      // Second pass: push whole sections away from page cuts (heading + content together)
      shiftResumeSections();
      // Third pass: push individual entry blocks away from page cuts
      shiftResumeEntries();

      rootTop = root.getBoundingClientRect().top;
      nodes.forEach(node => {
        const rect = node.getBoundingClientRect();
        const top = (rect.top - rootTop) / (previewScale || 1);
        const bottom = (rect.bottom - rootTop) / (previewScale || 1);
        const height = bottom - top;
        if (top <= 0) return;

        const y = top % pageH;
        const pageBottom = (Math.floor(top / pageH) + 1) * pageH;
        const canShiftWholeItem = height <= maxWholeItemShiftHeight;
        let offset = 0;
        if (y < pageTopGap && top >= pageH) offset = pageTopGap - y;
        if (canShiftWholeItem && y > pageH - pageBottomGap) offset = pageH + pageTopGap - y;
        if (canShiftWholeItem && top < pageBottom && bottom > pageBottom - pageBottomGap) {
          offset = Math.max(offset, pageBottom + pageTopGap - top);
        }

        if (offset > 0) node.style.setProperty('--navy-page-offset', `${Math.ceil(offset)}px`);
      });

      const getLineOffset = (lineTop, lineBottom) => {
        if (lineTop <= 0) return 0;

        const y = lineTop % pageH;
        const pageBottom = (Math.floor(lineTop / pageH) + 1) * pageH;
        if (y < pageTopGap && lineTop >= pageH) return pageTopGap - y;
        if (lineBottom > pageBottom - pageBottomGap) return pageBottom + pageTopGap - lineTop;
        return 0;
      };

      for (let pass = 0; pass < 20; pass += 1) {
        let lineFirst = null;
        let lineTop = 0;
        let lineBottom = 0;
        let lineKey = null;
        let applied = false;

        const applyLineBreak = () => {
          if (!lineFirst) return false;
          const offset = getLineOffset(lineTop, lineBottom);
          if (offset <= 0) return false;

          lineFirst.style.display = 'block';
          lineFirst.style.marginTop = `${Math.ceil(offset)}px`;
          return true;
        };

        for (const word of flowWords) {
          const rect = word.getBoundingClientRect();
          const top = (rect.top - rootTop) / (previewScale || 1);
          const bottom = (rect.bottom - rootTop) / (previewScale || 1);
          const key = Math.round(top * 2) / 2;

          if (lineFirst && key !== lineKey) {
            applied = applyLineBreak();
            if (applied) break;
            lineFirst = word;
            lineTop = top;
            lineBottom = bottom;
            lineKey = key;
          } else if (!lineFirst) {
            lineFirst = word;
            lineTop = top;
            lineBottom = bottom;
            lineKey = key;
          } else {
            lineBottom = Math.max(lineBottom, bottom);
          }
        }

        if (!applied) applied = applyLineBreak();
        if (!applied) break;
      }

      // Signals to exportToPDF (see exportUtils.js) that page-break offsets are
      // settled and it's safe to capture — otherwise content can be snapshotted
      // mid-calculation and land flush against a page edge with no reserved margin.
      root.dataset.paginationReady = 'true';
    };

    const rafs = [];
    const scheduleApply = () => {
      root.dataset.paginationReady = 'false';
      const raf = requestAnimationFrame(applyNavyPageOffsets);
      rafs.push(raf);
    };
    scheduleApply();
    const timeouts = [80, 250, 600, 1200].map(delay => setTimeout(scheduleApply, delay));
    const ro = new ResizeObserver(scheduleApply);
    ro.observe(root);

    return () => {
      rafs.forEach(cancelAnimationFrame);
      timeouts.forEach(clearTimeout);
      ro.disconnect();
      Array.from(root.querySelectorAll('[data-page-safe-original-margin-top]')).forEach(resetShift);
    };
  }, [singlePage, data, pageH, previewScale]);

  if (!data) return null;
  activeDateFormat = data.personalInfo?.dateFormat || 'shortMonth';

  // ── SINGLE-PAGE MODE (template picker thumbnails) ──────────────────────────
  if (singlePage) {
    const scale = containerWidth >= NATURAL_W ? 1 : containerWidth / NATURAL_W;
    const pageStyle = {
      width: '100%',
      aspectRatio: pageSize === 'letter' ? '8.5 / 11' : '1 / 1.4142',
      position: 'relative',
      overflow: 'hidden',
    };
    const scaledPageStyle = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: NATURAL_W,
      minHeight: pageH,
      background: 'white',
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      transformOrigin: 'top left',
      transform: `scale(${scale})`,
    };
    if (data.type === 'cover_letter') {
      return (
        <div ref={outerRef} style={pageStyle}>
          <div id={id} style={scaledPageStyle}><CoverLetterPreview d={data} />{watermarkOverlay}</div>
        </div>
      );
    }
    const Tmpl = MAP[data.template] || ModernTemplate;
    return (
      <div ref={outerRef} style={pageStyle}>
        <div id={id} style={scaledPageStyle}>
          <style dangerouslySetInnerHTML={{ __html: `#${id} [data-resume-section] p { white-space: pre-line; }` }} />
          <Tmpl d={data} />
          {watermarkOverlay}
        </div>
      </div>
    );
  }

  // ── MULTI-PAGE MODE (live preview + PDF export) ────────────────────────────
  const pi = data.personalInfo || {};
  // 8 px blank + 2 px red line + 8 px blank = 18 px separator band between pages
  const PAGE_SEP = PREVIEW_PAGE_SEP;

  const pageNumber = pi.pageNumberVisible ? (
    <div style={{ position: 'absolute', right: 18, bottom: 12, color: '#94A3B8', fontSize: '8pt', zIndex: 5 }}>1</div>
  ) : null;

  // Round total height up to nearest whole page so template backgrounds fill completely.
  const numPages = wrapHeight ? Math.ceil(wrapHeight / pageH) : 1;
  const fillH = numPages * pageH;

  const rawContent = data.type === 'cover_letter'
    ? <CoverLetterPreview d={data} />
    : (() => { const Tmpl = MAP[data.template] || ModernTemplate; return <Tmpl d={data} />; })();
  const content = <PageFillContext.Provider value={fillH}>{rawContent}</PageFillContext.Provider>;

  // Scale the 794px-wide content to fit whatever container we're in.
  const scale = previewScale;
  const scaledTotalH = fillH * scale;

  return (
    <div ref={outerRef} style={{ position: 'relative', width: '100%', overflow: 'hidden', height: scaledTotalH || 'auto' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: NATURAL_W,
        transformOrigin: 'top left',
        transform: `scale(${scale})`,
      }}>
        <div style={{ position: 'relative' }}>
          {/* ── Content: the element captured for PDF export ── */}
          <div id={id} ref={wrapRef} style={{ background: 'white', width: NATURAL_W, minHeight: fillH, position: 'relative', boxShadow: '0 4px 28px rgba(0,0,0,0.10)' }}>
            {/* Global page-break safety — applies to every template, current and future */}
            <style dangerouslySetInnerHTML={{ __html: `
              #${id} p, #${id} li, #${id} tr, #${id} td,
              #${id} [data-resume-entry], #${id} [data-resume-tag],
              #${id} [data-resume-section] {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              #${id} [data-resume-section] p {
                white-space: pre-line;
              }
            `}} />
            {content}
            {pageNumber}
            {watermarkOverlay}
          </div>

          {/* ── Preview-only overlays: outside #resume-preview, never captured in PDF ── */}
          {breakLines.map((top, i) => (
            <React.Fragment key={i}>
              {/* Page separator: 8 px gap + 2 px red line + 8 px gap */}
              <div data-html2canvas-ignore="true" style={{
                position: 'absolute', left: 0, right: 0,
                top: top - PAGE_SEP,
                height: PAGE_SEP * 2 + 2,
                background: '#DDE1E7',
                zIndex: 30,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <div style={{
                  position: 'absolute', left: 0, right: 0,
                  top: PAGE_SEP, height: 2,
                  background: '#EF4444', opacity: 0.3,
                }} />
                <span style={{
                  position: 'relative', zIndex: 1,
                  fontSize: 8, fontWeight: 700,
                  color: '#EF4444', opacity: 0.75,
                  fontFamily: 'Inter, sans-serif',
                  background: '#DDE1E7', padding: '2px 8px', borderRadius: 3,
                  letterSpacing: 0.4,
                }}>
                  Page {i + 2}
                </span>
              </div>

              {/* Box-shadow frame for subsequent page area */}
              <div data-html2canvas-ignore="true" style={{
                position: 'absolute',
                top: top + PAGE_SEP + 2,
                left: 0, width: NATURAL_W, height: pageH,
                boxShadow: '0 4px 28px rgba(0,0,0,0.10)',
                pointerEvents: 'none', zIndex: 1,
              }} />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
